import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost } from '../theme'

// ─── Ch02 Canvas Illustrations ───────────────────────────────────────────────

function ChartTypesVisual() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const panelW = Math.floor(W / 3);
    const prices = [48, 50, 47, 52, 49, 54, 51, 56, 53, 58];
    const opens  = [47, 51, 49, 48, 52, 50, 53, 52, 54, 55];
    const highs  = [51, 53, 51, 54, 53, 55, 55, 57, 56, 59];
    const lows   = [46, 48, 46, 47, 48, 49, 50, 51, 52, 54];
    const pad = 28, barW = 7;

    const minV = 44, maxV = 62;
    const py = (v, px0) => px0 + (H - pad * 2) * (1 - (v - minV) / (maxV - minV));
    const px = (i, panelX) => panelX + pad + i * ((panelW - pad * 2) / (prices.length - 1));

    ctx.clearRect(0, 0, W, H);

    // Dividers
    [0, 1, 2].forEach(p => {
      const x0 = p * panelW;
      ctx.fillStyle = 'rgba(42,38,30,0.6)';
      ctx.fillRect(x0, 0, panelW - 1, H);
      const labels = ['Line Chart', 'Bar Chart (OHLC)', 'Candlestick Chart'];
      ctx.fillStyle = 'rgba(201,168,76,0.7)';
      ctx.font = '10px Jost, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(labels[p], x0 + panelW / 2, H - 8);
    });

    // Baseline axis
    [0, 1, 2].forEach(p => {
      ctx.strokeStyle = 'rgba(120,110,90,0.25)';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(p * panelW + pad / 2, pad / 2);
      ctx.lineTo(p * panelW + pad / 2, H - 20);
      ctx.stroke();
    });

    // Panel 0: Line chart
    ctx.beginPath();
    ctx.strokeStyle = '#5a9cd6';
    ctx.lineWidth = 1.5;
    prices.forEach((v, i) => {
      const x = px(i, 0), y = py(v, 4);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.stroke();
    prices.forEach((v, i) => {
      ctx.beginPath();
      ctx.arc(px(i, 0), py(v, 4), 2, 0, Math.PI * 2);
      ctx.fillStyle = '#5a9cd6';
      ctx.fill();
    });

    // Panel 1: OHLC bars
    for (let i = 0; i < prices.length; i++) {
      const x = px(i, panelW);
      const bullish = prices[i] >= opens[i];
      ctx.strokeStyle = bullish ? '#7aab8a' : '#c47a7a';
      ctx.lineWidth = 1;
      // High-low line
      ctx.beginPath();
      ctx.moveTo(x, py(highs[i], 4));
      ctx.lineTo(x, py(lows[i], 4));
      ctx.stroke();
      // Open tick left
      ctx.beginPath();
      ctx.moveTo(x - 4, py(opens[i], 4));
      ctx.lineTo(x, py(opens[i], 4));
      ctx.stroke();
      // Close tick right
      ctx.beginPath();
      ctx.moveTo(x, py(prices[i], 4));
      ctx.lineTo(x + 4, py(prices[i], 4));
      ctx.stroke();
    }

    // Panel 2: Candlestick
    for (let i = 0; i < prices.length; i++) {
      const x = px(i, panelW * 2);
      const open = opens[i], close = prices[i];
      const bullish = close >= open;
      const bodyTop = py(Math.max(open, close), 4);
      const bodyBot = py(Math.min(open, close), 4);
      const bodyH = Math.max(bodyBot - bodyTop, 1);
      // Wick
      ctx.strokeStyle = bullish ? '#7aab8a' : '#c47a7a';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, py(highs[i], 4));
      ctx.lineTo(x, py(lows[i], 4));
      ctx.stroke();
      // Body
      ctx.fillStyle = bullish ? 'rgba(122,171,138,0.7)' : 'rgba(196,122,122,0.7)';
      ctx.strokeStyle = bullish ? '#7aab8a' : '#c47a7a';
      ctx.lineWidth = 0.5;
      ctx.fillRect(x - barW / 2, bodyTop, barW, bodyH);
      ctx.strokeRect(x - barW / 2, bodyTop, barW, bodyH);
    }
  }, []);

  return (
    <div style={{ margin: '16px 0' }}>
      <canvas ref={canvasRef} width={600} height={160} style={{ width: '100%', maxWidth: '600px', display: 'block', borderRadius: '8px', border: '0.5px solid rgba(120,110,90,0.25)' }} />
      <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", marginTop: '6px', textAlign: 'center' }}>Same 10-day price data — three different views. Candlesticks show the most information at a glance.</p>
    </div>
  );
}

function OtherChartTypesVisual() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const panelW = Math.floor(W / 3);
    ctx.clearRect(0, 0, W, H);

    [0, 1, 2].forEach(p => {
      ctx.fillStyle = 'rgba(42,38,30,0.6)';
      ctx.fillRect(p * panelW, 0, panelW - 1, H);
      const labels = ['Heikin-Ashi', 'Point & Figure', 'Renko'];
      ctx.fillStyle = 'rgba(201,168,76,0.7)';
      ctx.font = '10px Jost, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(labels[p], p * panelW + panelW / 2, H - 8);
    });

    // Panel 0: Heikin-Ashi (smoothed candles — all same direction, bodies touching)
    const haData = [
      {o:50,h:54,l:48,c:53,bull:true},{o:53,h:57,l:51,c:56,bull:true},{o:56,h:60,l:54,c:58,bull:true},
      {o:58,h:59,l:53,c:54,bull:false},{o:54,h:55,l:49,c:50,bull:false},{o:50,h:54,l:47,c:52,bull:true},
    ];
    const haMin=46, haMax=62;
    const haY = (v) => 8 + (H - 30) * (1 - (v - haMin)/(haMax - haMin));
    haData.forEach((d, i) => {
      const x = panelW * 0 + 18 + i * 28;
      ctx.strokeStyle = d.bull ? '#7aab8a' : '#c47a7a';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x, haY(d.h)); ctx.lineTo(x, haY(d.l)); ctx.stroke();
      const bTop = haY(Math.max(d.o,d.c)), bH = Math.max(haY(Math.min(d.o,d.c)) - bTop, 2);
      ctx.fillStyle = d.bull ? 'rgba(122,171,138,0.75)' : 'rgba(196,122,122,0.75)';
      ctx.fillRect(x - 8, bTop, 16, bH);
      ctx.strokeRect(x - 8, bTop, 16, bH);
    });
    ctx.fillStyle = 'rgba(201,168,76,0.4)'; ctx.font = '9px Jost,sans-serif'; ctx.textAlign='center';
    ctx.fillText('Smoothed — less noise', panelW * 0 + panelW/2, 18);

    // Panel 1: Point & Figure (X/O columns)
    const pfCols = [{type:'X',rows:[2,3,4,5,6]},{type:'O',rows:[5,4,3]},{type:'X',rows:[3,4,5,6,7]},{type:'O',rows:[6,5,4]},{type:'X',rows:[4,5,6,7,8]}];
    const pfX0 = panelW;
    pfCols.forEach((col, ci) => {
      const cx = pfX0 + 18 + ci * 22;
      col.rows.forEach(row => {
        const cy = 20 + (9 - row) * 14;
        ctx.strokeStyle = col.type === 'X' ? '#7aab8a' : '#c47a7a';
        ctx.lineWidth = 1.5;
        if (col.type === 'X') {
          ctx.beginPath(); ctx.moveTo(cx-7,cy-7); ctx.lineTo(cx+7,cy+7); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(cx+7,cy-7); ctx.lineTo(cx-7,cy+7); ctx.stroke();
        } else {
          ctx.beginPath(); ctx.arc(cx, cy, 7, 0, Math.PI*2); ctx.stroke();
        }
      });
    });
    ctx.fillStyle = 'rgba(201,168,76,0.4)'; ctx.font = '9px Jost,sans-serif'; ctx.textAlign='center';
    ctx.fillText('X = up  O = down', panelW + panelW/2, H - 20);

    // Panel 2: Renko (uniform blocks)
    const renkoBlocks = [
      {x:0,y:5,bull:true},{x:0,y:4,bull:true},{x:0,y:3,bull:true},
      {x:1,y:3,bull:false},{x:1,y:4,bull:false},
      {x:2,y:4,bull:true},{x:2,y:3,bull:true},{x:2,y:2,bull:true},{x:2,y:1,bull:true},
      {x:3,y:1,bull:false},{x:3,y:2,bull:false},
    ];
    const rx0 = panelW * 2 + 14, bSize = 18;
    renkoBlocks.forEach(b => {
      const bx = rx0 + b.x * (bSize + 4);
      const by = 10 + b.y * (bSize + 2);
      ctx.fillStyle = b.bull ? 'rgba(122,171,138,0.7)' : 'rgba(196,122,122,0.7)';
      ctx.strokeStyle = b.bull ? '#7aab8a' : '#c47a7a';
      ctx.lineWidth = 0.5;
      ctx.fillRect(bx, by, bSize, bSize);
      ctx.strokeRect(bx, by, bSize, bSize);
    });
    ctx.fillStyle = 'rgba(201,168,76,0.4)'; ctx.font = '9px Jost,sans-serif'; ctx.textAlign='center';
    ctx.fillText('Equal-size blocks', panelW * 2 + panelW/2, 18);
  }, []);

  return (
    <div style={{ margin: '16px 0' }}>
      <canvas ref={canvasRef} width={600} height={160} style={{ width: '100%', maxWidth: '600px', display: 'block', borderRadius: '8px', border: '0.5px solid rgba(120,110,90,0.25)' }} />
      <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", marginTop: '6px', textAlign: 'center' }}>All three show the same price data — each filters it differently. Candlesticks remain your primary tool.</p>
    </div>
  );
}

function CandleAnatomyCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(26,22,16,0.92)';
    ctx.fillRect(0, 0, W, H);

    const cx = 220, bw = 50;
    const bodyTop = 115, bodyBot = 200;
    const wickTop = 52, wickBot = 252;

    ctx.fillStyle = 'rgba(122,171,138,0.65)';
    ctx.font = 'bold 11px Jost, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('BULLISH CANDLESTICK', cx, 28);

    ctx.strokeStyle = '#7aab8a'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(cx, wickTop); ctx.lineTo(cx, bodyTop); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, bodyBot); ctx.lineTo(cx, wickBot); ctx.stroke();

    ctx.fillStyle = 'rgba(122,171,138,0.82)';
    ctx.strokeStyle = '#7aab8a'; ctx.lineWidth = 1.5;
    ctx.fillRect(cx - bw/2, bodyTop, bw, bodyBot - bodyTop);
    ctx.strokeRect(cx - bw/2, bodyTop, bw, bodyBot - bodyTop);

    const arrow = (x1, y1, x2, y2) => {
      ctx.strokeStyle = 'rgba(100,90,70,0.45)'; ctx.lineWidth = 0.8;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      const ang = Math.atan2(y2 - y1, x2 - x1), hs = 5;
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - hs * Math.cos(ang - 0.4), y2 - hs * Math.sin(ang - 0.4));
      ctx.lineTo(x2 - hs * Math.cos(ang + 0.4), y2 - hs * Math.sin(ang + 0.4));
      ctx.closePath();
      ctx.fillStyle = 'rgba(100,90,70,0.45)'; ctx.fill();
    };

    const leftLabels = [
      { text: 'High',  y: wickTop,  toX: cx },
      { text: 'Open',  y: bodyTop,  toX: cx - bw / 2 },
      { text: 'Close', y: bodyBot,  toX: cx - bw / 2 },
      { text: 'Low',   y: wickBot,  toX: cx },
    ];
    leftLabels.forEach(({ text, y, toX }) => {
      ctx.fillStyle = 'rgba(180,170,145,0.88)';
      ctx.font = 'bold 11px Jost, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(text, 146, y + 4);
      arrow(149, y, toX - 5, y);
    });

    const rx = 264;
    const grp = (connFromY, titleY, title, sub1, sub2) => {
      ctx.strokeStyle = 'rgba(100,90,70,0.3)'; ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(cx + bw / 2 + 3, connFromY);
      ctx.lineTo(rx - 7, titleY + 2);
      ctx.stroke();
      ctx.fillStyle = '#c9a84c'; ctx.font = 'bold 11px Jost, sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(title, rx, titleY);
      ctx.fillStyle = 'rgba(160,148,120,0.72)'; ctx.font = '10px Jost, sans-serif';
      ctx.fillText(sub1, rx, titleY + 14);
      ctx.fillText(sub2, rx, titleY + 27);
    };

    grp(wickTop + 28,  68, 'Upper Wick (Shadow)', 'Price stretched here but was rejected', 'Sellers overwhelmed buyers at the high');
    grp((bodyTop + bodyBot) / 2, 158, 'Body', 'Open to Close range', 'Green = closed higher than opened');
    grp(wickBot - 28, 236, 'Lower Wick (Shadow)', 'Price stretched here but snapped back', 'Buyers overwhelmed sellers at the low');
  }, []);

  return (
    <div style={{ position: 'relative', overflow: 'visible', marginBottom: '12px' }}>
      <canvas ref={ref} width={640} height={288}
        style={{ display: 'block', width: '100%', maxWidth: '640px', borderRadius: '10px', border: '0.5px solid rgba(120,110,90,0.18)' }} />
    </div>
  );
}

function VolumeConvictionCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(26,22,16,0.88)'; ctx.fillRect(0, 0, W, H);

    const priceTop = 14, priceH = 96, bw = 22;
    const volTop = priceTop + priceH + 10, volH = 46;
    const minP = 44, maxP = 62;
    const py = (v) => priceTop + priceH * (1 - (v - minP) / (maxP - minP));

    const candles = [
      { cx: 90,  o:48,h:57,l:47,c:56, volH:44, bull:true,  lbl:'"Strong conviction"',  sub:'Large move + high volume' },
      { cx: 255, o:48,h:57,l:47,c:56, volH:13, bull:true,  lbl:'"Weak conviction"',    sub:'Same move, low volume' },
      { cx: 415, o:54,h:64,l:53,c:55, volH:44, bull:false, lbl:'"Strong rejection"',   sub:'Long wick + high volume' },
    ];

    candles.forEach(({ cx, o, h, l, c, volH: vH, bull, lbl, sub }) => {
      const clr = bull ? '#7aab8a' : '#c47a7a';
      ctx.strokeStyle = clr; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(cx, py(h)); ctx.lineTo(cx, py(l)); ctx.stroke();
      const bt = py(Math.max(o, c)), bh = Math.max(py(Math.min(o, c)) - bt, 2);
      ctx.fillStyle = bull ? 'rgba(122,171,138,0.75)' : 'rgba(196,122,122,0.75)';
      ctx.strokeStyle = clr; ctx.lineWidth = 0.8;
      ctx.fillRect(cx - bw/2, bt, bw, bh); ctx.strokeRect(cx - bw/2, bt, bw, bh);
      ctx.fillStyle = bull ? 'rgba(122,171,138,0.55)' : 'rgba(196,122,122,0.55)';
      ctx.fillRect(cx - bw/2, volTop + volH - vH, bw, vH);
      ctx.strokeStyle = bull ? 'rgba(122,171,138,0.35)' : 'rgba(196,122,122,0.35)';
      ctx.lineWidth = 0.5;
      ctx.strokeRect(cx - bw/2, volTop + volH - vH, bw, vH);
      ctx.fillStyle = '#c9a84c'; ctx.font = 'bold 10px Jost, sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(lbl, cx, volTop + volH + 15);
      ctx.fillStyle = 'rgba(130,120,100,0.65)'; ctx.font = '9px Jost, sans-serif';
      ctx.fillText(sub, cx, volTop + volH + 27);
    });

    ctx.strokeStyle = 'rgba(100,90,70,0.18)'; ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(30, volTop - 3); ctx.lineTo(W - 10, volTop - 3); ctx.stroke();

    ctx.save();
    ctx.translate(17, volTop + volH / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = 'rgba(120,110,90,0.45)'; ctx.font = '9px Jost, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('Volume', 0, 0);
    ctx.restore();
  }, []);

  return (
    <div style={{ position: 'relative', overflow: 'visible', margin: '14px 0' }}>
      <canvas ref={ref} width={520} height={196}
        style={{ display: 'block', width: '100%', maxWidth: '520px', borderRadius: '8px', border: '0.5px solid rgba(120,110,90,0.18)' }} />
      <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", margin: '6px 0 0', textAlign: 'center' }}>
        Candles 1 &amp; 2 show identical price moves. Volume reveals whether participants agreed with the move.
      </p>
    </div>
  );
}

function PatternMiniCanvas({ drawFn }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) drawFn(ref.current); }, []);
  return (
    <canvas ref={ref} width={100} height={100}
      style={{ display: 'block', borderRadius: '6px', background: 'rgba(26,22,16,0.92)', width: '100px', height: '100px' }} />
  );
}

const patternDrawers = {
  doji: (cv) => {
    const ctx = cv.getContext('2d'), cx = 50;
    ctx.clearRect(0, 0, 100, 100);
    ctx.fillStyle = 'rgba(26,22,16,0.92)'; ctx.fillRect(0, 0, 100, 100);
    const py = (v) => 10 + 78 * (1 - (v - 44) / 22);
    ctx.strokeStyle = 'rgba(200,185,150,0.65)'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(cx, py(64)); ctx.lineTo(cx, py(46)); ctx.stroke();
    const bt = py(55.3), bh = Math.max(py(55) - bt, 2);
    ctx.fillStyle = 'rgba(160,148,120,0.45)'; ctx.strokeStyle = 'rgba(200,185,150,0.65)'; ctx.lineWidth = 0.8;
    ctx.fillRect(cx - 15, bt, 30, bh); ctx.strokeRect(cx - 15, bt, 30, bh);
  },
  hammer: (cv) => {
    const ctx = cv.getContext('2d'), cx = 50;
    ctx.clearRect(0, 0, 100, 100); ctx.fillStyle = 'rgba(26,22,16,0.92)'; ctx.fillRect(0, 0, 100, 100);
    const py = (v) => 10 + 78 * (1 - (v - 40) / 26);
    ctx.strokeStyle = '#7aab8a'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(cx, py(62)); ctx.lineTo(cx, py(44)); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, py(64)); ctx.lineTo(cx, py(62)); ctx.stroke();
    const bt = py(64), bh = Math.max(py(62) - bt, 5);
    ctx.fillStyle = 'rgba(122,171,138,0.78)'; ctx.strokeStyle = '#7aab8a'; ctx.lineWidth = 0.8;
    ctx.fillRect(cx - 15, bt, 30, bh); ctx.strokeRect(cx - 15, bt, 30, bh);
  },
  shootingstar: (cv) => {
    const ctx = cv.getContext('2d'), cx = 50;
    ctx.clearRect(0, 0, 100, 100); ctx.fillStyle = 'rgba(26,22,16,0.92)'; ctx.fillRect(0, 0, 100, 100);
    const py = (v) => 10 + 78 * (1 - (v - 44) / 26);
    ctx.strokeStyle = '#c47a7a'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(cx, py(66)); ctx.lineTo(cx, py(52)); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx, py(50)); ctx.lineTo(cx, py(48)); ctx.stroke();
    const bt = py(52), bh = Math.max(py(50) - bt, 5);
    ctx.fillStyle = 'rgba(196,122,122,0.78)'; ctx.strokeStyle = '#c47a7a'; ctx.lineWidth = 0.8;
    ctx.fillRect(cx - 15, bt, 30, bh); ctx.strokeRect(cx - 15, bt, 30, bh);
  },
  bullishengulfing: (cv) => {
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, 100, 100); ctx.fillStyle = 'rgba(26,22,16,0.92)'; ctx.fillRect(0, 0, 100, 100);
    const py = (v) => 10 + 78 * (1 - (v - 44) / 20);
    ctx.strokeStyle = '#c47a7a'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(33, py(57)); ctx.lineTo(33, py(52)); ctx.stroke();
    let bt = py(57), bh = Math.max(py(52) - bt, 4);
    ctx.fillStyle = 'rgba(196,122,122,0.75)'; ctx.strokeStyle = '#c47a7a'; ctx.lineWidth = 0.6;
    ctx.fillRect(24, bt, 18, bh); ctx.strokeRect(24, bt, 18, bh);
    ctx.strokeStyle = '#7aab8a'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(67, py(60)); ctx.lineTo(67, py(48)); ctx.stroke();
    bt = py(60); bh = Math.max(py(48) - bt, 6);
    ctx.fillStyle = 'rgba(122,171,138,0.78)'; ctx.strokeStyle = '#7aab8a'; ctx.lineWidth = 0.8;
    ctx.fillRect(56, bt, 22, bh); ctx.strokeRect(56, bt, 22, bh);
  },
  bearishengulfing: (cv) => {
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, 100, 100); ctx.fillStyle = 'rgba(26,22,16,0.92)'; ctx.fillRect(0, 0, 100, 100);
    const py = (v) => 10 + 78 * (1 - (v - 44) / 20);
    ctx.strokeStyle = '#7aab8a'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(33, py(57)); ctx.lineTo(33, py(52)); ctx.stroke();
    let bt = py(57), bh = Math.max(py(52) - bt, 4);
    ctx.fillStyle = 'rgba(122,171,138,0.75)'; ctx.strokeStyle = '#7aab8a'; ctx.lineWidth = 0.6;
    ctx.fillRect(24, bt, 18, bh); ctx.strokeRect(24, bt, 18, bh);
    ctx.strokeStyle = '#c47a7a'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(67, py(60)); ctx.lineTo(67, py(48)); ctx.stroke();
    bt = py(60); bh = Math.max(py(48) - bt, 6);
    ctx.fillStyle = 'rgba(196,122,122,0.78)'; ctx.strokeStyle = '#c47a7a'; ctx.lineWidth = 0.8;
    ctx.fillRect(56, bt, 22, bh); ctx.strokeRect(56, bt, 22, bh);
  },
  marubozu: (cv) => {
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, 100, 100); ctx.fillStyle = 'rgba(26,22,16,0.92)'; ctx.fillRect(0, 0, 100, 100);
    const py = (v) => 10 + 78 * (1 - (v - 44) / 20);
    ctx.fillStyle = 'rgba(122,171,138,0.82)'; ctx.strokeStyle = '#7aab8a'; ctx.lineWidth = 0.8;
    const gbt = py(60), gbh = Math.max(py(49) - gbt, 5);
    ctx.fillRect(20, gbt, 24, gbh); ctx.strokeRect(20, gbt, 24, gbh);
    ctx.fillStyle = 'rgba(196,122,122,0.82)'; ctx.strokeStyle = '#c47a7a';
    const rbt = py(58), rbh = Math.max(py(47) - rbt, 5);
    ctx.fillRect(56, rbt, 24, rbh); ctx.strokeRect(56, rbt, 24, rbh);
    ctx.fillStyle = 'rgba(122,171,138,0.6)'; ctx.font = '9px Jost,sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('Bull', 32, py(47));
    ctx.fillStyle = 'rgba(196,122,122,0.6)';
    ctx.fillText('Bear', 68, py(45));
  },
};

function ReadingCandlesticksSection({ onNavigate }) {
  const ss = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
  const b = { fontSize: '14px', color: 'rgba(240,237,230,0.82)', fontFamily: "'Jost',sans-serif", lineHeight: 1.85, margin: 0 };
  const hr = { height: '0.5px', background: 'rgba(201,168,76,0.12)', margin: '28px 0' };

  const parts = [
    { t: 'Upper Wick', d: 'Price stretched up to this level but sellers pushed it back down before close. The longer the wick, the more aggressive the rejection. A long upper wick at resistance is the rubber band snapping back in real time.' },
    { t: 'The Body',   d: 'The distance between open and close. Large body = conviction — the session was dominated by one side. Small body (doji) = indecision. Buyers and sellers were evenly matched.' },
    { t: 'Lower Wick', d: 'Price stretched down but buyers stepped in and pushed it back up before close. A long lower wick at support shows strong demand — buyers defended that level aggressively.' },
    { t: 'Color',      d: 'Green body = closed higher than opened. Buyers won the session. Red body = closed lower than opened. Sellers won the session.' },
  ];

  const pats = [
    { k: 'doji',             n: 'Doji',              d: 'Complete indecision. Neither buyers nor sellers won. Often appears at turning points. Watch what comes next.' },
    { k: 'hammer',           n: 'Hammer',            d: 'Buyers overwhelmed sellers at the lows. Bullish reversal signal — especially powerful at support zones.' },
    { k: 'shootingstar',     n: 'Shooting Star',     d: 'Sellers overwhelmed buyers at the highs. Bearish reversal signal — especially powerful at resistance zones.' },
    { k: 'bullishengulfing', n: 'Bullish Engulfing', d: 'Complete reversal of selling pressure. Strong bullish signal — especially at support.' },
    { k: 'bearishengulfing', n: 'Bearish Engulfing', d: 'Complete reversal of buying pressure. Strong bearish signal — especially at resistance.' },
    { k: 'marubozu',         n: 'Marubozu',          d: 'Pure conviction. Price opened and never looked back. Strong directional signal. Confirm with volume.' },
  ];

  return (
    <div style={{ position: 'relative', overflow: 'visible', height: 'auto', display: 'block' }}>

      {/* 1 — Anatomy */}
      <h3 style={{ ...ss, fontSize: '22px', color: V.gold, fontWeight: 400, margin: '0 0 16px' }}>Anatomy of a Candlestick</h3>
      <CandleAnatomyCanvas />
      <div style={{ display: 'flex', gap: '10px', marginBottom: '0' }}>
        <div style={{ flex: 1, padding: '12px 16px', background: 'rgba(122,171,138,0.07)', border: '0.5px solid rgba(122,171,138,0.2)', borderRadius: '8px' }}>
          <div style={{ fontSize: '11px', color: V.sage, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', marginBottom: '4px' }}>▲ BULLISH (GREEN)</div>
          <div style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.6 }}>Close is <strong style={{ color: V.sage }}>above</strong> open. Buyers won the session.</div>
        </div>
        <div style={{ flex: 1, padding: '12px 16px', background: 'rgba(196,122,122,0.07)', border: '0.5px solid rgba(196,122,122,0.2)', borderRadius: '8px' }}>
          <div style={{ fontSize: '11px', color: V.rose, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', marginBottom: '4px' }}>▼ BEARISH (RED)</div>
          <div style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.6 }}>Close is <strong style={{ color: V.rose }}>below</strong> open. Sellers won. Same structure — opposite outcome.</div>
        </div>
      </div>

      <div style={hr} />

      {/* 2 — What Each Part Tells You */}
      <h3 style={{ ...ss, fontSize: '20px', color: V.gold, fontWeight: 400, margin: '0 0 14px' }}>What Each Part Tells You</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(175px, 1fr))', gap: '10px' }}>
        {parts.map(({ t, d }) => (
          <div key={t} style={{ background: V.bg3, borderTop: `2px solid ${V.gold}`, borderRadius: '0 0 8px 8px', padding: '14px 16px' }}>
            <div style={{ ...ss, fontSize: '16px', color: V.gold, fontWeight: 400, marginBottom: '8px' }}>{t}</div>
            <p style={{ ...b, fontSize: '13px', lineHeight: 1.7 }}>{d}</p>
          </div>
        ))}
      </div>

      <div style={hr} />

      {/* 3 — Volume */}
      <h3 style={{ ...ss, fontSize: '20px', color: V.gold, fontWeight: 400, margin: '0 0 12px' }}>Volume — The Conviction Meter</h3>
      <p style={{ ...b, marginBottom: '12px' }}>Candlesticks show price action. Volume shows conviction behind the move.</p>
      <p style={{ ...b, marginBottom: '12px' }}>Large green candle + high volume = strong buyer conviction. Large green candle + low volume = weak signal. The move may not sustain without participants agreeing.</p>
      <p style={{ ...b, marginBottom: '14px' }}>Watch for volume spikes — they often precede or confirm significant moves. A long wick on high volume means many participants tried to push price in one direction and were overwhelmed. That is a powerful rejection signal.</p>
      <VolumeConvictionCanvas />

      <div style={hr} />

      {/* 4 — Key Patterns */}
      <h3 style={{ ...ss, fontSize: '20px', color: V.gold, fontWeight: 400, margin: '0 0 14px' }}>Key Candlestick Patterns</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
        {pats.map(({ k, n, d }) => (
          <div key={k} style={{ background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <PatternMiniCanvas drawFn={patternDrawers[k]} />
            <div style={{ ...ss, fontSize: '15px', color: V.gold, fontWeight: 400 }}>{n}</div>
            <p style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.65, margin: 0 }}>{d}</p>
          </div>
        ))}
      </div>

      <div style={hr} />

      {/* 5 — Rubber Band */}
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 10px 10px 0', padding: '22px 24px' }}>
        <h3 style={{ ...ss, fontSize: '22px', color: V.gold, fontWeight: 400, margin: '0 0 14px' }}>Every Candlestick Is a Rubber Band Story</h3>
        <p style={{ ...b, marginBottom: '12px' }}>The wicks show you where the rubber band stretched — and was rejected. The body shows you where it settled after the stretch. The color shows you who won. The size shows you conviction. The volume shows you how many agreed.</p>
        <p style={{ ...b }}>A single candlestick tells you everything that happened in one time period — from where it opened, to where it stretched, to where it settled. When you learn to read each candle as a rubber band story, the chart stops being noise and becomes a clear record of the battle between buyers and sellers at every price level, in every session.</p>
      </div>

    </div>
  );
}

function RubberBandChartVisual() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(30,26,20,0.7)';
    ctx.fillRect(0, 0, W, H);

    const pads = { l:50, r:20, t:16, b:24 };
    const gW = W - pads.l - pads.r, gH = H - pads.t - pads.b;

    // Price path: starts near MA, stretches up, returns, stretches down, returns
    const maY = H / 2;
    const pts = [
      {x:0,y:0},{x:0.08,y:-8},{x:0.14,y:5},{x:0.2,y:2},{x:0.26,y:-12},{x:0.32,y:-28},
      {x:0.38,y:-38},{x:0.44,y:-22},{x:0.5,y:-6},{x:0.56,y:4},{x:0.62,y:22},
      {x:0.68,y:38},{x:0.74,y:30},{x:0.82,y:12},{x:0.9,y:0},{x:1,y:-4},
    ].map(p => ({ x: pads.l + p.x * gW, y: maY + p.y }));

    // MA line (equilibrium)
    ctx.beginPath();
    ctx.strokeStyle = 'rgba(201,168,76,0.5)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 3]);
    ctx.moveTo(pads.l, maY);
    ctx.lineTo(pads.l + gW, maY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Price line
    ctx.beginPath();
    ctx.strokeStyle = '#5a9cd6';
    ctx.lineWidth = 1.5;
    pts.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
    ctx.stroke();

    // Labels
    const label = (text, x, y, color, size = '10px') => {
      ctx.fillStyle = color;
      ctx.font = `${size} Jost, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(text, x, y);
    };

    // Stretched down label
    label('⬇ Stretched down', pads.l + gW * 0.38, maY + 50, 'rgba(196,122,122,0.85)');
    label('CSP opportunity', pads.l + gW * 0.38, maY + 62, 'rgba(196,122,122,0.6)', '9px');
    // Stretched up label
    label('⬆ Stretched up', pads.l + gW * 0.68, maY - 50, 'rgba(122,171,138,0.85)');
    label('CC opportunity', pads.l + gW * 0.68, maY - 62, 'rgba(122,171,138,0.6)', '9px');
    // Equilibrium label
    ctx.fillStyle = 'rgba(201,168,76,0.6)';
    ctx.font = '9px Jost, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Equilibrium (MA)', pads.l + 6, maY - 6);

    // Y-axis label
    ctx.save();
    ctx.translate(14, H/2);
    ctx.rotate(-Math.PI/2);
    ctx.fillStyle = 'rgba(120,110,90,0.5)';
    ctx.font = '9px Jost, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Price', 0, 0);
    ctx.restore();
  }, []);

  return (
    <div style={{ margin: '16px 0' }}>
      <canvas ref={canvasRef} width={560} height={160} style={{ width: '100%', maxWidth: '560px', display: 'block', borderRadius: '8px', border: '0.5px solid rgba(120,110,90,0.25)' }} />
      <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", marginTop: '6px', textAlign: 'center' }}>The rubber band in action: price oscillates around the equilibrium (moving average). Stretched = opportunity.</p>
    </div>
  );
}

// ─── Ch02 New Tab Visuals ─────────────────────────────────────────────────────

function Ch02CandlestickAnatomy() {
  const ref = useRef(null);
  const ss = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
  const jost = { fontFamily: "'Jost',sans-serif" };

  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d');
    const W = cv.width, H = cv.height;
    ctx.clearRect(0,0,W,H);
    ctx.fillStyle = '#0a0a0c'; ctx.fillRect(0,0,W,H);

    // Candle geometry
    const cx = 290;              // wick x (center)
    const bodyLeft = 260;        // body x left edge
    const bodyRight = 320;       // body x right edge
    const yHigh = 40;            // top of upper wick
    const yClose = 120;          // top of body (close — bullish)
    const yOpen = 220;           // bottom of body (open — bullish)
    const yLow = 280;            // bottom of lower wick

    // ── Wicks ──
    ctx.strokeStyle = '#7aab8a';
    ctx.lineWidth = 2;
    // upper wick
    ctx.beginPath(); ctx.moveTo(cx, yClose); ctx.lineTo(cx, yHigh); ctx.stroke();
    // lower wick
    ctx.beginPath(); ctx.moveTo(cx, yOpen); ctx.lineTo(cx, yLow); ctx.stroke();

    // ── Body ──
    ctx.fillStyle = 'rgba(122,171,138,0.8)';
    ctx.fillRect(bodyLeft, yClose, bodyRight - bodyLeft, yOpen - yClose);
    ctx.strokeStyle = '#7aab8a';
    ctx.lineWidth = 1;
    ctx.strokeRect(bodyLeft, yClose, bodyRight - bodyLeft, yOpen - yClose);

    // ── Dashed connectors + RIGHT-side price labels ──
    const drawDashed = (x1, y, x2) => {
      ctx.strokeStyle = 'rgba(160,148,120,0.45)';
      ctx.lineWidth = 0.8;
      ctx.setLineDash([4, 3]);
      ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke();
      ctx.setLineDash([]);
    };

    const rightLabel = (y, title, titleColor, sub) => {
      ctx.fillStyle = titleColor;
      ctx.font = `bold 13px Jost, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(title, 388, y);
      ctx.fillStyle = 'rgba(160,148,120,0.65)';
      ctx.font = `11px Jost, sans-serif`;
      ctx.fillText(sub, 388, y + 14);
    };

    // High (top of upper wick) — dashed from x=290 to x=380, label at 388
    drawDashed(cx, yHigh, 380);
    rightLabel(yHigh, 'High', '#f0ede6', 'Highest price of the session');

    // Close (top of body) — dashed from x=320 to x=380, label at 388
    drawDashed(bodyRight, yClose, 380);
    rightLabel(yClose, 'Close', '#7aab8a', 'Where price settled — above open = green');

    // Open (bottom of body) — dashed from x=320 to x=380, label at 388
    drawDashed(bodyRight, yOpen, 380);
    rightLabel(yOpen, 'Open', '#7aab8a', 'Where price started the session');

    // Low (bottom of lower wick) — dashed from x=290 to x=380, label at 388
    drawDashed(cx, yLow, 380);
    rightLabel(yLow, 'Low', '#f0ede6', 'Lowest price of the session');

    // ── LEFT-side wick labels ──
    // Upper wick label (midpoint ≈ y=80)
    ctx.fillStyle = '#c47a7a';
    ctx.font = `11px Jost, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('Upper Wick (Shadow)', 20, 76);
    ctx.fillStyle = 'rgba(160,148,120,0.65)';
    ctx.font = `10px Jost, sans-serif`;
    ctx.fillText('Sellers rejected price here', 20, 90);
    // connector from label edge to wick midpoint
    ctx.strokeStyle = 'rgba(196,122,122,0.35)';
    ctx.lineWidth = 0.6;
    ctx.setLineDash([3, 3]);
    ctx.beginPath(); ctx.moveTo(200, 80); ctx.lineTo(cx - 4, 80); ctx.stroke();
    ctx.setLineDash([]);

    // Lower wick label (midpoint ≈ y=255)
    ctx.fillStyle = '#7aab8a';
    ctx.font = `11px Jost, sans-serif`;
    ctx.fillText('Lower Wick (Shadow)', 20, 251);
    ctx.fillStyle = 'rgba(160,148,120,0.65)';
    ctx.font = `10px Jost, sans-serif`;
    ctx.fillText('Buyers defended price here', 20, 265);
    ctx.strokeStyle = 'rgba(122,171,138,0.35)';
    ctx.lineWidth = 0.6;
    ctx.setLineDash([3, 3]);
    ctx.beginPath(); ctx.moveTo(200, 255); ctx.lineTo(cx - 4, 255); ctx.stroke();
    ctx.setLineDash([]);

    // ── Centered Body label ──
    ctx.fillStyle = '#c9a84c';
    ctx.font = `bold 13px Jost, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Body', cx, 166);
    ctx.fillStyle = 'rgba(240,237,230,0.85)';
    ctx.font = `10px Jost, sans-serif`;
    ctx.fillText('Open → Close range', cx, 180);
  }, []);

  return (
    <div>
      <p style={{ fontSize:'13px', color:V.muted, lineHeight:1.8, fontFamily:"'Jost',sans-serif", marginBottom:'16px' }}>
        A candlestick is the foundational unit of price analysis. Each one tells the complete story of one time period — from where price opened, to where it stretched, to where it settled.
      </p>
      <p style={{ ...ss, fontSize:'22px', color:V.gold, fontWeight:400, margin:'0 0 16px' }}>Anatomy of a Candlestick</p>
      <canvas
        ref={ref}
        width={580}
        height={320}
        style={{ display:'block', width:'100%', maxWidth:'580px', borderRadius:'8px', background:'#0a0a0c' }}
      />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginTop:'16px' }}>
        <div style={{ background:V.bg3, border:`1px solid ${V.sage}40`, borderRadius:'8px', padding:'14px' }}>
          <p style={{ color:V.sage, fontFamily:"'Jost',sans-serif", fontSize:'13px', fontWeight:600, margin:'0 0 6px' }}>▲ BULLISH (GREEN)</p>
          <p style={{ color:V.muted, fontSize:'13px', lineHeight:1.6, margin:0 }}>Close is ABOVE open. Buyers won the session.</p>
        </div>
        <div style={{ background:V.bg3, border:`1px solid ${V.rose}40`, borderRadius:'8px', padding:'14px' }}>
          <p style={{ color:V.rose, fontFamily:"'Jost',sans-serif", fontSize:'13px', fontWeight:600, margin:'0 0 6px' }}>▼ BEARISH (RED)</p>
          <p style={{ color:V.muted, fontSize:'13px', lineHeight:1.6, margin:0 }}>Close is BELOW open. Sellers won. Same structure — opposite outcome.</p>
        </div>
      </div>
      <p style={{ ...eyebrow, color:V.gold, margin:'20px 0 12px' }}>WHAT EACH PART TELLS YOU</p>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
        {[
          { title:'Upper Wick', body:'Price stretched up to this level but sellers stepped in and pushed it back down. The longer the upper wick, the stronger the rejection.' },
          { title:'The Body',   body:'The distance between open and close. A large body means strong conviction. A small body means indecision — buyers and sellers were roughly equal.' },
          { title:'Lower Wick', body:'Price stretched down to this level but buyers stepped in and pushed it back up. The longer the lower wick, the stronger the support.' },
          { title:'Color',      body:'Green body = close above open. Buyers won the session. Red body = close below open. Sellers won.' },
        ].map(({ title, body }, i) => (
          <div key={i} style={{ background:V.bg2, borderRadius:'8px', padding:'14px' }}>
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', fontWeight:600, color:V.text, margin:'0 0 6px' }}>{title}</p>
            <p style={{ fontSize:'13px', color:V.muted, lineHeight:1.6, margin:0 }}>{body}</p>
          </div>
        ))}
      </div>
      <div style={{ background:V.bg2, borderLeft:`3px solid ${V.gold}`, borderRadius:'8px', padding:'14px', marginTop:'16px' }}>
        <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', fontWeight:600, color:V.gold, margin:'0 0 6px' }}>Volume — The Confirmation</p>
        <p style={{ fontSize:'12px', color:V.muted, lineHeight:1.7, margin:0 }}>Below every candlestick on your TOS chart you will see a volume bar. A tall volume bar on a green candle means many buyers were committed. A tall volume bar on a red candle means many sellers were committed. Volume confirms what the candle is telling you. You will learn to read volume in detail in the Volume tab.</p>
      </div>
    </div>
  );
}

function Ch02CandlestickTabVisual({ onNavigate }) {
  const ss = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
  const jost = { fontFamily: "'Jost',sans-serif" };
  const hr = { height: '0.5px', background: 'rgba(201,168,76,0.15)', margin: '28px 0' };

  const singlePatterns = [
    { k: 'doji',         n: 'Doji',            badge: 'INDECISION',       badgeColor: V.gold,
      body: 'Open and close are nearly identical. Neither buyers nor sellers won this session. Often appears before a reversal — especially at support or resistance.' },
    { k: 'hammer',       n: 'Hammer',           badge: 'BULLISH REVERSAL', badgeColor: V.sage,
      body: 'Price fell hard during the session but buyers stepped in and pushed it back up. The long lower wick shows rejected selling. Most powerful at support zones.' },
    { k: 'shootingstar', n: 'Shooting Star',    badge: 'BEARISH REJECTION',badgeColor: V.rose,
      body: 'Price rose sharply but sellers overwhelmed buyers and pushed it back down. The long upper wick shows rejected buying. Most powerful at resistance zones.' },
    { k: 'marubozu',     n: 'Bullish Marubozu', badge: 'STRONG CONVICTION', badgeColor: V.sage,
      body: 'No wicks means price opened at the low and closed at the high. Buyers were in complete control for the entire session. Strong continuation signal.' },
    { k: 'spinningtop',  n: 'Spinning Top',     badge: 'INDECISION',       badgeColor: V.gold,
      body: 'Small body with similar wicks on both sides. Buyers and sellers fought to a draw. Similar to a Doji — watch for what comes next.' },
  ];

  const multiPatterns = [
    { k: 'bullishengulfing', n: 'Bullish Engulfing', badge: 'BULLISH REVERSAL', badgeColor: V.sage,
      body: 'A large green candle completely engulfs the previous red candle. Buyers overwhelmed sellers decisively. One of the most reliable reversal signals — especially at support.' },
    { k: 'bearishengulfing', n: 'Bearish Engulfing', badge: 'BEARISH REVERSAL', badgeColor: V.rose,
      body: 'A large red candle completely engulfs the previous green candle. Sellers overwhelmed buyers decisively. Watch for this at resistance zones.' },
    { k: 'morningstar',      n: 'Morning Star',      badge: 'BULLISH REVERSAL', badgeColor: V.sage,
      body: 'Three candle reversal pattern. Bearish candle, indecision, then a strong bullish candle. The market found its footing. Most powerful at support zones.' },
    { k: 'eveningstar',      n: 'Evening Star',      badge: 'BEARISH REVERSAL', badgeColor: V.rose,
      body: 'The bearish mirror of the Morning Star. Bullish candle, indecision, strong bearish candle. Sellers took control. Watch for this at resistance zones.' },
  ];

  const extDrawers = {
    spinningtop: (cv) => {
      const ctx = cv.getContext('2d'), cx = 50;
      ctx.clearRect(0,0,100,100); ctx.fillStyle='rgba(26,22,16,0.92)'; ctx.fillRect(0,0,100,100);
      const py=(v)=>10+78*(1-(v-44)/22);
      ctx.strokeStyle='rgba(200,185,150,0.65)'; ctx.lineWidth=1.5;
      ctx.beginPath(); ctx.moveTo(cx,py(64)); ctx.lineTo(cx,py(46)); ctx.stroke();
      const bt=py(57),bh=Math.max(py(53)-bt,6);
      ctx.fillStyle='rgba(122,171,138,0.6)'; ctx.strokeStyle='rgba(122,171,138,0.8)'; ctx.lineWidth=0.8;
      ctx.fillRect(cx-14,bt,28,bh); ctx.strokeRect(cx-14,bt,28,bh);
    },
    morningstar: (cv) => {
      const ctx=cv.getContext('2d');
      ctx.clearRect(0,0,100,100); ctx.fillStyle='rgba(26,22,16,0.92)'; ctx.fillRect(0,0,100,100);
      const py=(v)=>8+82*(1-(v-42)/24);
      const drawC=(x,o,h,l,c,w)=>{
        const bull=c>=o; const clr=bull?'#7aab8a':'#c47a7a';
        ctx.strokeStyle=clr; ctx.lineWidth=1;
        ctx.beginPath(); ctx.moveTo(x,py(h)); ctx.lineTo(x,py(l)); ctx.stroke();
        const bt=py(Math.max(o,c)),bh=Math.max(py(Math.min(o,c))-bt,3);
        ctx.fillStyle=bull?'rgba(122,171,138,0.75)':'rgba(196,122,122,0.75)';
        ctx.strokeStyle=clr; ctx.lineWidth=0.6;
        ctx.fillRect(x-w/2,bt,w,bh); ctx.strokeRect(x-w/2,bt,w,bh);
      };
      drawC(22,58,61,51,52,16); // red
      drawC(50,53,56,52,54,8);  // doji
      drawC(78,53,64,52,63,16); // green
    },
    eveningstar: (cv) => {
      const ctx=cv.getContext('2d');
      ctx.clearRect(0,0,100,100); ctx.fillStyle='rgba(26,22,16,0.92)'; ctx.fillRect(0,0,100,100);
      const py=(v)=>8+82*(1-(v-42)/24);
      const drawC=(x,o,h,l,c,w)=>{
        const bull=c>=o; const clr=bull?'#7aab8a':'#c47a7a';
        ctx.strokeStyle=clr; ctx.lineWidth=1;
        ctx.beginPath(); ctx.moveTo(x,py(h)); ctx.lineTo(x,py(l)); ctx.stroke();
        const bt=py(Math.max(o,c)),bh=Math.max(py(Math.min(o,c))-bt,3);
        ctx.fillStyle=bull?'rgba(122,171,138,0.75)':'rgba(196,122,122,0.75)';
        ctx.strokeStyle=clr; ctx.lineWidth=0.6;
        ctx.fillRect(x-w/2,bt,w,bh); ctx.strokeRect(x-w/2,bt,w,bh);
      };
      drawC(22,52,63,51,63,16); // green
      drawC(50,63,66,62,64,8);  // doji
      drawC(78,63,64,52,53,16); // red
    },
  };
  const allDrawers = { ...patternDrawers, ...extDrawers };

  const PatCard = ({ k, n, badge, badgeColor, body }) => (
    <div style={{ background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <PatternMiniCanvas drawFn={allDrawers[k]} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <span style={{ ...ss, fontSize: '15px', color: V.gold, fontWeight: 400 }}>{n}</span>
        <span style={{ ...jost, fontSize: '9px', color: badgeColor, background: `${badgeColor}18`, border: `0.5px solid ${badgeColor}55`, borderRadius: '4px', padding: '2px 6px', letterSpacing: '0.08em' }}>{badge}</span>
      </div>
      <p style={{ ...jost, fontSize: '12px', color: V.muted, lineHeight: 1.65, margin: 0 }}>{body}</p>
    </div>
  );

  return (
    <div>
      <div style={{ height:'0.5px', background:'rgba(201,168,76,0.15)', margin:'20px 0 24px' }} />
      <Ch02CandlestickAnatomy />
      <div style={{ height:'0.5px', background:'rgba(201,168,76,0.15)', margin:'28px 0' }} />
      <h3 style={{ ...ss, fontSize: '22px', color: V.gold, fontWeight: 400, margin: '0 0 6px' }}>Candlestick Patterns</h3>
      <p style={{ ...jost, fontSize: '13px', color: V.muted, fontStyle:'italic', lineHeight: 1.7, margin: '0 0 20px' }}>
        Individual candles and groups of candles tell specific stories. Learn to recognize these patterns on any chart.
      </p>
      <div style={{ ...jost, fontSize: '11px', color: V.gold, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '10px' }}>Single Candle Patterns</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        {singlePatterns.map(p => <PatCard key={p.k} {...p} />)}
      </div>
      <div style={{ ...jost, fontSize: '11px', color: V.gold, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '10px' }}>Two & Three Candle Patterns</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        {multiPatterns.map(p => <PatCard key={p.k} {...p} />)}
      </div>
      <div style={hr} />
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 10px 10px 0', padding: '18px 22px', marginBottom: '20px' }}>
        <h3 style={{ ...ss, fontSize: '20px', color: V.gold, fontWeight: 400, margin: '0 0 10px' }}>The Rubber Band in Every Pattern</h3>
        <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: '0 0 8px' }}>
          Every candlestick pattern is showing you a moment in the rubber band's journey. Hammers and morning stars appear when the rubber band is fully stretched bearish and buyers step in. Shooting stars and evening stars appear when it is fully stretched bullish and sellers step in.
        </p>
        <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: 0, fontStyle: 'italic' }}>
          The pattern is the rubber band beginning to snap back.
        </p>
      </div>
      <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px' }}
        onClick={() => onNavigate && onNavigate('ind_candles_practice')}>
        Practice Candlestick Reading →
      </button>
    </div>
  );
}

function Ch02TrendVisual() {
  const upRef = useRef(null), dnRef = useRef(null), swRef = useRef(null);

  const drawTrend = (canvas, type) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0,0,W,H);
    ctx.fillStyle = 'rgba(14,12,8,0.95)'; ctx.fillRect(0,0,W,H);
    const pad = { l:18, r:18, t:22, b:24 };
    const gW = W-pad.l-pad.r, gH = H-pad.t-pad.b;

    if (type === 'up') {
      const pts = [
        {x:0,y:0.72},{x:0.12,y:0.62},{x:0.22,y:0.52},{x:0.32,y:0.60},{x:0.42,y:0.48},
        {x:0.52,y:0.38},{x:0.62,y:0.46},{x:0.72,y:0.34},{x:0.82,y:0.24},{x:0.92,y:0.32},{x:1,y:0.20}
      ].map(p=>({x:pad.l+p.x*gW, y:pad.t+p.y*gH}));
      // gradient fill
      const g = ctx.createLinearGradient(0,pad.t,0,pad.t+gH);
      g.addColorStop(0,'rgba(122,171,138,0.15)'); g.addColorStop(1,'rgba(122,171,138,0)');
      ctx.beginPath(); pts.forEach((p,i)=>i===0?ctx.moveTo(p.x,p.y):ctx.lineTo(p.x,p.y));
      ctx.lineTo(pts[pts.length-1].x,pad.t+gH); ctx.lineTo(pts[0].x,pad.t+gH);
      ctx.closePath(); ctx.fillStyle=g; ctx.fill();
      // price line
      ctx.beginPath(); ctx.strokeStyle='#7aab8a'; ctx.lineWidth=2;
      pts.forEach((p,i)=>i===0?ctx.moveTo(p.x,p.y):ctx.lineTo(p.x,p.y)); ctx.stroke();
      // HH/HL markers
      [[2,'HH'],[4,'HH'],[7,'HH'],[3,'HL'],[6,'HL'],[9,'HL']].forEach(([i,lbl])=>{
        const isHH=lbl==='HH';
        ctx.fillStyle=isHH?'rgba(122,171,138,0.85)':'rgba(122,171,138,0.6)';
        ctx.font=`bold 9px Jost,sans-serif`; ctx.textAlign='center';
        ctx.fillText(lbl, pts[i].x, pts[i].y+(isHH?-6:12));
      });
    } else if (type === 'down') {
      const pts = [
        {x:0,y:0.20},{x:0.12,y:0.30},{x:0.22,y:0.42},{x:0.32,y:0.32},{x:0.42,y:0.46},
        {x:0.52,y:0.56},{x:0.62,y:0.48},{x:0.72,y:0.62},{x:0.82,y:0.70},{x:0.92,y:0.62},{x:1,y:0.76}
      ].map(p=>({x:pad.l+p.x*gW, y:pad.t+p.y*gH}));
      ctx.beginPath(); ctx.strokeStyle='#c47a7a'; ctx.lineWidth=2;
      pts.forEach((p,i)=>i===0?ctx.moveTo(p.x,p.y):ctx.lineTo(p.x,p.y)); ctx.stroke();
      [[2,'LL'],[4,'LL'],[7,'LL'],[3,'LH'],[6,'LH'],[9,'LH']].forEach(([i,lbl])=>{
        const isLL=lbl==='LL';
        ctx.fillStyle=isLL?'rgba(196,122,122,0.85)':'rgba(196,122,122,0.6)';
        ctx.font='bold 9px Jost,sans-serif'; ctx.textAlign='center';
        ctx.fillText(lbl, pts[i].x, pts[i].y+(isLL?12:-6));
      });
    } else {
      const pts = [
        {x:0,y:0.45},{x:0.1,y:0.38},{x:0.2,y:0.52},{x:0.3,y:0.40},{x:0.4,y:0.55},
        {x:0.5,y:0.42},{x:0.6,y:0.57},{x:0.7,y:0.43},{x:0.8,y:0.56},{x:0.9,y:0.44},{x:1,y:0.50}
      ].map(p=>({x:pad.l+p.x*gW, y:pad.t+p.y*gH}));
      // range band
      ctx.fillStyle='rgba(201,168,76,0.06)';
      ctx.fillRect(pad.l, pad.t+gH*0.32, gW, gH*0.34);
      ctx.strokeStyle='rgba(201,168,76,0.25)'; ctx.lineWidth=0.8; ctx.setLineDash([4,3]);
      ctx.beginPath(); ctx.moveTo(pad.l,pad.t+gH*0.32); ctx.lineTo(pad.l+gW,pad.t+gH*0.32); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(pad.l,pad.t+gH*0.66); ctx.lineTo(pad.l+gW,pad.t+gH*0.66); ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath(); ctx.strokeStyle='rgba(201,168,76,0.7)'; ctx.lineWidth=1.5;
      pts.forEach((p,i)=>i===0?ctx.moveTo(p.x,p.y):ctx.lineTo(p.x,p.y)); ctx.stroke();
      ctx.fillStyle='rgba(201,168,76,0.5)'; ctx.font='9px Jost,sans-serif'; ctx.textAlign='center';
      ctx.fillText('RANGE', pad.l+gW*0.5, pad.t+gH*0.49);
    }
  };

  useEffect(()=>{ drawTrend(upRef.current,'up'); drawTrend(dnRef.current,'down'); drawTrend(swRef.current,'side'); },[]);

  const jost = { fontFamily:"'Jost',sans-serif" };
  const ss = { fontFamily:"'Cormorant Garamond',Georgia,serif" };

  const states = [
    { ref: upRef, color: V.sage, title: 'UPTREND', body: 'Price making Higher Highs (HH) and Higher Lows (HL). Each rally exceeds the last. Each pullback holds above the previous pullback. The rubber band is stretching upward and snapping back — but each snapback lands higher. This is the environment where CSP sellers thrive.' },
    { ref: dnRef, color: V.rose, title: 'DOWNTREND', body: 'Price making Lower Lows (LL) and Lower Highs (LH). Each decline goes deeper. Each bounce fails below the previous bounce. CSP sellers use extreme caution in downtrends — support breaks. Wait for reversal confirmation before re-entering.' },
    { ref: swRef, color: V.gold, title: 'SIDEWAYS / CONSOLIDATION', body: 'Price moving in a tight range with no clear direction. The rubber band is coiling. A significant move is coming — direction unknown yet. Wait for a clear breakout before committing to a direction.' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', margin: '20px 0' }}>
        {states.map(s => (
          <div key={s.title}>
            <canvas ref={s.ref} width={560} height={120}
              style={{ width:'100%', maxWidth:'560px', display:'block', borderRadius:'8px', border:`0.5px solid ${s.color}33` }} />
            <div style={{ background: V.bg3, border: `0.5px solid ${s.color}44`, borderLeft: `3px solid ${s.color}`, borderRadius:'0 8px 8px 0', padding:'12px 16px', marginTop:'0' }}>
              <div style={{ ...jost, fontSize:'12px', fontWeight:600, color:s.color, marginBottom:'4px', letterSpacing:'0.08em' }}>{s.title}</div>
              <p style={{ ...jost, fontSize:'13px', color:V.muted, lineHeight:1.75, margin:0 }}>{s.body}</p>
            </div>
          </div>
        ))}
      </div>
      <div style={{ background: V.bg2, borderLeft:`3px solid ${V.gold}`, borderRadius:'8px', padding:'16px 20px', marginTop:'8px' }}>
        <div style={{ ...jost, fontSize:'13px', fontWeight:600, color:V.gold, marginBottom:'8px' }}>The Simple Trend Rule</div>
        <p style={{ ...jost, fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 8px' }}>Look at the last 3–5 price swings.</p>
        <p style={{ ...jost, fontSize:'12px', color:V.text, lineHeight:1.85, margin:'0 0 4px' }}>
          <span style={{ color:V.sage }}>Uptrend:</span> Each swing high is higher than the last ✓ — Each swing low is higher than the last ✓
        </p>
        <p style={{ ...jost, fontSize:'12px', color:V.text, lineHeight:1.85, margin:'0 0 4px' }}>
          <span style={{ color:V.rose }}>Downtrend:</span> Each swing high is lower than the last ✓ — Each swing low is lower than the last ✓
        </p>
        <p style={{ ...jost, fontSize:'12px', color:V.muted, lineHeight:1.85, margin:0, fontStyle:'italic' }}>
          No clear pattern: Consolidation or choppy market. No trade.
        </p>
      </div>
      <div style={{ background: V.bg2, borderLeft:`3px solid ${V.gold}`, borderRadius:'8px', padding:'14px 18px', marginTop:'16px' }}>
        <p style={{ ...jost, fontSize:'12px', color:V.muted, fontStyle:'italic', lineHeight:1.75, margin:0 }}>
          In Chapter 3 you will add Moving Average lines to your chart. Those lines will make trend direction even clearer — they visually confirm the higher highs and higher lows you just learned to read here.
        </p>
      </div>
    </div>
  );
}

function CandleVolumeCard({ label, bullish, highVolume, badge, badgeColor, description }) {
  const candleColor = bullish ? '#7aab8a' : '#c47a7a';
  const badgeBg = badgeColor === 'sage' ? 'rgba(122,171,138,0.15)' : badgeColor === 'rose' ? 'rgba(196,122,122,0.15)' : 'rgba(201,168,76,0.15)';
  const badgeBorder = badgeColor === 'sage' ? '#7aab8a' : badgeColor === 'rose' ? '#c47a7a' : '#c9a84c';
  const badgeText = badgeColor === 'sage' ? '#7aab8a' : badgeColor === 'rose' ? '#c47a7a' : '#c9a84c';
  return (
    <div style={{ background: '#111114', borderRadius: '10px', padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ fontSize: '11px', color: '#f0ede6', fontFamily: "'Jost',sans-serif", fontWeight: '600', letterSpacing: '0.5px', textAlign: 'center' }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '140px', justifyContent: 'flex-end' }}>
        <div style={{ width: '2px', height: '24px', background: candleColor, flexShrink: 0 }} />
        <div style={{ width: '36px', height: '60px', background: candleColor, borderRadius: '2px', flexShrink: 0 }} />
        <div style={{ width: '2px', height: '16px', background: candleColor, flexShrink: 0 }} />
        <div style={{ height: '8px' }} />
        <div style={{ width: '36px', height: highVolume ? '28px' : '8px', background: candleColor, borderRadius: '2px', flexShrink: 0, opacity: highVolume ? 1 : 0.5 }} />
        <div style={{ fontSize: '9px', color: candleColor, fontFamily: "'Jost',sans-serif", fontWeight: '700', letterSpacing: '1px', marginTop: '4px', opacity: highVolume ? 1 : 0.7 }}>
          {highVolume ? 'HIGH VOLUME' : 'LOW VOLUME'}
        </div>
      </div>
      <div style={{ background: badgeBg, border: `1px solid ${badgeBorder}`, borderRadius: '4px', padding: '4px 10px', fontSize: '11px', color: badgeText, fontFamily: "'Jost',sans-serif", fontWeight: '700', textAlign: 'center' }}>{badge}</div>
      <div style={{ fontSize: '11px', color: 'rgba(240,237,230,0.6)', fontFamily: "'Jost',sans-serif", fontStyle: 'italic', textAlign: 'center', lineHeight: '1.5' }}>{description}</div>
    </div>
  );
}

function Ch02VolumeScenarios() {
  return (
    <>
      <div style={{ fontSize: '12px', color: 'rgba(201,168,76,0.7)', fontFamily: "'Jost',sans-serif", letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px', marginTop: '24px' }}>
        Four Scenarios Every Trader Must Know
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '24px' }}>
        <CandleVolumeCard label="PRICE UP + VOLUME UP" bullish={true} highVolume={true} badge="STRONG BULLISH ✓" badgeColor="sage" description="Buyers committed. Move has real conviction." />
        <CandleVolumeCard label="PRICE UP + VOLUME DOWN" bullish={true} highVolume={false} badge="WEAK — CAUTION ⚠" badgeColor="gold" description="Few participants. May not sustain." />
        <CandleVolumeCard label="PRICE DOWN + VOLUME UP" bullish={false} highVolume={true} badge="STRONG BEARISH ✗" badgeColor="rose" description="Sellers committed. Real selling pressure." />
        <CandleVolumeCard label="PRICE DOWN + VOLUME DOWN" bullish={false} highVolume={false} badge="WEAK SELLOFF ○" badgeColor="gold" description="Few sellers. Healthy pullback — possible CSP zone." />
      </div>
    </>
  );
}

function Ch02VolumeVisual() {
  const jost = {fontFamily:"'Jost',sans-serif"};
  return (
    <div>
      <Ch02VolumeScenarios />
      <div style={{ background:V.bg3, border:`0.5px solid ${V.gold}44`, borderRadius:'10px', padding:'18px 20px', marginBottom:'16px' }}>
        <div style={{ ...jost, fontSize:'13px', fontWeight:600, color:V.gold, marginBottom:'8px' }}>Volume Confirms Breakouts</div>
        <p style={{ ...jost, fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 8px' }}>
          When price breaks above resistance or below support — check volume.
        </p>
        <p style={{ ...jost, fontSize:'13px', color:V.text, lineHeight:1.8, margin:'0 0 6px' }}>
          <span style={{color:V.sage}}>High volume breakout:</span> Real move. Buyers/sellers committed. The rubber band has released with real participation behind it.
        </p>
        <p style={{ ...jost, fontSize:'13px', color:V.text, lineHeight:1.8, margin:'0 0 10px' }}>
          <span style={{color:V.rose}}>Low volume breakout:</span> Suspect. False breakout likely. Price often reverses back through the level it just broke.
        </p>
        <p style={{ ...jost, fontSize:'12px', color:V.muted, fontStyle:'italic', lineHeight:1.75, margin:0 }}>
          Rule: Never adjust your strike selection based on a breakout until volume confirms the move.
        </p>
      </div>
      <div style={{ background:V.bg2, borderLeft:`3px solid ${V.gold}`, borderRadius:'8px', padding:'14px 18px' }}>
        <p style={{ ...jost, fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:0 }}>
          High volume at extremes — at the top of a rally or the bottom of a selloff — often marks the rubber band at maximum stretch. Heavy volume exhaustion moves are some of the best signals that a snapback is near.
        </p>
      </div>
    </div>
  );
}

function Ch02ChartPatternsVisual({ onNavigate }) {
  const jost = {fontFamily:"'Jost',sans-serif"};
  const ss = {fontFamily:"'Cormorant Garamond',Georgia,serif"};

  const PatternCanvas = ({ drawFn, w=260, h=130 }) => {
    const ref = useRef(null);
    useEffect(()=>{ if(ref.current) drawFn(ref.current); },[]);
    return <canvas ref={ref} width={w} height={h}
      style={{display:'block',width:'100%',maxWidth:`${w}px`,borderRadius:'8px',border:'0.5px solid rgba(120,110,90,0.2)',background:'rgba(14,12,8,0.95)'}} />;
  };

  const drawDoubleTop = (cv) => {
    const ctx=cv.getContext('2d'); const W=cv.width,H=cv.height;
    ctx.clearRect(0,0,W,H); ctx.fillStyle='rgba(14,12,8,0.95)'; ctx.fillRect(0,0,W,H);
    const pts=[{x:0,y:0.8},{x:0.15,y:0.3},{x:0.3,y:0.55},{x:0.5,y:0.3},{x:0.65,y:0.55},{x:0.8,y:0.85},{x:1,y:0.95}];
    ctx.strokeStyle='rgba(196,122,122,0.8)'; ctx.lineWidth=2; ctx.beginPath();
    pts.forEach((p,i)=>{const x=10+p.x*(W-20),y=10+p.y*(H-20); i===0?ctx.moveTo(x,y):ctx.lineTo(x,y)}); ctx.stroke();
    // neck
    ctx.strokeStyle='rgba(201,168,76,0.4)'; ctx.lineWidth=1; ctx.setLineDash([4,3]);
    ctx.beginPath(); ctx.moveTo(10,10+0.55*(H-20)); ctx.lineTo(W-10,10+0.55*(H-20)); ctx.stroke(); ctx.setLineDash([]);
    ctx.fillStyle='rgba(201,168,76,0.5)'; ctx.font='9px Jost,sans-serif'; ctx.textAlign='right';
    ctx.fillText('Neckline',W-12,10+0.52*(H-20));
    ctx.fillStyle='rgba(196,122,122,0.6)'; ctx.font='bold 8px Jost,sans-serif'; ctx.textAlign='center';
    ctx.fillText('TOP',10+0.15*(W-20),10+0.22*(H-20));
    ctx.fillText('TOP',10+0.5*(W-20),10+0.22*(H-20));
  };
  const drawDoubleBot = (cv) => {
    const ctx=cv.getContext('2d'); const W=cv.width,H=cv.height;
    ctx.clearRect(0,0,W,H); ctx.fillStyle='rgba(14,12,8,0.95)'; ctx.fillRect(0,0,W,H);
    const pts=[{x:0,y:0.2},{x:0.15,y:0.75},{x:0.3,y:0.45},{x:0.5,y:0.75},{x:0.65,y:0.45},{x:0.8,y:0.2},{x:1,y:0.1}];
    ctx.strokeStyle='rgba(122,171,138,0.8)'; ctx.lineWidth=2; ctx.beginPath();
    pts.forEach((p,i)=>{const x=10+p.x*(W-20),y=10+p.y*(H-20); i===0?ctx.moveTo(x,y):ctx.lineTo(x,y)}); ctx.stroke();
    ctx.strokeStyle='rgba(201,168,76,0.4)'; ctx.lineWidth=1; ctx.setLineDash([4,3]);
    ctx.beginPath(); ctx.moveTo(10,10+0.45*(H-20)); ctx.lineTo(W-10,10+0.45*(H-20)); ctx.stroke(); ctx.setLineDash([]);
    ctx.fillStyle='rgba(201,168,76,0.5)'; ctx.font='9px Jost,sans-serif'; ctx.textAlign='right';
    ctx.fillText('Neckline',W-12,10+0.42*(H-20));
    ctx.fillStyle='rgba(122,171,138,0.6)'; ctx.font='bold 8px Jost,sans-serif'; ctx.textAlign='center';
    ctx.fillText('BOT',10+0.15*(W-20),10+0.85*(H-20));
    ctx.fillText('BOT',10+0.5*(W-20),10+0.85*(H-20));
  };
  const drawHnS = (cv) => {
    const ctx=cv.getContext('2d'); const W=cv.width,H=cv.height;
    ctx.clearRect(0,0,W,H); ctx.fillStyle='rgba(14,12,8,0.95)'; ctx.fillRect(0,0,W,H);
    const pts=[{x:0,y:0.8},{x:0.15,y:0.45},{x:0.3,y:0.58},{x:0.5,y:0.2},{x:0.7,y:0.58},{x:0.85,y:0.45},{x:1,y:0.9}];
    ctx.strokeStyle='rgba(196,122,122,0.8)'; ctx.lineWidth=2; ctx.beginPath();
    pts.forEach((p,i)=>{const x=10+p.x*(W-20),y=10+p.y*(H-20); i===0?ctx.moveTo(x,y):ctx.lineTo(x,y)}); ctx.stroke();
    ctx.strokeStyle='rgba(201,168,76,0.4)'; ctx.lineWidth=1; ctx.setLineDash([4,3]);
    ctx.beginPath(); ctx.moveTo(10,10+0.58*(H-20)); ctx.lineTo(W-10,10+0.58*(H-20)); ctx.stroke(); ctx.setLineDash([]);
    ['L','H','R'].forEach((lbl,i)=>{
      const xs=[0.15,0.5,0.85]; const ys=[0.45,0.2,0.45];
      ctx.fillStyle='rgba(196,122,122,0.6)'; ctx.font='bold 8px Jost,sans-serif'; ctx.textAlign='center';
      ctx.fillText(lbl==='H'?'HEAD':lbl+'SH', 10+xs[i]*(W-20), 10+ys[i]*(H-20)-5);
    });
  };
  const drawBullFlag = (cv) => {
    const ctx=cv.getContext('2d'); const W=cv.width,H=cv.height;
    ctx.clearRect(0,0,W,H); ctx.fillStyle='rgba(14,12,8,0.95)'; ctx.fillRect(0,0,W,H);
    // pole
    ctx.strokeStyle='rgba(122,171,138,0.8)'; ctx.lineWidth=2.5;
    ctx.beginPath(); ctx.moveTo(30,H-20); ctx.lineTo(30,20); ctx.stroke();
    // flag (consolidation)
    const fp=[{x:30,y:20},{x:55,y:30},{x:80,y:24},{x:105,y:34},{x:130,y:28}];
    ctx.strokeStyle='rgba(201,168,76,0.6)'; ctx.lineWidth=1.5;
    ctx.beginPath(); fp.forEach((p,i)=>i===0?ctx.moveTo(p.x,p.y):ctx.lineTo(p.x,p.y)); ctx.stroke();
    const fp2=[{x:30,y:34},{x:55,y:44},{x:80,y:38},{x:105,y:48},{x:130,y:42}];
    ctx.beginPath(); fp2.forEach((p,i)=>i===0?ctx.moveTo(p.x,p.y):ctx.lineTo(p.x,p.y)); ctx.stroke();
    // breakout
    ctx.strokeStyle='rgba(122,171,138,0.9)'; ctx.lineWidth=2.5;
    ctx.beginPath(); ctx.moveTo(130,28); ctx.lineTo(W-15,8); ctx.stroke();
    ctx.fillStyle='rgba(122,171,138,0.5)'; ctx.font='8px Jost,sans-serif'; ctx.textAlign='center';
    ctx.fillText('FLAG',80,52); ctx.fillText('BREAKOUT→',W-40,14);
  };
  const drawRisingWedge = (cv) => {
    const ctx=cv.getContext('2d'); const W=cv.width,H=cv.height;
    ctx.clearRect(0,0,W,H); ctx.fillStyle='rgba(14,12,8,0.95)'; ctx.fillRect(0,0,W,H);
    const p = W-20;
    ctx.strokeStyle='rgba(201,168,76,0.6)'; ctx.lineWidth=1.5; ctx.setLineDash([3,3]);
    ctx.beginPath(); ctx.moveTo(10,H-20); ctx.lineTo(p,20); ctx.stroke(); // upper trend line
    ctx.beginPath(); ctx.moveTo(10,H-10); ctx.lineTo(p,40); ctx.stroke(); // lower trend line
    ctx.setLineDash([]);
    // price oscillating between lines
    const pts=[{x:10,y:H-15},{x:p*0.18,y:H-40},{x:p*0.32,y:H-25},{x:p*0.48,y:H-58},{x:p*0.62,y:H-46},{x:p*0.76,y:H-70},{x:p*0.88,y:H-60},{x:p,y:H-78}];
    ctx.strokeStyle='rgba(196,122,122,0.75)'; ctx.lineWidth=2;
    ctx.beginPath(); pts.forEach((p2,i)=>i===0?ctx.moveTo(p2.x,p2.y):ctx.lineTo(p2.x,p2.y)); ctx.stroke();
    ctx.fillStyle='rgba(201,168,76,0.4)'; ctx.font='9px Jost,sans-serif'; ctx.textAlign='center';
    ctx.fillText('Converging →',W/2,H-5);
  };
  const drawFallingWedge = (cv) => {
    const ctx=cv.getContext('2d'); const W=cv.width,H=cv.height;
    ctx.clearRect(0,0,W,H); ctx.fillStyle='rgba(14,12,8,0.95)'; ctx.fillRect(0,0,W,H);
    const p=W-20;
    ctx.strokeStyle='rgba(201,168,76,0.6)'; ctx.lineWidth=1.5; ctx.setLineDash([3,3]);
    ctx.beginPath(); ctx.moveTo(10,20); ctx.lineTo(p,H-40); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(10,40); ctx.lineTo(p,H-20); ctx.stroke();
    ctx.setLineDash([]);
    const pts=[{x:10,y:25},{x:p*0.18,y:60},{x:p*0.32,y:40},{x:p*0.48,y:80},{x:p*0.62,y:60},{x:p*0.76,y:95},{x:p*0.88,y:78},{x:p,y:H-35}];
    ctx.strokeStyle='rgba(122,171,138,0.75)'; ctx.lineWidth=2;
    ctx.beginPath(); pts.forEach((p2,i)=>i===0?ctx.moveTo(p2.x,p2.y):ctx.lineTo(p2.x,p2.y)); ctx.stroke();
    ctx.fillStyle='rgba(201,168,76,0.4)'; ctx.font='9px Jost,sans-serif'; ctx.textAlign='center';
    ctx.fillText('Converging ↓',W/2,H-5);
  };
  const drawAscTriangle = (cv) => {
    const ctx=cv.getContext('2d'); const W=cv.width,H=cv.height;
    ctx.clearRect(0,0,W,H); ctx.fillStyle='rgba(14,12,8,0.95)'; ctx.fillRect(0,0,W,H);
    // flat resistance
    ctx.strokeStyle='rgba(196,122,122,0.5)'; ctx.lineWidth=1.5; ctx.setLineDash([4,3]);
    ctx.beginPath(); ctx.moveTo(10,28); ctx.lineTo(W-10,28); ctx.stroke(); ctx.setLineDash([]);
    // rising support
    ctx.strokeStyle='rgba(122,171,138,0.5)'; ctx.lineWidth=1; ctx.setLineDash([3,3]);
    ctx.beginPath(); ctx.moveTo(10,H-20); ctx.lineTo(W-10,40); ctx.stroke(); ctx.setLineDash([]);
    // price
    const pts=[{x:10,y:H-25},{x:0.15,y:28},{x:0.3,y:H*0.6},{x:0.45,y:32},{x:0.6,y:H*0.5},{x:0.75,y:36},{x:0.88,y:H*0.45},{x:1,y:28}];
    ctx.strokeStyle='rgba(201,168,76,0.8)'; ctx.lineWidth=2;
    ctx.beginPath(); pts.forEach((p2,i)=>{ const x=i===0?p2.x:(10+p2.x*(W-20)),y=i===0?p2.y:p2.y; i===0?ctx.moveTo(x,y):ctx.lineTo(x,y); }); ctx.stroke();
    ctx.fillStyle='rgba(196,122,122,0.4)'; ctx.font='9px Jost,sans-serif'; ctx.textAlign='right';
    ctx.fillText('Resistance',W-12,24);
  };
  const drawDescTriangle = (cv) => {
    const ctx=cv.getContext('2d'); const W=cv.width,H=cv.height;
    ctx.clearRect(0,0,W,H); ctx.fillStyle='rgba(14,12,8,0.95)'; ctx.fillRect(0,0,W,H);
    ctx.strokeStyle='rgba(122,171,138,0.5)'; ctx.lineWidth=1.5; ctx.setLineDash([4,3]);
    ctx.beginPath(); ctx.moveTo(10,H-28); ctx.lineTo(W-10,H-28); ctx.stroke(); ctx.setLineDash([]);
    ctx.strokeStyle='rgba(196,122,122,0.5)'; ctx.lineWidth=1; ctx.setLineDash([3,3]);
    ctx.beginPath(); ctx.moveTo(10,18); ctx.lineTo(W-10,H-38); ctx.stroke(); ctx.setLineDash([]);
    const pts=[{x:10,y:22},{x:0.15,y:H-28},{x:0.3,y:H*0.4},{x:0.45,y:H-32},{x:0.6,y:H*0.5},{x:0.75,y:H-35},{x:0.88,y:H*0.57},{x:1,y:H-28}];
    ctx.strokeStyle='rgba(201,168,76,0.8)'; ctx.lineWidth=2;
    ctx.beginPath(); pts.forEach((p2,i)=>{ const x=i===0?p2.x:(10+p2.x*(W-20)),y=i===0?p2.y:p2.y; i===0?ctx.moveTo(x,y):ctx.lineTo(x,y); }); ctx.stroke();
    ctx.fillStyle='rgba(122,171,138,0.4)'; ctx.font='9px Jost,sans-serif'; ctx.textAlign='right';
    ctx.fillText('Support',W-12,H-31);
  };

  const PCard = ({ title, badge, badgeColor, borderColor, body, drawFn }) => (
    <div style={{ background:V.bg3, border:`0.5px solid ${borderColor}44`, borderTop:`3px solid ${borderColor}`, borderRadius:'10px', padding:'14px', display:'flex', flexDirection:'column', gap:'10px' }}>
      <PatternCanvas drawFn={drawFn} />
      <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
        <span style={{ ...ss, fontSize:'16px', color:borderColor, fontWeight:400 }}>{title}</span>
        <span style={{ ...jost, fontSize:'9px', color:badgeColor, background:`${badgeColor}18`, border:`0.5px solid ${badgeColor}44`, borderRadius:'4px', padding:'2px 6px', letterSpacing:'0.07em' }}>{badge}</span>
      </div>
      <p style={{ ...jost, fontSize:'12px', color:V.muted, lineHeight:1.7, margin:0 }}>{body}</p>
    </div>
  );

  return (
    <div>
      <div style={{ height:'0.5px', background:'rgba(201,168,76,0.15)', margin:'20px 0 24px' }} />
      <h3 style={{ ...ss, fontSize:'22px', color:V.gold, fontWeight:400, margin:'0 0 6px' }}>Reversal & Continuation Patterns</h3>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(230px, 1fr))', gap:'14px', marginBottom:'28px' }}>
        <PCard title="Double Top" badge="BEARISH REVERSAL" badgeColor={V.rose} borderColor={V.rose} drawFn={drawDoubleTop}
          body="Two peaks at the same resistance level. The market tried twice to break higher — and failed twice. When price breaks below the neckline, the reversal is confirmed. Strong covered call / bear call spread signal at the second top." />
        <PCard title="Double Bottom" badge="BULLISH REVERSAL" badgeColor={V.sage} borderColor={V.sage} drawFn={drawDoubleBot}
          body="Two lows at the same support level. Buyers defended the same price twice. When price breaks above the neckline, the reversal is confirmed. One of the most reliable CSP setup signals — the rubber band has tested support twice and held." />
        <PCard title="Head & Shoulders" badge="BEARISH REVERSAL" badgeColor={V.rose} borderColor={V.rose} drawFn={drawHnS}
          body="Left shoulder, higher head, right shoulder at same level as left. Bearish reversal pattern. Neckline break confirms it. When you see this forming at a high — covered calls or cash on the sidelines." />
        <PCard title="Bull Flag" badge="BULLISH CONTINUATION" badgeColor={V.sage} borderColor={V.sage} drawFn={drawBullFlag}
          body="Strong upward move (the pole) followed by a tight consolidation (the flag). Price coils briefly before continuing the original move upward. Volume dries up during the flag and spikes on breakout. CSP entry on the breakout." />
      </div>
      <h3 style={{ ...ss, fontSize:'20px', color:V.gold, fontWeight:400, margin:'0 0 14px' }}>Wedge & Triangle Patterns</h3>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(230px, 1fr))', gap:'14px', marginBottom:'24px' }}>
        <PCard title="Rising Wedge" badge="BEARISH DESPITE RISE" badgeColor={V.rose} borderColor={V.rose} drawFn={drawRisingWedge}
          body="Price making higher highs and higher lows — but the highs and lows are converging. Despite the upward slope this pattern resolves bearish. When price breaks below the lower trend line the snapback is downward. At resistance: covered call setup." />
        <PCard title="Falling Wedge" badge="BULLISH DESPITE FALL" badgeColor={V.sage} borderColor={V.sage} drawFn={drawFallingWedge}
          body="Price making lower lows and lower highs — but converging downward. Despite the downward slope this pattern resolves bullish. When price breaks above the upper trend line the snapback is upward. Near support: CSP setup." />
        <PCard title="Ascending Triangle" badge="BULLISH BIAS" badgeColor={V.sage} borderColor={V.sage} drawFn={drawAscTriangle}
          body="Flat resistance at the top. Rising support — buyers making higher lows. Sellers defending the same level but buyers getting stronger with each attempt. Usually resolves with a bullish breakout. Volume spike on breakout confirms." />
        <PCard title="Descending Triangle" badge="BEARISH BIAS" badgeColor={V.rose} borderColor={V.rose} drawFn={drawDescTriangle}
          body="Flat support at the bottom. Declining resistance — sellers making lower highs. Buyers defending the same level but sellers getting stronger. Usually resolves with a bearish breakdown below support. Volume spike confirms." />
      </div>
      <div style={{ background:V.bg2, borderLeft:`3px solid ${V.gold}`, borderRadius:'8px', padding:'16px 20px', marginBottom:'20px' }}>
        <p style={{ ...jost, fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:0 }}>
          Every chart pattern is showing you the rubber band in a specific state. Wedges and triangles show it coiling — getting tighter before a release. The direction of the release tells you where the rubber band is going to snap. Volume on the breakout tells you if it means it.
        </p>
      </div>
    </div>
  );
}

function Ch02DrawingTOSVisual() {
  const jost = {fontFamily:"'Jost',sans-serif"};
  const tools = [
    {
      title: 'Horizontal Line — Support / Resistance',
      steps: ['Open any stock chart in TOS','Tap the pencil / drawing icon (top right of chart)','Select "Horizontal Line"','Tap the price level where you want to draw','The line extends across the entire chart','Tap the line to change color: use GREEN for support, RED for resistance','Tap the checkmark to save'],
      use: 'Marking previous highs, previous lows, and key price levels that price has respected before.',
    },
    {
      title: 'Trend Line — Direction',
      steps: ['Open your drawing tools','Select "Trend Line" or "Line"','Tap your first point (a swing low for an uptrend line)','Tap your second point (the next swing low)','The line extends showing the trend angle','A rising trend line = uptrend support / A falling trend line = downtrend resistance'],
      use: 'Connecting swing lows in an uptrend (support trend line) or swing highs in a downtrend (resistance trend line).',
    },
    {
      title: 'Channel — Price Range',
      steps: ['Draw a trend line along the swing lows','Add a parallel line along the swing highs','Price oscillates between the two lines','Lower channel line = support / Upper channel line = resistance'],
      use: 'When price touches the lower channel line in an uptrend — CSP entry zone.',
    },
  ];
  const colorGuide = [
    { emoji: '🟢', label: 'Green lines', note: 'Support levels and demand zones' },
    { emoji: '🔴', label: 'Red lines',   note: 'Resistance levels and supply zones' },
    { emoji: '🔵', label: 'Blue lines',  note: 'Trend lines (direction lines)' },
    { emoji: '⚪', label: 'White lines', note: 'Channels and reference levels' },
  ];
  return (
    <div>
      <div style={{ background:V.bg2, borderLeft:`3px solid ${V.gold}`, borderRadius:'8px', padding:'16px 20px', margin:'20px 0' }}>
        <p style={{ ...jost, fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 8px' }}>
          When you draw a line yourself you own that level. You will never forget where your support is if you drew it with your own hand.
        </p>
        <p style={{ ...jost, fontSize:'13px', color:V.muted, lineHeight:1.8, margin:0 }}>
          This section teaches you HOW to use the drawing tools in ThinkorSwim. In Chapter 4 you will learn exactly WHERE to draw your lines and why certain levels matter more than others.
        </p>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:'12px', marginBottom:'20px' }}>
        {tools.map(t => (
          <div key={t.title} style={{ background:V.bg3, border:`0.5px solid ${V.border}`, borderRadius:'10px', padding:'16px' }}>
            <div style={{ ...jost, fontSize:'13px', fontWeight:600, color:V.gold, marginBottom:'10px' }}>{t.title}</div>
            <ol style={{ ...jost, fontSize:'12px', color:V.muted, lineHeight:2.0, margin:'0 0 10px', paddingLeft:'18px' }}>
              {t.steps.map((s,i) => <li key={i}>{s}</li>)}
            </ol>
            <div style={{ ...jost, fontSize:'11px', color:V.text, background:'rgba(201,168,76,0.05)', border:`0.5px solid ${V.gold}22`, borderRadius:'6px', padding:'8px 12px' }}>
              <span style={{color:V.gold, fontWeight:600}}>Use for: </span>{t.use}
            </div>
          </div>
        ))}
      </div>
      <div style={{ background:V.bg2, border:`0.5px solid ${V.gold}44`, borderRadius:'10px', padding:'16px 20px', marginBottom:'16px' }}>
        <div style={{ ...jost, fontSize:'11px', color:V.gold, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'12px' }}>Recommended Color Coding in TOS</div>
        {colorGuide.map(c => (
          <div key={c.label} style={{ display:'flex', gap:'10px', alignItems:'baseline', marginBottom:'6px' }}>
            <span style={{fontSize:'12px'}}>{c.emoji}</span>
            <span style={{ ...jost, fontSize:'12px', color:V.text, fontWeight:600, minWidth:'90px' }}>{c.label}</span>
            <span style={{ ...jost, fontSize:'12px', color:V.muted }}>{c.note}</span>
          </div>
        ))}
        <p style={{ ...jost, fontSize:'11px', color:V.dim, fontStyle:'italic', lineHeight:1.7, margin:'10px 0 0' }}>
          Consistent color coding means you can read your chart instantly. Green = buyers defended here. Red = sellers defended here.
        </p>
      </div>
      <div style={{ background:V.bg2, borderLeft:`3px solid ${V.gold}`, borderRadius:'8px', padding:'16px 20px' }}>
        <p style={{ ...jost, fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 8px' }}>
          You now know HOW to draw lines in ThinkorSwim.
        </p>
        <p style={{ ...jost, fontSize:'13px', color:V.muted, lineHeight:1.8, margin:0 }}>
          In Chapter 4 — Reading Structure you will learn exactly WHERE to draw them. Which price levels are significant. How many touches confirm a level. How volume and open interest validate your lines.
        </p>
      </div>
    </div>
  );
}

function ConsolidationPhasesCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    const W = cv.width, H = cv.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(18,14,10,0.95)'; ctx.fillRect(0, 0, W, H);
    const pad = { l: 36, r: 10, t: 26, b: 44 };
    const gW = W - pad.l - pad.r, gH = H - pad.t - pad.b;
    const N = 60;
    const prices = [];
    for (let i = 0; i < N; i++) {
      if (i < 20) prices.push(0.14 + (i / 19) * 0.43 + Math.sin(i * 1.8) * 0.025 + Math.cos(i * 3.1) * 0.015);
      else if (i < 41) prices.push(0.57 + Math.sin(i * 3.5) * 0.038 + Math.cos(i * 5.7) * 0.014);
      else { const p = (i - 40) / 19; prices.push(0.55 + p * 0.33 + Math.sin(i * 1.3) * 0.014); }
    }
    const sma = prices.map((_, i) => { const sl = prices.slice(Math.max(0, i - 9), i + 1); return sl.reduce((a, b) => a + b) / sl.length; });
    const px = i => pad.l + (i / (N - 1)) * gW;
    const py = p => pad.t + gH * (1 - Math.min(0.99, Math.max(0.01, p)));
    const x2 = px(20), x3 = px(41);
    // Phase shading
    ctx.fillStyle = 'rgba(201,168,76,0.06)'; ctx.fillRect(x2, pad.t, x3 - x2, gH);
    // Phase dividers
    ctx.strokeStyle = 'rgba(201,168,76,0.2)'; ctx.lineWidth = 0.8; ctx.setLineDash([4, 4]);
    [x2, x3].forEach(x => { ctx.beginPath(); ctx.moveTo(x, pad.t); ctx.lineTo(x, pad.t + gH + 20); ctx.stroke(); });
    ctx.setLineDash([]);
    // Volatility envelope (visual context)
    const bbW = i => { if (i < 20) return 0.075; if (i < 41) return 0.055 - 0.035 * (1 - Math.abs(i - 30) / 11); return 0.025 + ((i - 41) / 18) * 0.065; };
    ctx.strokeStyle = 'rgba(160,148,120,0.22)'; ctx.lineWidth = 0.8; ctx.setLineDash([3, 4]);
    ['u', 'l'].forEach(side => {
      ctx.beginPath();
      sma.forEach((m, i) => { const y = py(side === 'u' ? m + bbW(i) : m - bbW(i)); i === 0 ? ctx.moveTo(px(i), y) : ctx.lineTo(px(i), y); });
      ctx.stroke();
    });
    ctx.setLineDash([]);
    // MA line
    ctx.beginPath(); ctx.strokeStyle = 'rgba(122,158,196,0.65)'; ctx.lineWidth = 1.2;
    sma.forEach((p, i) => i === 0 ? ctx.moveTo(px(i), py(p)) : ctx.lineTo(px(i), py(p)));
    ctx.stroke();
    // Price line
    ctx.beginPath(); ctx.strokeStyle = '#c9a84c'; ctx.lineWidth = 1.8;
    prices.forEach((p, i) => i === 0 ? ctx.moveTo(px(i), py(p)) : ctx.lineTo(px(i), py(p)));
    ctx.stroke();
    // Volume bars
    const volBase = H - 6, vmx = 14;
    for (let i = 0; i < N; i++) {
      const v = i < 20 ? 0.3 + Math.abs(Math.sin(i * 1.3)) * 0.4 : i < 41 ? 0.1 + Math.abs(Math.sin(i * 2.1)) * 0.18 : (i > 45 ? 0.55 : 0.3) + Math.abs(Math.sin(i * 0.7)) * 0.4;
      ctx.fillStyle = i >= 41 ? 'rgba(122,171,138,0.5)' : i >= 20 ? 'rgba(201,168,76,0.3)' : 'rgba(122,158,196,0.35)';
      ctx.fillRect(px(i) - 2.5, volBase - v * vmx, 4, v * vmx);
    }
    // Phase labels
    ctx.font = 'bold 8px Jost, sans-serif'; ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(122,158,196,0.8)'; ctx.fillText('TREND', pad.l + (x2 - pad.l) / 2, 17);
    ctx.fillStyle = 'rgba(201,168,76,0.8)'; ctx.fillText('CONSOLIDATION', (x2 + x3) / 2, 17);
    ctx.fillStyle = 'rgba(122,171,138,0.8)'; ctx.fillText('BREAKOUT', x3 + (W - pad.r - x3) / 2, 17);
    // Sub-labels
    ctx.font = '7px Jost, sans-serif';
    ctx.fillStyle = 'rgba(122,158,196,0.45)'; ctx.fillText('stretching', pad.l + (x2 - pad.l) / 2, H - 16);
    ctx.fillStyle = 'rgba(201,168,76,0.45)'; ctx.fillText('coiling', (x2 + x3) / 2, H - 16);
    ctx.fillStyle = 'rgba(122,171,138,0.45)'; ctx.fillText('releasing', x3 + (W - pad.r - x3) / 2, H - 16);
  }, []); // eslint-disable-line
  return (
    <div style={{ margin: '16px 0' }}>
      <canvas ref={ref} width={560} height={200} style={{ width: '100%', maxWidth: '560px', display: 'block', borderRadius: '8px', border: '0.5px solid rgba(120,110,90,0.25)' }} />
      <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", marginTop: '6px', textAlign: 'center' }}>Three phases: trend → consolidation → breakout. The dotted lines show the volatility envelope narrowing in phase 2 and widening on breakout. Volume (colored bars) drops in phase 2 and spikes on the breakout.</p>
    </div>
  );
}

function ConsolidationComparisonCanvas() {
  const refC = useRef(null), refCh = useRef(null);
  useEffect(() => {
    const draw = (cv, mode) => {
      if (!cv) return;
      const ctx = cv.getContext('2d');
      const W = cv.width, H = cv.height;
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = 'rgba(18,14,10,0.95)'; ctx.fillRect(0, 0, W, H);
      const pad = { l: 8, r: 8, t: 22, b: 18 };
      const gW = W - pad.l - pad.r, gH = H - pad.t - pad.b;
      const N = 40;
      const px = i => pad.l + (i / (N - 1)) * gW;
      const py = p => pad.t + gH * (1 - Math.min(0.99, Math.max(0.01, p)));
      if (mode === 'consol') {
        const prices = Array.from({ length: N }, (_, i) => 0.50 + Math.sin(i * 2.7) * 0.08 + Math.cos(i * 4.1) * 0.04);
        const sma = prices.map((_, i) => { const sl = prices.slice(Math.max(0, i - 7), i + 1); return sl.reduce((a, b) => a + b) / sl.length; });
        const rt = 0.63, rb = 0.37;
        ctx.strokeStyle = 'rgba(122,171,138,0.5)'; ctx.lineWidth = 0.8; ctx.setLineDash([4, 4]);
        [rt, rb].forEach(y => { ctx.beginPath(); ctx.moveTo(pad.l, py(y)); ctx.lineTo(W - pad.r, py(y)); ctx.stroke(); });
        ctx.setLineDash([]);
        ctx.beginPath(); ctx.strokeStyle = 'rgba(122,158,196,0.7)'; ctx.lineWidth = 1;
        sma.forEach((p, i) => i === 0 ? ctx.moveTo(px(i), py(p)) : ctx.lineTo(px(i), py(p)));
        ctx.stroke();
        ctx.beginPath(); ctx.strokeStyle = '#c9a84c'; ctx.lineWidth = 1.5;
        prices.forEach((p, i) => i === 0 ? ctx.moveTo(px(i), py(p)) : ctx.lineTo(px(i), py(p)));
        ctx.stroke();
        ctx.font = '7.5px Jost, sans-serif'; ctx.textAlign = 'right'; ctx.fillStyle = 'rgba(122,171,138,0.7)';
        ctx.fillText('R', W - pad.r - 1, py(rt) + 3);
        ctx.fillText('S', W - pad.r - 1, py(rb) + 3);
        ctx.fillStyle = 'rgba(122,171,138,0.9)'; ctx.font = 'bold 9px Jost, sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('CONSOLIDATION', W / 2, 13);
      } else {
        const prices = Array.from({ length: N }, (_, i) => 0.50 + Math.sin(i * 1.2) * 0.18 + Math.cos(i * 3.7) * 0.12 + Math.sin(i * 7.1) * 0.07);
        const sma = prices.map((_, i) => { const sl = prices.slice(Math.max(0, i - 6), i + 1); return sl.reduce((a, b) => a + b) / sl.length; });
        const sma2 = prices.map((_, i) => { const sl = prices.slice(Math.max(0, i - 14), i + 1); return sl.reduce((a, b) => a + b) / sl.length; });
        ctx.beginPath(); ctx.strokeStyle = 'rgba(122,158,196,0.65)'; ctx.lineWidth = 1;
        sma.forEach((p, i) => i === 0 ? ctx.moveTo(px(i), py(p)) : ctx.lineTo(px(i), py(p)));
        ctx.stroke();
        ctx.beginPath(); ctx.strokeStyle = 'rgba(196,122,122,0.65)'; ctx.lineWidth = 1;
        sma2.forEach((p, i) => i === 0 ? ctx.moveTo(px(i), py(p)) : ctx.lineTo(px(i), py(p)));
        ctx.stroke();
        ctx.beginPath(); ctx.strokeStyle = '#c9a84c'; ctx.lineWidth = 1.5;
        prices.forEach((p, i) => i === 0 ? ctx.moveTo(px(i), py(p)) : ctx.lineTo(px(i), py(p)));
        ctx.stroke();
        ctx.fillStyle = 'rgba(196,122,122,0.6)'; ctx.font = '7.5px Jost, sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('MAs tangled — no clear direction', W / 2, H - 5);
        ctx.fillStyle = 'rgba(196,122,122,0.9)'; ctx.font = 'bold 9px Jost, sans-serif';
        ctx.fillText('CHOPPINESS', W / 2, 13);
      }
    };
    draw(refC.current, 'consol');
    draw(refCh.current, 'choppy');
  }, []); // eslint-disable-line
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', margin: '16px 0' }}>
      <canvas ref={refC} width={240} height={110} style={{ width: '100%', display: 'block', borderRadius: '6px', border: '0.5px solid rgba(122,171,138,0.3)' }} />
      <canvas ref={refCh} width={240} height={110} style={{ width: '100%', display: 'block', borderRadius: '6px', border: '0.5px solid rgba(196,122,122,0.3)' }} />
    </div>
  );
}

function Ch04SupportVisual({ onNavigate, onPractice }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d');
    const W = cv.width, H = cv.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#0a0a0c'; ctx.fillRect(0, 0, W, H);
    const pad = { l: 24, r: 22, t: 42, b: 24 };
    const gW = W - pad.l - pad.r, gH = H - pad.t - pad.b;
    const SUP_LO = 0.22, SUP_HI = 0.38;
    const prices = [
      0.93,0.89,0.85,0.80,0.74,0.66,0.58,0.50,0.44,0.40,0.37,0.34,0.32,
      0.33,0.38,0.45,0.53,0.60,0.66,0.70,0.68,0.64,0.59,
      0.54,0.48,0.43,0.39,0.35,0.33,0.32,0.33,0.37,0.43,
      0.50,0.56,0.61,0.65,0.63,0.59,0.54,
      0.49,0.43,0.38,0.34,0.31,0.32,0.36,0.42,
      0.51,0.60,0.69,0.77,0.83,0.88,0.91,0.93,0.94
    ];
    const N = prices.length;
    const px = i => pad.l + (i / (N - 1)) * gW;
    const py = v => pad.t + gH * (1 - v);
    // Support zone shading
    ctx.fillStyle = 'rgba(122,171,138,0.12)';
    ctx.fillRect(pad.l, py(SUP_HI), gW, py(SUP_LO) - py(SUP_HI));
    // Subtle grid
    ctx.strokeStyle = 'rgba(255,255,255,0.03)'; ctx.lineWidth = 0.5;
    [0.25, 0.5, 0.75].forEach(f => {
      ctx.beginPath(); ctx.moveTo(pad.l, pad.t + gH * f); ctx.lineTo(pad.l + gW, pad.t + gH * f); ctx.stroke();
    });
    // Dashed line at top of support zone
    ctx.strokeStyle = 'rgba(122,171,138,0.62)'; ctx.lineWidth = 1;
    ctx.setLineDash([5, 4]);
    ctx.beginPath(); ctx.moveTo(pad.l, py(SUP_HI)); ctx.lineTo(pad.l + gW, py(SUP_HI)); ctx.stroke();
    ctx.setLineDash([]);
    // Price line
    ctx.strokeStyle = '#c9a84c'; ctx.lineWidth = 1.8;
    ctx.beginPath();
    prices.forEach((v, i) => { i === 0 ? ctx.moveTo(px(i), py(v)) : ctx.lineTo(px(i), py(v)); });
    ctx.stroke();
    // Three touch points and bounce arrows
    const touches = [12, 30, 45];
    const aH = 15;
    touches.forEach(ti => {
      const ax = px(ti), ay = py(prices[ti]);
      ctx.strokeStyle = 'rgba(122,171,138,0.85)'; ctx.fillStyle = 'rgba(122,171,138,0.85)'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(ax, ay + aH + 2); ctx.lineTo(ax, ay + 3); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(ax - 5, ay + 9); ctx.lineTo(ax, ay + 3); ctx.lineTo(ax + 5, ay + 9); ctx.fill();
      ctx.beginPath(); ctx.arc(ax, ay, 3, 0, Math.PI * 2); ctx.fillStyle = '#7aab8a'; ctx.fill();
    });
    // Touch labels above zone
    const lblY = py(SUP_HI) - 10;
    ctx.font = '9px Jost,sans-serif'; ctx.textAlign = 'center';
    const tlabels = ['Price bounces here', 'Buyers step in again', 'Strong demand →'];
    touches.forEach((ti, li) => {
      ctx.fillStyle = 'rgba(122,171,138,0.88)';
      ctx.fillText(tlabels[li], px(ti), lblY);
    });
    // "Support Zone" label inside band
    const szMid = (py(SUP_HI) + py(SUP_LO)) / 2;
    ctx.font = '10px Jost,sans-serif'; ctx.fillStyle = 'rgba(122,171,138,0.6)'; ctx.textAlign = 'center';
    ctx.fillText('Support Zone  ($162–$165)', pad.l + gW / 2, szMid + 4);
  }, []); // eslint-disable-line
  return (
    <div style={{ margin: '20px 0' }}>
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 8px 8px 0', padding: '14px 18px', marginBottom: '16px' }}>
        <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, fontStyle:'italic', lineHeight:1.75, margin:0 }}>
          In Chapter 2 you learned HOW to draw horizontal lines in ThinkorSwim. This section teaches you WHERE to draw them — specifically at support and demand zones where buyers have historically stepped in.
        </p>
      </div>
      <canvas ref={ref} width={600} height={240} style={{ width: '100%', maxWidth: '600px', display: 'block', borderRadius: '8px', border: '0.5px solid rgba(120,110,90,0.2)' }} />
      <p style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", marginTop: '8px', lineHeight: 1.7, fontStyle: 'italic' }}>
        Price has tested this level three times and buyers defended it each time. This is a strong support zone — a CSP strike at or below this level has the market's buying history behind it.
      </p>
      {onNavigate && (
        <button style={{ ...btnGold, marginTop: '14px', fontSize: '13px', padding: '10px 20px' }} onClick={() => onNavigate('support_resistance_practice')}>
          Practice Support &amp; Demand →
        </button>
      )}
    </div>
  );
}

function Ch04ResistanceVisual({ onNavigate, onPractice }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d');
    const W = cv.width, H = cv.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#0a0a0c'; ctx.fillRect(0, 0, W, H);
    const pad = { l: 24, r: 22, t: 30, b: 30 };
    const gW = W - pad.l - pad.r, gH = H - pad.t - pad.b;
    const RES_LO = 0.62, RES_HI = 0.76;
    const prices = [
      0.08,0.13,0.19,0.26,0.33,0.40,0.47,0.52,0.57,0.61,0.64,
      0.67,0.69,0.71,0.68,0.65,0.60,
      0.55,0.49,0.44,0.40,0.38,0.41,0.46,0.51,0.56,0.60,0.63,
      0.66,0.69,0.71,0.70,0.67,0.63,0.59,
      0.54,0.49,0.44,0.39,0.35,0.31,0.27,0.24,0.21
    ];
    const N = prices.length;
    const px = i => pad.l + (i / (N - 1)) * gW;
    const py = v => pad.t + gH * (1 - v);
    // Resistance zone shading
    ctx.fillStyle = 'rgba(196,122,122,0.12)';
    ctx.fillRect(pad.l, py(RES_HI), gW, py(RES_LO) - py(RES_HI));
    // Subtle grid
    ctx.strokeStyle = 'rgba(255,255,255,0.03)'; ctx.lineWidth = 0.5;
    [0.25, 0.5, 0.75].forEach(f => {
      ctx.beginPath(); ctx.moveTo(pad.l, pad.t + gH * f); ctx.lineTo(pad.l + gW, pad.t + gH * f); ctx.stroke();
    });
    // Dashed line at bottom of resistance zone
    ctx.strokeStyle = 'rgba(196,122,122,0.62)'; ctx.lineWidth = 1;
    ctx.setLineDash([5, 4]);
    ctx.beginPath(); ctx.moveTo(pad.l, py(RES_LO)); ctx.lineTo(pad.l + gW, py(RES_LO)); ctx.stroke();
    ctx.setLineDash([]);
    // Price line
    ctx.strokeStyle = '#c9a84c'; ctx.lineWidth = 1.8;
    ctx.beginPath();
    prices.forEach((v, i) => { i === 0 ? ctx.moveTo(px(i), py(v)) : ctx.lineTo(px(i), py(v)); });
    ctx.stroke();
    // Two rejection peaks and down arrows
    const peaks = [13, 30];
    const aH = 15;
    peaks.forEach(ti => {
      const ax = px(ti), ay = py(prices[ti]);
      ctx.strokeStyle = 'rgba(196,122,122,0.85)'; ctx.fillStyle = 'rgba(196,122,122,0.85)'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(ax, ay + 3); ctx.lineTo(ax, ay + aH + 2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(ax - 5, ay + aH - 4); ctx.lineTo(ax, ay + aH + 2); ctx.lineTo(ax + 5, ay + aH - 4); ctx.fill();
      ctx.beginPath(); ctx.arc(ax, ay, 3, 0, Math.PI * 2); ctx.fillStyle = '#c47a7a'; ctx.fill();
    });
    // Rejection labels above each peak
    ctx.font = '9px Jost,sans-serif'; ctx.textAlign = 'center';
    const rlabels = ['Sellers step in', 'Rejected again —\ndouble top forming'];
    [[13, 'Sellers step in'], [30, 'Rejected again']].forEach(([ti, lbl]) => {
      ctx.fillStyle = 'rgba(196,122,122,0.88)';
      ctx.fillText(lbl, px(ti), py(RES_HI) - 8);
    });
    // "Resistance Zone" label inside band
    const rzMid = (py(RES_HI) + py(RES_LO)) / 2;
    ctx.font = '10px Jost,sans-serif'; ctx.fillStyle = 'rgba(196,122,122,0.6)'; ctx.textAlign = 'center';
    ctx.fillText('Resistance Zone  ($183–$186)', pad.l + gW / 2, rzMid + 4);
  }, []); // eslint-disable-line
  return (
    <div style={{ margin: '20px 0' }}>
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 8px 8px 0', padding: '14px 18px', marginBottom: '16px' }}>
        <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, fontStyle:'italic', lineHeight:1.75, margin:0 }}>
          In Chapter 2 you learned HOW to draw horizontal lines in ThinkorSwim. This section teaches you WHERE to draw them — specifically at resistance and supply zones where sellers have historically stepped in to cap price.
        </p>
      </div>
      <canvas ref={ref} width={600} height={240} style={{ width: '100%', maxWidth: '600px', display: 'block', borderRadius: '8px', border: '0.5px solid rgba(120,110,90,0.2)' }} />
      <p style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", marginTop: '8px', lineHeight: 1.7, fontStyle: 'italic' }}>
        Price has tested this level twice and sellers defended it both times. This is a strong resistance zone — a covered call strike at or just above this level has the market's selling history behind it.
      </p>
      {onNavigate && (
        <button style={{ ...btnGold, marginTop: '14px', fontSize: '13px', padding: '10px 20px' }} onClick={() => onNavigate('support_resistance_practice')}>
          Practice Resistance &amp; Supply →
        </button>
      )}
    </div>
  );
}

function Ch04ConsolidationVisual() {
  const ss = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
  const b = { fontSize: '14px', color: 'rgba(240,237,230,0.82)', fontFamily: "'Jost',sans-serif", lineHeight: 1.85, margin: 0 };
  const hr = { height: '0.5px', background: 'rgba(201,168,76,0.12)', margin: '28px 0' };
  const checkItems = [
    'Is price in a trend or consolidating?',
    'If consolidating — are moving averages flattening and converging? (breakout may be coming)',
    'If choppy — consider standing aside entirely',
    'Has price recently broken out of consolidation on high volume? (new trend may be starting)',
  ];
  return (
    <div style={{ position: 'relative', overflow: 'visible', height: 'auto', display: 'block' }}>

      {/* Opening quote */}
      <div style={{ borderLeft: `3px solid ${V.gold}`, paddingLeft: '20px', marginBottom: '28px' }}>
        <p style={{ ...ss, fontSize: '18px', color: V.gold, fontWeight: 400, lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
          "The market moves in three phases: trend, consolidation, trend. Learn to recognize the pause."
        </p>
        <p style={{ fontSize: '12px', color: V.dim, fontFamily: "'Jost',sans-serif", margin: '8px 0 0', letterSpacing: '0.05em' }}>— Technical Analysis wisdom</p>
      </div>

      {/* Rubber Band Connection */}
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 10px 10px 0', padding: '20px 24px', marginBottom: '28px' }}>
        <div style={{ fontSize: '11px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', marginBottom: '8px' }}>THE RUBBER BAND CONNECTION</div>
        <p style={{ ...b }}>Consolidation is the rubber band at rest. After a strong stretch in one direction, price pauses — coiling tightly before the next move. Moving averages flatten and converge. Volume dries up. The rubber band is being pulled tight. A breakout is coming.</p>
      </div>

      {/* Section 1 */}
      <h3 style={{ ...ss, fontSize: '20px', color: V.gold, fontWeight: 400, margin: '0 0 12px' }}>The Three Phases of Price</h3>
      <p style={{ ...b, marginBottom: '14px' }}>After every significant price move the market pauses to digest what just happened. Buyers and sellers reach temporary agreement on price. The result is consolidation — price moving sideways in a tight range.</p>
      <p style={{ ...b, marginBottom: '16px' }}>Consolidation is not a signal to ignore. It is a signal to pay attention. The market is coiling. Something is coming.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
        {[
          { num: '1', color: '#7a9ec4', label: 'TREND', text: 'Price moves strongly in one direction. Moving averages fan out. Volume is high. The rubber band is stretching.' },
          { num: '2', color: V.gold, label: 'CONSOLIDATION', text: 'Price moves sideways in a tight range. Moving averages flatten and converge. Volume drops. The rubber band is coiling.' },
          { num: '3', color: V.sage, label: 'TREND RESUMES OR REVERSES', text: 'Price breaks out of consolidation. Volume spikes confirming the move. Moving averages begin to fan again. The rubber band releases.' },
        ].map(({ num, color, label, text }) => (
          <div key={num} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', background: V.bg2, border: `0.5px solid ${V.border}`, borderLeft: `3px solid ${color}`, borderRadius: '0 8px 8px 0', padding: '12px 16px' }}>
            <div style={{ ...ss, fontSize: '22px', color, fontWeight: 400, lineHeight: 1, flexShrink: 0, minWidth: '16px' }}>{num}</div>
            <div>
              <div style={{ fontSize: '11px', color, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.08em', marginBottom: '4px' }}>{label}</div>
              <p style={{ ...b, fontSize: '13px', lineHeight: 1.7 }}>{text}</p>
            </div>
          </div>
        ))}
      </div>
      <ConsolidationPhasesCanvas />

      <div style={hr} />

      {/* Section 2 */}
      <h3 style={{ ...ss, fontSize: '20px', color: V.gold, fontWeight: 400, margin: '0 0 12px' }}>What Consolidation Means for Options Sellers</h3>
      <p style={{ ...b, marginBottom: '16px' }}>Consolidation directly affects your options trading in two important ways:</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '10px', marginBottom: '16px' }}>
        {[
          { color: V.rose, label: '1. PREMIUM COMPRESSION', text: 'During consolidation implied volatility tends to drop — options become cheaper. This is not ideal for sellers who want rich premium. Wait for the breakout to restore volatility before selling.' },
          { color: V.sage, label: '2. BREAKOUT SETUP', text: 'When price breaks out of consolidation on high volume, that is often the beginning of a new trend. A confirmed bullish breakout is a strong CSP environment. A confirmed breakdown is a warning to avoid selling puts on that stock.' },
        ].map(({ color, label, text }) => (
          <div key={label} style={{ background: V.bg3, borderLeft: `3px solid ${color}`, borderRadius: '0 8px 8px 0', padding: '14px 16px' }}>
            <div style={{ fontSize: '11px', color, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.08em', marginBottom: '8px' }}>{label}</div>
            <p style={{ ...b, fontSize: '13px', lineHeight: 1.7 }}>{text}</p>
          </div>
        ))}
      </div>
      <div style={{ background: 'rgba(201,168,76,0.06)', border: `1px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '16px 20px' }}>
        <div style={{ fontSize: '12px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.08em', marginBottom: '10px' }}>VOLATILITY COMPRESSION — YOUR EARLY WARNING</div>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          {[
            { sym: '⟨⟩', color: V.gold, top: 'MAs Converging', bot: '= Consolidation — coiling' },
            { sym: '⟪⟫', color: V.sage, top: 'MAs Diverging', bot: '= Breakout — premium returns' },
          ].map(({ sym, color, top, bot }) => (
            <div key={top} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px', color, lineHeight: 1 }}>{sym}</span>
              <div>
                <div style={{ fontSize: '12px', color, fontFamily: "'Jost',sans-serif", fontWeight: 600, marginBottom: '2px' }}>{top}</div>
                <div style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>{bot}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={hr} />

      {/* Section 3 */}
      <h3 style={{ ...ss, fontSize: '20px', color: V.gold, fontWeight: 400, margin: '0 0 12px' }}>Consolidation vs. Choppiness</h3>
      <p style={{ ...b, marginBottom: '16px' }}>Not all sideways movement is healthy consolidation. There is an important difference:</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
        {[
          { color: V.sage, label: 'HEALTHY CONSOLIDATION', points: ['Tight defined range with clear upper and lower boundaries', 'Volume orderly and declining', 'Overall trend still intact', 'Moving averages flat but not tangled', 'Rubber band coiling — expect continuation'] },
          { color: V.rose, label: 'CHOPPINESS', points: ['Erratic movement with no clear range boundaries', 'Moving averages tangled and crossing', 'Volume inconsistent', 'No clear direction or structure', 'Rubber band fraying — no clean setup'] },
        ].map(({ color, label, points }) => (
          <div key={label} style={{ background: V.bg3, borderTop: `2px solid ${color}`, borderRadius: '0 0 8px 8px', padding: '14px 16px' }}>
            <div style={{ fontSize: '11px', color, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.08em', marginBottom: '10px' }}>{label}</div>
            {points.map((pt, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: i < points.length - 1 ? '5px' : 0 }}>
                <span style={{ color, fontSize: '10px', flexShrink: 0, marginTop: '4px' }}>—</span>
                <p style={{ ...b, fontSize: '12px', lineHeight: 1.6 }}>{pt}</p>
              </div>
            ))}
          </div>
        ))}
      </div>
      <ConsolidationComparisonCanvas />
      <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '14px 18px', marginTop: '4px' }}>
        <div style={{ fontSize: '12px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.08em', marginBottom: '10px' }}>FOR PREMIUM SELLERS</div>
        {[
          { color: V.sage, bold: 'Healthy consolidation', rest: ' — wait for the breakout, then trade with the new trend.' },
          { color: V.rose, bold: 'Choppiness', rest: ' — stand aside entirely. Choppy markets eat premium sellers alive.' },
        ].map(({ color, bold, rest }) => (
          <div key={bold} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '6px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0, marginTop: '5px' }} />
            <p style={{ ...b, fontSize: '13px', lineHeight: 1.7 }}><strong style={{ color }}>{bold}</strong>{rest}</p>
          </div>
        ))}
      </div>

      <div style={hr} />

      {/* Section 4 — checklist */}
      <div style={{ background: V.bg3, border: `1px solid rgba(201,168,76,0.25)`, borderRadius: '12px', padding: '20px 24px', marginBottom: '24px' }}>
        <div style={{ fontSize: '12px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', marginBottom: '14px' }}>BEFORE SELLING PREMIUM — CHECK</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {checkItems.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div style={{ width: '18px', height: '18px', border: `1px solid rgba(201,168,76,0.35)`, borderRadius: '4px', flexShrink: 0, marginTop: '1px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: 'rgba(201,168,76,0.2)' }} />
              </div>
              <p style={{ ...b, fontSize: '13px', lineHeight: 1.65 }}>{item}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Closing note */}
      <div style={{ borderLeft: `3px solid ${V.gold}`, paddingLeft: '20px' }}>
        <p style={{ ...b, lineHeight: 1.85 }}>The best premium selling environments are clear trends with occasional healthy pullbacks to moving average support. Consolidation tells you to be patient. Choppiness tells you to step away. Learning to recognize both will save you from many bad trades.</p>
      </div>

    </div>
  );
}

function Ch04OIVisual({ onNavigate, onPractice }) {
  const ss = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
  const b = { fontSize: '14px', color: 'rgba(240,237,230,0.82)', fontFamily: "'Jost',sans-serif", lineHeight: 1.85, margin: 0 };
  const hr = { height: '0.5px', background: 'rgba(201,168,76,0.12)', margin: '28px 0' };

  const checkItems = [
    'Is there strong OI at or near my strike? (confirms the level is watched)',
    'Is OI at least 500 on my target strike? (liquidity minimum)',
    'Does OI support my directional thesis? (high put OI = support, high call OI = resistance)',
    'Where is max pain this expiration cycle? (know the magnet)',
    'Is the bid/ask spread tight enough to enter and exit cleanly?',
  ];

  return (
    <div style={{ position: 'relative', overflow: 'visible', height: 'auto', display: 'block' }}>

      {/* Opening quote */}
      <div style={{ borderLeft: `3px solid ${V.gold}`, paddingLeft: '20px', marginBottom: '28px' }}>
        <p style={{ ...ss, fontSize: '18px', color: V.gold, fontWeight: 400, lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
          "Open interest is not a prediction. It is a map of where money is already committed."
        </p>
        <p style={{ fontSize: '12px', color: V.dim, fontFamily: "'Jost',sans-serif", margin: '8px 0 0', letterSpacing: '0.05em' }}>
          The options chain does not just show you what could happen — it shows you what others are already betting on.
        </p>
      </div>

      {/* Rubber Band Connection */}
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 10px 10px 0', padding: '20px 24px', marginBottom: '28px' }}>
        <div style={{ fontSize: '11px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', marginBottom: '8px' }}>THE RUBBER BAND CONNECTION</div>
        <p style={{ ...b }}>High open interest at a strike tells you: large numbers of professional traders have planted their flag at that price. When price approaches that level, all of those position holders act — hedging, rolling, defending. The rubber band does not stretch in a vacuum. It stretches against the weight of committed money. Open interest is a map of where that weight is concentrated.</p>
      </div>

      {/* What Is OI */}
      <h3 style={{ ...ss, fontSize: '20px', color: V.gold, fontWeight: 400, margin: '0 0 12px' }}>What Is Open Interest?</h3>
      <p style={{ ...b, marginBottom: '12px' }}>Open interest (OI) is the total number of active, outstanding option contracts at a given strike and expiration — contracts that have been opened but not yet closed, expired, or exercised.</p>
      <p style={{ ...b, marginBottom: '12px' }}>Each time a new contract is created between a buyer and seller, OI increases by one. When a position is closed or expires, OI decreases. Unlike volume — which resets every day — open interest is cumulative. It represents commitment.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '0' }}>
        <div style={{ background: V.bg3, borderTop: `2px solid ${V.sage}`, borderRadius: '0 0 8px 8px', padding: '14px 16px' }}>
          <div style={{ fontSize: '11px', color: V.sage, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', marginBottom: '6px' }}>VOLUME</div>
          <p style={{ ...b, fontSize: '13px', lineHeight: 1.7 }}>Contracts traded <strong style={{ color: V.text }}>today</strong>. Resets to zero each morning. Shows activity but not commitment. A spike in volume without growing OI = the same positions changing hands.</p>
        </div>
        <div style={{ background: V.bg3, borderTop: `2px solid ${V.gold}`, borderRadius: '0 0 8px 8px', padding: '14px 16px' }}>
          <div style={{ fontSize: '11px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', marginBottom: '6px' }}>OPEN INTEREST</div>
          <p style={{ ...b, fontSize: '13px', lineHeight: 1.7 }}>Contracts <strong style={{ color: V.text }}>still open</strong>. Accumulates over the life of the contract. Shows where money is <em>staying</em>. Growing OI = new money entering. Falling OI = positions being closed.</p>
        </div>
      </div>

      <div style={hr} />

      {/* Why It Matters */}
      <h3 style={{ ...ss, fontSize: '20px', color: V.gold, fontWeight: 400, margin: '0 0 12px' }}>Why It Matters for Options Sellers</h3>
      <p style={{ ...b, marginBottom: '14px' }}>For premium sellers, open interest does two jobs: it tells you whether a strike is worth trading (liquidity), and it tells you whether a price level is structurally significant (positioning).</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
        {[
          { color: V.sage, label: 'LIQUIDITY', text: 'High OI = tight bid/ask spreads and easy fills. The minimum for any strike you trade is 500 OI. Below that, you risk wide spreads that eat into your premium — or being stuck in a position you cannot exit cleanly.' },
          { color: V.gold, label: 'STRUCTURE', text: 'Concentrated OI at a price level tells you where institutional money has positioned. These strikes often act as price magnets or repellents — especially in the final week before expiration.' },
          { color: '#c4934c', label: 'CONVICTION', text: 'Rising OI means new money is entering. Falling OI means positions are being closed. When OI is growing rapidly at a strike, pay attention — someone is making a large directional bet at that level.' },
        ].map(({ color, label, text }) => (
          <div key={label} style={{ background: V.bg3, borderLeft: `3px solid ${color}`, borderRadius: '0 8px 8px 0', padding: '14px 16px' }}>
            <div style={{ fontSize: '11px', color, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', marginBottom: '8px' }}>{label}</div>
            <p style={{ ...b, fontSize: '13px', lineHeight: 1.7 }}>{text}</p>
          </div>
        ))}
      </div>

      <div style={hr} />

      {/* Reading OI on the Chain */}
      <h3 style={{ ...ss, fontSize: '20px', color: V.gold, fontWeight: 400, margin: '0 0 12px' }}>Reading OI on the Options Chain</h3>
      <p style={{ ...b, marginBottom: '16px' }}>When you pull up the options chain on ThinkorSwim, OI appears as a column next to each strike. Here is how to read what you see:</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '0' }}>
        {[
          { label: 'High PUT OI below current price', color: V.sage, text: 'Large numbers of put buyers have purchased protection at that level. The market makers who sold those puts must hedge — adding buying pressure near that strike. This is structural support. Your CSP strike is well-placed here.' },
          { label: 'High CALL OI above current price', color: V.rose, text: 'Significant call buying overhead. Market makers hedging those short calls may sell stock near that level — creating a ceiling. This is structural resistance. Natural strike territory for covered calls.' },
          { label: 'Max Pain', color: V.gold, text: 'The expiration price where the maximum number of options contracts expire worthless. Dealers benefit when price settles near max pain. In the final days before expiration, price often gravitates toward this level. Know where it is — it is context, not a trading signal.' },
        ].map(({ label, color, text }) => (
          <div key={label} style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '16px 18px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0, marginTop: '5px' }} />
            <div>
              <div style={{ fontSize: '13px', color: V.text, fontFamily: "'Jost',sans-serif", fontWeight: 600, marginBottom: '6px' }}>{label}</div>
              <p style={{ ...b, fontSize: '13px', lineHeight: 1.7 }}>{text}</p>
            </div>
          </div>
        ))}
      </div>

      <div style={hr} />

      {/* OI and Liquidity */}
      <h3 style={{ ...ss, fontSize: '20px', color: V.gold, fontWeight: 400, margin: '0 0 12px' }}>OI and Liquidity — The Non-Negotiable</h3>
      <p style={{ ...b, marginBottom: '12px' }}>Liquidity is what separates a clean trade from a trap. A strike with low open interest can have spreads of $0.30 or more — meaning you give up $30 per contract just getting in and out. On a $1.50 credit, that is 20% of your premium gone before the trade has even breathed.</p>
      <div style={{ background: 'rgba(201,168,76,0.06)', border: `1px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '18px 22px' }}>
        <div style={{ fontSize: '12px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', marginBottom: '10px' }}>THE LIQUIDITY RULE</div>
        <p style={{ ...b, marginBottom: '8px' }}>Minimum <strong style={{ color: V.gold }}>500 open interest</strong> on any strike you intend to trade. This is not a guideline — it is a minimum. If the OI is below 500, skip the trade and find a better strike or underlying.</p>
        <p style={{ ...b }}>On TOS, you can filter the Stock Hacker for OI &gt; 500 to pre-screen your watchlist. The best trades are on liquid underlyings where every strike near the money has thousands of contracts in open interest.</p>
      </div>

      <div style={hr} />

      {/* OI in Your Routine */}
      <h3 style={{ ...ss, fontSize: '20px', color: V.gold, fontWeight: 400, margin: '0 0 12px' }}>OI in Your Trading Routine</h3>
      <p style={{ ...b, marginBottom: '12px' }}>Open interest is not something you check once — it is part of your pre-trade ritual. Before every trade, glance at the OI at your target strike and at nearby strikes one standard deviation out. You are looking for:</p>
      <p style={{ ...b, marginBottom: '12px' }}>Heavy OI at your strike → a watched level with structural support. The market knows about this level. That is good. It means your thesis is shared by others with real money committed. Heavy OI one strike away → potential resistance or acceleration point if price breaks through. Factor it into your exit plan.</p>
      <p style={{ ...b }}>The options chain is not just a price list — it is a live map of where money is committed. Learning to read it is learning to see what the professionals see before they act.</p>

      <div style={hr} />

      {/* Pre-trade OI checklist */}
      <div style={{ background: V.bg3, border: `1px solid rgba(201,168,76,0.25)`, borderRadius: '12px', padding: '20px 24px' }}>
        <div style={{ fontSize: '12px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', marginBottom: '14px' }}>PRE-TRADE OI CHECKLIST</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {checkItems.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div style={{ width: '18px', height: '18px', border: `1px solid rgba(201,168,76,0.35)`, borderRadius: '4px', flexShrink: 0, marginTop: '1px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: 'rgba(201,168,76,0.2)' }} />
              </div>
              <p style={{ ...b, fontSize: '13px', lineHeight: 1.65 }}>{item}</p>
            </div>
          ))}
        </div>
      </div>

      <div style={hr} />

      {/* Closing connection card */}
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 10px 10px 0', padding: '20px 24px' }}>
        <div style={{ fontSize: '11px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', marginBottom: '8px' }}>PUTTING IT ALL TOGETHER</div>
        <p style={{ ...b, marginBottom: '18px' }}>Support zones + resistance zones + open interest levels = a three-layer structural map. When these layers align — your strike sits above a support zone, has strong put OI beneath it, and the rubber band momentum confirms the setup — you have a high-conviction trade with structure working in your favor from multiple angles.</p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => onNavigate && onNavigate('support_resistance_practice')}
            style={{ ...btnGold, padding: '10px 20px', fontSize: '13px' }}>
            Practice in Options Chain →
          </button>
        </div>
      </div>

    </div>
  );
}

function Ch08PositionSizingVisual({ onNavigate }) {
  return (
    <div style={{ marginTop: '24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        <div style={{ background: 'rgba(201,168,76,0.06)', border: `1.5px solid ${V.gold}`, borderRadius: '12px', padding: '22px 20px' }}>
          <div style={{ fontSize: '10px', letterSpacing: '0.14em', color: V.gold, textTransform: 'uppercase', fontFamily: "'Jost',sans-serif", fontWeight: 700, marginBottom: '10px' }}>Rule 1 — Per Trade Maximum</div>
          <div style={{ ...serif, fontSize: '22px', color: V.goldL, fontWeight: 500, marginBottom: '12px' }}>1–2% of account</div>
          <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: '0 0 14px' }}>
            The maximum you can lose on any single trade. No exceptions, no matter how confident you feel.
          </p>
          <div style={{ background: 'rgba(201,168,76,0.08)', borderRadius: '8px', padding: '12px 14px' }}>
            <div style={{ fontSize: '12px', color: V.text, fontFamily: "'Jost',sans-serif", lineHeight: 1.75 }}>
              <strong style={{ color: V.gold }}>$25,000 account example:</strong><br />
              Maximum loss per trade: <strong style={{ color: V.goldL }}>$250 – $500</strong>
            </div>
          </div>
        </div>
        <div style={{ background: 'rgba(201,168,76,0.06)', border: `1.5px solid ${V.gold}`, borderRadius: '12px', padding: '22px 20px' }}>
          <div style={{ fontSize: '10px', letterSpacing: '0.14em', color: V.gold, textTransform: 'uppercase', fontFamily: "'Jost',sans-serif", fontWeight: 700, marginBottom: '10px' }}>Rule 2 — Total Portfolio Maximum</div>
          <div style={{ ...serif, fontSize: '22px', color: V.goldL, fontWeight: 500, marginBottom: '12px' }}>5% of account</div>
          <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: '0 0 14px' }}>
            The maximum at risk across ALL open positions combined. A bad day in the market should never be a catastrophic day for your account.
          </p>
          <div style={{ background: 'rgba(201,168,76,0.08)', borderRadius: '8px', padding: '12px 14px' }}>
            <div style={{ fontSize: '12px', color: V.text, fontFamily: "'Jost',sans-serif", lineHeight: 1.75 }}>
              <strong style={{ color: V.gold }}>$25,000 account example:</strong><br />
              Total open risk across all positions: <strong style={{ color: V.goldL }}>$1,250 max</strong>
            </div>
          </div>
        </div>
      </div>
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.sage}`, borderRadius: '0 8px 8px 0', padding: '14px 18px' }}>
        <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.8, margin: 0 }}>
          <strong style={{ color: V.text }}>How they work together:</strong> With five positions each risking 1% of your account, your total portfolio risk is exactly 5% — right at the limit. This forces you to be selective. You cannot have seven simultaneous positions if each risks 1%. The math forces discipline.
        </p>
      </div>
    </div>
  );
}

function Ch03MAVisual() {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d');
    const W = cv.width, H = cv.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(12,10,8,0.97)'; ctx.fillRect(0, 0, W, H);
    const pad = { l: 10, r: 118, t: 18, b: 30 };
    const gW = W - pad.l - pad.r, gH = H - pad.t - pad.b;
    const N = 80;
    const px = i => pad.l + (i / (N - 1)) * gW;

    // Uptrending price with realistic noise
    const prices = Array.from({ length: N }, (_, i) =>
      0.22 + (0.54 / (N - 1)) * i + Math.sin(i * 0.68) * 0.038 + Math.cos(i * 1.4) * 0.022
    );

    // Simulated SMAs: 8-period ≈ 20-day, 18-period ≈ 50-day, 38-period ≈ 200-day
    const sma = (period) => prices.map((_, i) => {
      if (i < period - 1) return null;
      const sl = prices.slice(i - period + 1, i + 1);
      return sl.reduce((a, b) => a + b, 0) / sl.length;
    });
    const ma20 = sma(8); const ma50 = sma(18); const ma200 = sma(38);

    const allVals = [...prices, ...ma50.filter(Boolean), ...ma200.filter(Boolean)];
    const minP = Math.min(...allVals) - 0.04, maxP = Math.max(...allVals) + 0.04;
    const py = p => pad.t + gH * (1 - (p - minP) / (maxP - minP));

    // Subtle horizontal grid
    ctx.strokeStyle = 'rgba(201,168,76,0.06)'; ctx.lineWidth = 0.5;
    for (let i = 1; i < 4; i++) {
      ctx.beginPath(); ctx.moveTo(pad.l, pad.t + gH * i / 4); ctx.lineTo(pad.l + gW, pad.t + gH * i / 4); ctx.stroke();
    }

    // Draw 200-day SMA (red — furthest from price, long-term floor)
    ctx.beginPath(); ctx.strokeStyle = '#c47a7a'; ctx.lineWidth = 1.8; ctx.setLineDash([]);
    let go = false;
    for (let i = 0; i < N; i++) { if (ma200[i] == null) continue; if (!go) { ctx.moveTo(px(i), py(ma200[i])); go = true; } else ctx.lineTo(px(i), py(ma200[i])); }
    ctx.stroke();

    // Draw 50-day SMA (green)
    ctx.beginPath(); ctx.strokeStyle = '#7aab8a'; ctx.lineWidth = 1.8; go = false;
    for (let i = 0; i < N; i++) { if (ma50[i] == null) continue; if (!go) { ctx.moveTo(px(i), py(ma50[i])); go = true; } else ctx.lineTo(px(i), py(ma50[i])); }
    ctx.stroke();

    // Draw 20-day SMA (blue — closest to price)
    ctx.beginPath(); ctx.strokeStyle = '#7a9ec4'; ctx.lineWidth = 1.8; go = false;
    for (let i = 0; i < N; i++) { if (ma20[i] == null) continue; if (!go) { ctx.moveTo(px(i), py(ma20[i])); go = true; } else ctx.lineTo(px(i), py(ma20[i])); }
    ctx.stroke();

    // Draw price (gold, on top)
    ctx.beginPath(); ctx.strokeStyle = '#c9a84c'; ctx.lineWidth = 2; ctx.setLineDash([]);
    prices.forEach((p, i) => i === 0 ? ctx.moveTo(px(i), py(p)) : ctx.lineTo(px(i), py(p)));
    ctx.stroke();

    // CSP entry zone circle — where price dips to 20-day MA (~index 52)
    const eIdx = 52;
    const eX = px(eIdx), eY = py(ma20[eIdx] != null ? ma20[eIdx] : prices[eIdx]);
    ctx.strokeStyle = 'rgba(122,171,138,0.7)'; ctx.lineWidth = 1; ctx.setLineDash([2, 3]);
    ctx.beginPath(); ctx.arc(eX, eY, 9, 0, Math.PI * 2); ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = '8px "DM Mono", monospace'; ctx.fillStyle = 'rgba(122,171,138,0.85)'; ctx.textAlign = 'left';
    ctx.fillText('CSP entry zone', eX + 12, eY + 4);

    // Legend (right column)
    const lx = pad.l + gW + 10;
    const items = [
      { color: '#c9a84c', label: 'Price' },
      { color: '#7a9ec4', label: 'Blue — 20 Day SMA' },
      { color: '#7aab8a', label: 'Green — 50 Day SMA' },
      { color: '#c47a7a', label: 'Red — 200 Day SMA' },
    ];
    items.forEach(({ color, label }, idx) => {
      const ly = pad.t + idx * 26;
      ctx.strokeStyle = color; ctx.lineWidth = idx === 0 ? 2 : 1.8;
      ctx.beginPath(); ctx.moveTo(lx, ly + 7); ctx.lineTo(lx + 16, ly + 7); ctx.stroke();
      ctx.font = '9px "DM Mono", monospace'; ctx.fillStyle = color; ctx.textAlign = 'left';
      ctx.fillText(label, lx + 20, ly + 10);
    });

    // x-axis label
    ctx.textAlign = 'center'; ctx.font = '8px "Jost", sans-serif'; ctx.fillStyle = 'rgba(240,237,230,0.2)';
    ctx.fillText('Time →', pad.l + gW / 2, H - 8);
  }, []);
  return (
    <div style={{ margin: '20px 0' }}>
      <canvas ref={ref} width={560} height={180} style={{ width: '100%', maxWidth: '560px', display: 'block', borderRadius: '8px', border: '0.5px solid rgba(120,110,90,0.2)' }} />
      <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", marginTop: '6px', textAlign: 'center' }}>
        The three MAs fan in order during a healthy uptrend — price above all three is the ideal environment for premium sellers. The circled zone shows where price pulls back to the 20-day MA: your CSP entry zone.
      </p>
    </div>
  );
}

function Ch03MATabVisual({ onNavigate }) {
  const eqRef = useRef(null);
  const gcRef = useRef(null);
  const dcRef = useRef(null);
  const conRef = useRef(null);
  const chRef = useRef(null);
  const cspRef = useRef(null);

  useEffect(() => {
    const drawEq = () => {
      const cv = eqRef.current; if (!cv) return;
      const ctx = cv.getContext('2d'), W = cv.width, H = cv.height;
      ctx.fillStyle = 'rgba(12,10,8,0.97)'; ctx.fillRect(0,0,W,H);
      const pad = {l:30,r:20,t:16,b:24}, gW = W-pad.l-pad.r, gH = H-pad.t-pad.b, N = 70;
      const px = i => pad.l+(i/(N-1))*gW;
      const ma = Array.from({length:N},(_,i) => 0.5+Math.sin(i*0.08)*0.04);
      const price = Array.from({length:N},(_,i) => ma[i]+Math.sin(i*0.38)*0.12+Math.sin(i*0.7)*0.04);
      const allV=[...ma,...price], minP=Math.min(...allV)-0.06, maxP=Math.max(...allV)+0.06;
      const py = p => pad.t+gH*(1-(p-minP)/(maxP-minP));
      ctx.strokeStyle='rgba(201,168,76,0.06)';ctx.lineWidth=0.5;
      for(let i=1;i<4;i++){ctx.beginPath();ctx.moveTo(pad.l,pad.t+gH*i/4);ctx.lineTo(pad.l+gW,pad.t+gH*i/4);ctx.stroke();}
      ctx.beginPath();ctx.strokeStyle='#7a9ec4';ctx.lineWidth=2;
      ma.forEach((v,i)=>i===0?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v)));ctx.stroke();
      ctx.beginPath();ctx.strokeStyle='#c9a84c';ctx.lineWidth=1.8;
      price.forEach((v,i)=>i===0?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v)));ctx.stroke();
      [14,28,42,56].forEach(idx=>{
        ctx.strokeStyle='rgba(122,171,138,0.4)';ctx.lineWidth=0.8;ctx.setLineDash([2,3]);
        ctx.beginPath();ctx.moveTo(px(idx),pad.t);ctx.lineTo(px(idx),pad.t+gH);ctx.stroke();
      });
      ctx.setLineDash([]);
      ctx.font='8px "DM Mono",monospace';ctx.fillStyle='#7a9ec4';ctx.textAlign='left';
      ctx.fillText('20-day MA',px(2),pad.t+gH/2-6);
      ctx.fillStyle='#c9a84c';ctx.fillText('Price',px(2),pad.t+16);
      ctx.fillStyle='rgba(122,171,138,0.7)';ctx.textAlign='center';
      ctx.fillText('↓ return',px(14),pad.t+gH+16);ctx.fillText('↓ return',px(42),pad.t+gH+16);
    };
    const drawGC = () => {
      const cv = gcRef.current; if(!cv) return;
      const ctx = cv.getContext('2d'), W = cv.width, H = cv.height;
      ctx.fillStyle='rgba(12,10,8,0.97)';ctx.fillRect(0,0,W,H);
      const pad={l:20,r:70,t:16,b:24},gW=W-pad.l-pad.r,gH=H-pad.t-pad.b,N=60;
      const px=i=>pad.l+(i/(N-1))*gW, cross=Math.floor(N*0.45);
      const ma200=Array.from({length:N},(_,i)=>0.45+i*0.001);
      const ma50=Array.from({length:N},(_,i)=>i<cross?ma200[i]-0.12+(i/cross)*0.12+0.01:ma200[i]+(i-cross)*0.003);
      const allV=[...ma200,...ma50],minP=Math.min(...allV)-0.06,maxP=Math.max(...allV)+0.06;
      const py=p=>pad.t+gH*(1-(p-minP)/(maxP-minP));
      ctx.strokeStyle='rgba(201,168,76,0.06)';ctx.lineWidth=0.5;
      for(let i=1;i<4;i++){ctx.beginPath();ctx.moveTo(pad.l,pad.t+gH*i/4);ctx.lineTo(pad.l+gW,pad.t+gH*i/4);ctx.stroke();}
      ctx.beginPath();ctx.strokeStyle='#c47a7a';ctx.lineWidth=2;
      ma200.forEach((v,i)=>i===0?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v)));ctx.stroke();
      ctx.beginPath();ctx.strokeStyle='#7aab8a';ctx.lineWidth=2;
      ma50.forEach((v,i)=>i===0?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v)));ctx.stroke();
      const cx=px(cross);
      ctx.fillStyle='rgba(122,171,138,0.1)';ctx.fillRect(cx,pad.t,gW/2,gH);
      ctx.strokeStyle='rgba(122,171,138,0.5)';ctx.lineWidth=1;ctx.setLineDash([3,3]);
      ctx.beginPath();ctx.moveTo(cx,pad.t);ctx.lineTo(cx,pad.t+gH);ctx.stroke();ctx.setLineDash([]);
      ctx.font='9px "DM Mono",monospace';ctx.fillStyle='rgba(122,171,138,0.9)';ctx.textAlign='center';
      ctx.fillText('GOLDEN CROSS',cx+gW/4,pad.t+14);ctx.fillText('Bullish',cx+gW/4,pad.t+26);
      const lx=pad.l+gW+8;ctx.font='8px "DM Mono",monospace';ctx.textAlign='left';
      ctx.strokeStyle='#7aab8a';ctx.lineWidth=1.8;ctx.beginPath();ctx.moveTo(lx,pad.t+10);ctx.lineTo(lx+14,pad.t+10);ctx.stroke();
      ctx.fillStyle='#7aab8a';ctx.fillText('50d',lx+16,pad.t+13);
      ctx.strokeStyle='#c47a7a';ctx.beginPath();ctx.moveTo(lx,pad.t+26);ctx.lineTo(lx+14,pad.t+26);ctx.stroke();
      ctx.fillStyle='#c47a7a';ctx.fillText('200d',lx+16,pad.t+29);
    };
    const drawDC = () => {
      const cv = dcRef.current; if(!cv) return;
      const ctx = cv.getContext('2d'), W = cv.width, H = cv.height;
      ctx.fillStyle='rgba(12,10,8,0.97)';ctx.fillRect(0,0,W,H);
      const pad={l:20,r:70,t:16,b:24},gW=W-pad.l-pad.r,gH=H-pad.t-pad.b,N=60;
      const px=i=>pad.l+(i/(N-1))*gW, cross=Math.floor(N*0.45);
      const ma200=Array.from({length:N},(_,i)=>0.52-i*0.001);
      const ma50=Array.from({length:N},(_,i)=>i<cross?ma200[i]+0.12-(i/cross)*0.12-0.01:ma200[i]-(i-cross)*0.003);
      const allV=[...ma200,...ma50],minP=Math.min(...allV)-0.06,maxP=Math.max(...allV)+0.06;
      const py=p=>pad.t+gH*(1-(p-minP)/(maxP-minP));
      ctx.strokeStyle='rgba(201,168,76,0.06)';ctx.lineWidth=0.5;
      for(let i=1;i<4;i++){ctx.beginPath();ctx.moveTo(pad.l,pad.t+gH*i/4);ctx.lineTo(pad.l+gW,pad.t+gH*i/4);ctx.stroke();}
      ctx.beginPath();ctx.strokeStyle='#c47a7a';ctx.lineWidth=2;
      ma200.forEach((v,i)=>i===0?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v)));ctx.stroke();
      ctx.beginPath();ctx.strokeStyle='#7aab8a';ctx.lineWidth=2;
      ma50.forEach((v,i)=>i===0?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v)));ctx.stroke();
      const cx=px(cross);
      ctx.fillStyle='rgba(196,122,122,0.1)';ctx.fillRect(cx,pad.t,gW/2,gH);
      ctx.strokeStyle='rgba(196,122,122,0.5)';ctx.lineWidth=1;ctx.setLineDash([3,3]);
      ctx.beginPath();ctx.moveTo(cx,pad.t);ctx.lineTo(cx,pad.t+gH);ctx.stroke();ctx.setLineDash([]);
      ctx.font='9px "DM Mono",monospace';ctx.fillStyle='rgba(196,122,122,0.9)';ctx.textAlign='center';
      ctx.fillText('DEATH CROSS',cx+gW/4,pad.t+14);ctx.fillText('Bearish',cx+gW/4,pad.t+26);
      const lx=pad.l+gW+8;ctx.font='8px "DM Mono",monospace';ctx.textAlign='left';
      ctx.strokeStyle='#7aab8a';ctx.lineWidth=1.8;ctx.beginPath();ctx.moveTo(lx,pad.t+10);ctx.lineTo(lx+14,pad.t+10);ctx.stroke();
      ctx.fillStyle='#7aab8a';ctx.fillText('50d',lx+16,pad.t+13);
      ctx.strokeStyle='#c47a7a';ctx.beginPath();ctx.moveTo(lx,pad.t+26);ctx.lineTo(lx+14,pad.t+26);ctx.stroke();
      ctx.fillStyle='#c47a7a';ctx.fillText('200d',lx+16,pad.t+29);
    };
    const drawCon = () => {
      const cv = conRef.current; if(!cv) return;
      const ctx = cv.getContext('2d'), W = cv.width, H = cv.height;
      ctx.fillStyle='rgba(12,10,8,0.97)';ctx.fillRect(0,0,W,H);
      const pad={l:20,r:20,t:16,b:28},gW=W-pad.l-pad.r,gH=H-pad.t-pad.b,N=90;
      const px=i=>pad.l+(i/(N-1))*gW;
      const ph=i=>i<30?1:i<60?2:3;
      const ma20=Array.from({length:N},(_,i)=>{
        if(ph(i)===1)return 0.3+i*0.005+Math.sin(i*0.5)*0.01;
        if(ph(i)===2)return 0.45+Math.sin((i-30)*0.3)*0.01;
        return 0.46+(i-60)*0.005+Math.sin(i*0.5)*0.01;
      });
      const ma50=Array.from({length:N},(_,i)=>{
        if(ph(i)===1)return 0.25+i*0.004;
        if(ph(i)===2)return 0.44+Math.sin((i-30)*0.15)*0.005;
        return 0.45+(i-60)*0.004;
      });
      const ma200=Array.from({length:N},(_,i)=>{
        if(ph(i)===1)return 0.18+i*0.002;
        if(ph(i)===2)return 0.42;
        return 0.42+(i-60)*0.002;
      });
      const allV=[...ma20,...ma50,...ma200],minP=Math.min(...allV)-0.06,maxP=Math.max(...allV)+0.06;
      const py=p=>pad.t+gH*(1-(p-minP)/(maxP-minP));
      ctx.strokeStyle='rgba(201,168,76,0.06)';ctx.lineWidth=0.5;
      for(let i=1;i<4;i++){ctx.beginPath();ctx.moveTo(pad.l,pad.t+gH*i/4);ctx.lineTo(pad.l+gW,pad.t+gH*i/4);ctx.stroke();}
      [px(30),px(60)].forEach(x=>{
        ctx.strokeStyle='rgba(201,168,76,0.2)';ctx.lineWidth=0.5;ctx.setLineDash([3,3]);
        ctx.beginPath();ctx.moveTo(x,pad.t);ctx.lineTo(x,pad.t+gH);ctx.stroke();ctx.setLineDash([]);
      });
      [[ma200,'#c47a7a'],[ma50,'#7aab8a'],[ma20,'#7a9ec4']].forEach(([ma,col])=>{
        ctx.beginPath();ctx.strokeStyle=col;ctx.lineWidth=1.6;
        ma.forEach((v,i)=>i===0?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v)));ctx.stroke();
      });
      ctx.font='8px "Jost",sans-serif';ctx.fillStyle='rgba(201,168,76,0.45)';ctx.textAlign='center';
      ctx.fillText('Trending',px(15),pad.t+gH+18);ctx.fillText('Consolidating',px(45),pad.t+gH+18);ctx.fillText('Resuming',px(75),pad.t+gH+18);
    };
    const drawCh = () => {
      const cv = chRef.current; if(!cv) return;
      const ctx = cv.getContext('2d'), W = cv.width, H = cv.height;
      ctx.fillStyle='rgba(12,10,8,0.97)';ctx.fillRect(0,0,W,H);
      const pad={l:20,r:20,t:16,b:16},gW=W-pad.l-pad.r,gH=H-pad.t-pad.b,N=70;
      const px=i=>pad.l+(i/(N-1))*gW,base=0.5;
      const ma20=Array.from({length:N},(_,i)=>base+Math.sin(i*0.4)*0.04+Math.sin(i*0.9)*0.02);
      const ma50=Array.from({length:N},(_,i)=>base+Math.cos(i*0.3)*0.035+Math.sin(i*1.1)*0.015);
      const ma200=Array.from({length:N},(_,i)=>base-0.01+Math.sin(i*0.2)*0.02+Math.cos(i*0.6)*0.01);
      const price=Array.from({length:N},(_,i)=>base+Math.sin(i*0.6)*0.08+Math.cos(i*1.3)*0.03);
      const allV=[...ma20,...ma50,...ma200,...price],minP=Math.min(...allV)-0.05,maxP=Math.max(...allV)+0.05;
      const py=p=>pad.t+gH*(1-(p-minP)/(maxP-minP));
      ctx.strokeStyle='rgba(201,168,76,0.06)';ctx.lineWidth=0.5;
      for(let i=1;i<4;i++){ctx.beginPath();ctx.moveTo(pad.l,pad.t+gH*i/4);ctx.lineTo(pad.l+gW,pad.t+gH*i/4);ctx.stroke();}
      ctx.beginPath();ctx.strokeStyle='rgba(201,168,76,0.3)';ctx.lineWidth=1.2;
      price.forEach((v,i)=>i===0?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v)));ctx.stroke();
      [[ma200,'#c47a7a'],[ma50,'#7aab8a'],[ma20,'#7a9ec4']].forEach(([ma,col])=>{
        ctx.beginPath();ctx.strokeStyle=col;ctx.lineWidth=1.5;
        ma.forEach((v,i)=>i===0?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v)));ctx.stroke();
      });
      ctx.font='bold 11px "Jost",sans-serif';ctx.fillStyle='rgba(196,122,122,0.8)';ctx.textAlign='center';
      ctx.fillText('TANGLED — NO CLEAR DIRECTION',W/2,pad.t+gH/2);
    };
    const drawCSP = () => {
      const cv = cspRef.current; if(!cv) return;
      const ctx = cv.getContext('2d'), W = cv.width, H = cv.height;
      ctx.fillStyle='rgba(12,10,8,0.97)';ctx.fillRect(0,0,W,H);
      const pad={l:20,r:20,t:16,b:24},gW=W-pad.l-pad.r,gH=H-pad.t-pad.b,N=80;
      const px=i=>pad.l+(i/(N-1))*gW;
      const prices=Array.from({length:N},(_,i)=>0.22+(0.48/(N-1))*i+Math.sin(i*0.68)*0.038+Math.cos(i*1.4)*0.022);
      const sma=p=>prices.map((_,i)=>{if(i<p-1)return null;const sl=prices.slice(i-p+1,i+1);return sl.reduce((a,b)=>a+b,0)/sl.length;});
      const ma20=sma(8),ma50=sma(18),ma200=sma(38);
      const allV=[...prices,...ma50.filter(Boolean),...ma200.filter(Boolean)];
      const minP=Math.min(...allV)-0.04,maxP=Math.max(...allV)+0.04;
      const py=p=>pad.t+gH*(1-(p-minP)/(maxP-minP));
      ctx.strokeStyle='rgba(201,168,76,0.06)';ctx.lineWidth=0.5;
      for(let i=1;i<4;i++){ctx.beginPath();ctx.moveTo(pad.l,pad.t+gH*i/4);ctx.lineTo(pad.l+gW,pad.t+gH*i/4);ctx.stroke();}
      [[ma200,'#c47a7a',1.6],[ma50,'#7aab8a',1.6],[ma20,'#7a9ec4',1.8]].forEach(([ma,col,lw])=>{
        ctx.beginPath();ctx.strokeStyle=col;ctx.lineWidth=lw;
        ma.forEach((v,i)=>{if(v==null)return;(i===0||ma[i-1]==null)?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v));});ctx.stroke();
      });
      ctx.beginPath();ctx.strokeStyle='#c9a84c';ctx.lineWidth=2;
      prices.forEach((v,i)=>i===0?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v)));ctx.stroke();
      const eIdx=52;
      if(ma20[eIdx]!=null){
        const eX=px(eIdx),eY=py(ma20[eIdx]);
        ctx.fillStyle='rgba(122,171,138,0.12)';ctx.beginPath();ctx.arc(eX,eY,18,0,Math.PI*2);ctx.fill();
        ctx.strokeStyle='rgba(122,171,138,0.7)';ctx.lineWidth=1;ctx.setLineDash([2,3]);
        ctx.beginPath();ctx.arc(eX,eY,18,0,Math.PI*2);ctx.stroke();ctx.setLineDash([]);
        ctx.font='8px "DM Mono",monospace';ctx.fillStyle='rgba(122,171,138,0.9)';ctx.textAlign='left';
        ctx.fillText('CSP zone',eX+22,eY+4);
      }
      ctx.font='8px "DM Mono",monospace';ctx.fillStyle='rgba(240,237,230,0.25)';ctx.textAlign='center';
      ctx.fillText('Time →',pad.l+gW/2,H-6);
    };
    drawEq(); drawGC(); drawDC(); drawCon(); drawCh(); drawCSP();
  }, []);

  const ss = { fontFamily:"'Cormorant Garamond',Georgia,serif" };
  const divider = { height:'0.5px', background:'rgba(201,168,76,0.12)', margin:'28px 0' };
  const sh = { ...ss, fontSize:'20px', fontWeight:400, color:V.gold, margin:'0 0 10px' };
  const bt = { fontFamily:"'Jost',sans-serif", fontSize:'14px', color:'rgba(240,237,230,0.82)', lineHeight:1.85, margin:0 };
  return (
    <div style={{ marginTop:'20px' }}>
      {/* Color reference */}
      <div style={{ background:V.bg2, borderRadius:'10px', padding:'20px 24px', marginBottom:'24px' }}>
        <h3 style={{ ...sh, marginBottom:'14px' }}>The Three Lines You Will Add to Your Chart</h3>
        <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
          {[
            { color:'#7a9ec4', period:'20-day', role:'Short-term momentum. The line price returns to most often. Your primary entry reference.' },
            { color:'#7aab8a', period:'50-day', role:'Medium-term trend. Defines the direction of the dominant trend.' },
            { color:'#c47a7a', period:'200-day', role:'Long-term trend. The most watched line on Wall Street. When price is above it, the market is in a long-term uptrend.' },
          ].map(({color,period,role})=>(
            <div key={period} style={{ display:'flex', alignItems:'flex-start', gap:'14px' }}>
              <div style={{ width:'28px', height:'3px', background:color, borderRadius:'2px', flexShrink:0, marginTop:'8px' }} />
              <div>
                <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'12px', color, marginRight:'10px' }}>{period}</span>
                <span style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:'rgba(240,237,230,0.7)', lineHeight:1.6 }}>{role}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Equilibrium */}
      <div style={divider} />
      <h3 style={sh}>MAs as Equilibrium — The Rubber Band Reset</h3>
      <p style={{ ...bt, marginBottom:'14px' }}>Price does not move in a straight line. It stretches away from the moving average and then returns to it. This is the rubber band at work. The 20-day MA is where price most often resets during a healthy uptrend.</p>
      <canvas ref={eqRef} width={520} height={130} style={{ width:'100%', maxWidth:'520px', display:'block', borderRadius:'8px', border:'0.5px solid rgba(120,110,90,0.2)' }} />
      <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'11px', color:V.dim, marginTop:'6px', textAlign:'center', marginBottom:'0' }}>Price oscillates around the 20-day MA (blue). Each time it stretches too far, it snaps back.</p>

      {/* Crossovers */}
      <div style={divider} />
      <h3 style={sh}>MA Crossovers — Reading the Long-Term Signal</h3>
      <p style={{ ...bt, marginBottom:'16px' }}>When the 50-day MA crosses the 200-day MA, it signals a major shift in trend. These are low-frequency, high-conviction signals.</p>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px', marginBottom:'16px' }}>
        <div>
          <canvas ref={gcRef} width={260} height={120} style={{ width:'100%', display:'block', borderRadius:'8px', border:'0.5px solid rgba(122,171,138,0.2)' }} />
          <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'11px', color:V.sage, marginTop:'6px', textAlign:'center', marginBottom:0 }}>Golden Cross — bullish</p>
        </div>
        <div>
          <canvas ref={dcRef} width={260} height={120} style={{ width:'100%', display:'block', borderRadius:'8px', border:'0.5px solid rgba(196,122,122,0.2)' }} />
          <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'11px', color:V.rose, marginTop:'6px', textAlign:'center', marginBottom:0 }}>Death Cross — bearish</p>
        </div>
      </div>
      <div style={{ background:V.bg2, borderLeft:`3px solid ${V.gold}`, borderRadius:'0 8px 8px 0', padding:'14px 18px' }}>
        <p style={{ ...bt, fontSize:'13px' }}>The 200-day MA deserves special respect. When the broad market is above its 200-day, conditions are favorable for premium sellers. When price is below it for an extended period, consider reducing size or waiting for clarity.</p>
      </div>

      {/* Consolidation */}
      <div style={divider} />
      <h3 style={sh}>Consolidation — What MAs Look Like When a Stock Rests</h3>
      <p style={{ ...bt, marginBottom:'14px' }}>After a strong move, stocks consolidate. Moving averages converge toward each other. This is normal and healthy. When they fan out again in the direction of the prior trend, the move often resumes.</p>
      <canvas ref={conRef} width={520} height={130} style={{ width:'100%', maxWidth:'520px', display:'block', borderRadius:'8px', border:'0.5px solid rgba(120,110,90,0.2)', marginBottom:'14px' }} />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px' }}>
        {[
          {phase:'Phase 1',desc:'MAs fanning — healthy trend in progress'},
          {phase:'Phase 2',desc:'MAs converging — stock is resting'},
          {phase:'Phase 3',desc:'MAs fanning again — trend resuming'},
        ].map(({phase,desc})=>(
          <div key={phase} style={{ background:V.bg2, borderRadius:'8px', padding:'12px 14px' }}>
            <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color:V.gold, marginBottom:'4px' }}>{phase}</div>
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, lineHeight:1.6, margin:0 }}>{desc}</p>
          </div>
        ))}
      </div>

      {/* Choppy markets */}
      <div style={divider} />
      <h3 style={sh}>Choppy Markets — When to Stand Aside</h3>
      <p style={{ ...bt, marginBottom:'14px' }}>When all three moving averages are flat and tangled, the market has no direction. This is the worst environment for premium sellers. You are not missing an opportunity by staying out — you are protecting your capital.</p>
      <canvas ref={chRef} width={520} height={110} style={{ width:'100%', maxWidth:'520px', display:'block', borderRadius:'8px', border:'0.5px solid rgba(196,122,122,0.2)', marginBottom:'14px' }} />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
        <div style={{ background:V.bg2, borderLeft:`3px solid ${V.rose}`, borderRadius:'0 8px 8px 0', padding:'14px 18px' }}>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color:V.rose, marginBottom:'6px' }}>WARNING SIGNS</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            {['All three MAs are flat and bunched together','Price crosses the MAs multiple times per week','No clear HH/HL structure in price action'].map(s=>(
              <div key={s} style={{ display:'flex', gap:'8px', alignItems:'flex-start' }}>
                <span style={{ color:V.rose, flexShrink:0 }}>—</span>
                <span style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, lineHeight:1.5 }}>{s}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background:'rgba(122,171,138,0.06)', border:`0.5px solid rgba(122,171,138,0.25)`, borderRadius:'8px', padding:'14px 18px' }}>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color:V.sage, marginBottom:'6px' }}>THE STAND ASIDE RULE</div>
          <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:'rgba(240,237,230,0.8)', lineHeight:1.65, margin:0 }}>If you cannot clearly answer "what direction is this stock trending?" — the answer is: it isn't. Stay in cash. Your premium-selling strategies require a trend.</p>
        </div>
      </div>

      {/* CSP entry zone */}
      <div style={divider} />
      <h3 style={sh}>The CSP Entry Zone</h3>
      <p style={{ ...bt, marginBottom:'14px' }}>When a stock is in a clear uptrend (price above all three MAs, MAs fanning up), the 20-day MA becomes your entry reference. When price dips to the 20-day MA in an uptrend, the rubber band is stretched — and the probability of a bounce is highest.</p>
      <canvas ref={cspRef} width={520} height={160} style={{ width:'100%', maxWidth:'520px', display:'block', borderRadius:'8px', border:'0.5px solid rgba(122,171,138,0.2)', marginBottom:'14px' }} />
      <div style={{ background:'rgba(122,171,138,0.06)', border:`0.5px solid rgba(122,171,138,0.25)`, borderRadius:'8px', padding:'16px 20px', marginBottom:'16px' }}>
        <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:'rgba(240,237,230,0.85)', lineHeight:1.75, margin:'0 0 8px' }}>When you see price touch or pierce the 20-day MA during a confirmed uptrend, this is your signal to evaluate a CSP entry. Check RSI (next section) to confirm the stretch before placing a trade.</p>
        <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, fontStyle:'italic', lineHeight:1.6, margin:0 }}>The best entries happen at the intersection of price at the MA and RSI showing oversold conditions.</p>
      </div>
      {onNavigate && (
        <button style={{ ...btnGold, padding:'10px 20px', fontSize:'13px' }} onClick={()=>onNavigate('practice_ma')}>
          Practice Moving Averages →
        </button>
      )}
    </div>
  );
}

function Ch03RSITabVisual({ onNavigate }) {
  const rsiRef = useRef(null);
  const comboRef = useRef(null);

  useEffect(() => {
    const drawRSI = () => {
      const cv = rsiRef.current; if(!cv) return;
      const ctx = cv.getContext('2d'), W = cv.width, H = cv.height;
      ctx.fillStyle='rgba(12,10,8,0.97)';ctx.fillRect(0,0,W,H);
      const pad={l:36,r:16,t:16,b:24},gW=W-pad.l-pad.r,gH=H-pad.t-pad.b,N=80;
      const px=i=>pad.l+(i/(N-1))*gW;
      const py=v=>pad.t+gH*(1-v/100);
      const rsi=Array.from({length:N},(_,i)=>Math.min(85,Math.max(18,50+Math.sin(i*0.25)*22+Math.sin(i*0.6)*12+Math.sin(i*1.1)*6)));
      ctx.strokeStyle='rgba(201,168,76,0.06)';ctx.lineWidth=0.5;
      for(const v of [30,50,70]){ctx.beginPath();ctx.moveTo(pad.l,py(v));ctx.lineTo(pad.l+gW,py(v));ctx.stroke();}
      ctx.fillStyle='rgba(196,122,122,0.07)';ctx.fillRect(pad.l,py(100),gW,py(70)-py(100));
      ctx.fillStyle='rgba(122,171,138,0.07)';ctx.fillRect(pad.l,py(30),gW,py(0)-py(30));
      ctx.font='8px "DM Mono",monospace';
      ctx.fillStyle='rgba(196,122,122,0.7)';ctx.textAlign='right';ctx.fillText('70',pad.l-4,py(70)+3);
      ctx.fillStyle='rgba(201,168,76,0.5)';ctx.fillText('50',pad.l-4,py(50)+3);
      ctx.fillStyle='rgba(122,171,138,0.7)';ctx.fillText('30',pad.l-4,py(30)+3);
      rsi.forEach((v,i)=>{
        if(i>0){
          if(v<30){ctx.fillStyle='rgba(122,171,138,0.25)';ctx.fillRect(px(i-1),py(30),px(i)-px(i-1),py(0)-py(30));}
          if(v>70){ctx.fillStyle='rgba(196,122,122,0.25)';ctx.fillRect(px(i-1),py(100),px(i)-px(i-1),py(70)-py(100));}
        }
      });
      ctx.beginPath();ctx.strokeStyle='#c9a84c';ctx.lineWidth=2;
      rsi.forEach((v,i)=>i===0?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v)));ctx.stroke();
      ctx.font='8px "Jost",sans-serif';ctx.fillStyle='rgba(240,237,230,0.2)';ctx.textAlign='center';
      ctx.fillText('RSI',pad.l+gW/2,H-6);
    };
    const drawCombo = () => {
      const cv = comboRef.current; if(!cv) return;
      const ctx = cv.getContext('2d'), W = cv.width, H = cv.height;
      ctx.fillStyle='rgba(12,10,8,0.97)';ctx.fillRect(0,0,W,H);
      const pBot=H*0.42, rTop=H*0.62;
      const pp={l:36,r:16,t:8,b:pBot}, rp={l:36,r:16,t:rTop,b:H-16};
      const pW=W-pp.l-pp.r, pGH=pp.b-pp.t, rGH=rp.b-rp.t, N=70;
      const px=i=>pp.l+(i/(N-1))*pW;
      const prices=Array.from({length:N},(_,i)=>0.25+(0.45/(N-1))*i+Math.sin(i*0.68)*0.038+Math.cos(i*1.4)*0.022);
      const sma=p=>prices.map((_,i)=>{if(i<p-1)return null;const sl=prices.slice(i-p+1,i+1);return sl.reduce((a,b)=>a+b,0)/sl.length;});
      const ma20=sma(8);
      const allV=[...prices,...ma20.filter(Boolean)],minP=Math.min(...allV)-0.04,maxP=Math.max(...allV)+0.04;
      const pyP=p=>pp.t+pGH*(1-(p-minP)/(maxP-minP));
      const rsi=Array.from({length:N},(_,i)=>Math.min(75,Math.max(20,50+Math.sin(i*0.25)*18+Math.sin(i*0.6)*10-((i>48&&i<58)?15:0))));
      const pyR=v=>rp.t+rGH*(1-v/100);
      ctx.strokeStyle='rgba(201,168,76,0.06)';ctx.lineWidth=0.5;
      for(let i=1;i<3;i++){ctx.beginPath();ctx.moveTo(pp.l,pp.t+pGH*i/3);ctx.lineTo(pp.l+pW,pp.t+pGH*i/3);ctx.stroke();}
      ctx.beginPath();ctx.strokeStyle='#7a9ec4';ctx.lineWidth=1.6;
      ma20.forEach((v,i)=>{if(v==null)return;(i===0||ma20[i-1]==null)?ctx.moveTo(px(i),pyP(v)):ctx.lineTo(px(i),pyP(v));});ctx.stroke();
      ctx.beginPath();ctx.strokeStyle='#c9a84c';ctx.lineWidth=1.8;
      prices.forEach((v,i)=>i===0?ctx.moveTo(px(i),pyP(v)):ctx.lineTo(px(i),pyP(v)));ctx.stroke();
      for(const v of [30,50,70]){ctx.strokeStyle='rgba(201,168,76,0.06)';ctx.lineWidth=0.5;ctx.beginPath();ctx.moveTo(pp.l,pyR(v));ctx.lineTo(pp.l+pW,pyR(v));ctx.stroke();}
      ctx.fillStyle='rgba(122,171,138,0.07)';ctx.fillRect(pp.l,pyR(30),pW,rp.b-pyR(30));
      ctx.beginPath();ctx.strokeStyle='#c9a84c';ctx.lineWidth=1.8;
      rsi.forEach((v,i)=>i===0?ctx.moveTo(px(i),pyR(v)):ctx.lineTo(px(i),pyR(v)));ctx.stroke();
      const sigIdx=52;
      if(ma20[sigIdx]){
        const sx=px(sigIdx);
        ctx.fillStyle='rgba(122,171,138,0.08)';ctx.fillRect(sx-18,pp.t,36,rp.b-pp.t);
        ctx.strokeStyle='rgba(122,171,138,0.4)';ctx.lineWidth=1;ctx.setLineDash([2,3]);
        ctx.beginPath();ctx.moveTo(sx,pp.t);ctx.lineTo(sx,rp.b);ctx.stroke();ctx.setLineDash([]);
        ctx.font='8px "DM Mono",monospace';ctx.fillStyle='rgba(122,171,138,0.9)';ctx.textAlign='center';
        ctx.fillText('Signal',sx,pp.t+10);
      }
      ctx.font='8px "DM Mono",monospace';ctx.fillStyle='rgba(201,168,76,0.35)';ctx.textAlign='right';
      ctx.fillText('Price',pp.l-2,pp.t+pGH/2);ctx.fillText('RSI',pp.l-2,rp.t+rGH/2);
      ctx.strokeStyle='rgba(120,110,90,0.3)';ctx.lineWidth=0.5;
      ctx.beginPath();ctx.moveTo(pp.l,pBot+6);ctx.lineTo(pp.l+pW,pBot+6);ctx.stroke();
      ctx.fillStyle='rgba(122,171,138,0.55)';ctx.textAlign='right';
      ctx.fillText('30',pp.l-4,pyR(30)+3);ctx.fillText('70',pp.l-4,pyR(70)+3);
    };
    drawRSI(); drawCombo();
  }, []);

  const ss = { fontFamily:"'Cormorant Garamond',Georgia,serif" };
  const bt = { fontFamily:"'Jost',sans-serif", fontSize:'14px', color:'rgba(240,237,230,0.82)', lineHeight:1.85, margin:0 };
  return (
    <div style={{ marginTop:'20px' }}>
      <div style={{ background:V.bg2, borderRadius:'10px', padding:'20px 24px', marginBottom:'24px' }}>
        <h3 style={{ ...ss, fontSize:'20px', fontWeight:400, color:V.gold, margin:'0 0 12px' }}>Reading the RSI Scale</h3>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px' }}>
          {[
            {range:'0 – 30',label:'Oversold',color:V.sage,note:'Rubber band stretched DOWN. Bounce likely.'},
            {range:'30 – 70',label:'Neutral',color:'rgba(240,237,230,0.5)',note:'No edge from RSI alone. Look for confluence.'},
            {range:'70 – 100',label:'Overbought',color:V.rose,note:'Rubber band stretched UP. Caution for new longs.'},
          ].map(({range,label,color,note})=>(
            <div key={range} style={{ background:'rgba(255,255,255,0.02)', border:`0.5px solid ${color}40`, borderRadius:'8px', padding:'12px 14px' }}>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'11px', color, marginBottom:'4px' }}>{range}</div>
              <div style={{ ...ss, fontSize:'15px', color, fontWeight:400, marginBottom:'6px' }}>{label}</div>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, lineHeight:1.5, margin:0 }}>{note}</p>
            </div>
          ))}
        </div>
      </div>

      <canvas ref={rsiRef} width={520} height={140} style={{ width:'100%', maxWidth:'520px', display:'block', borderRadius:'8px', border:'0.5px solid rgba(120,110,90,0.2)', marginBottom:'8px' }} />
      <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'11px', color:V.dim, marginBottom:'24px', textAlign:'center' }}>RSI oscillates 0–100. Below 30 = oversold (green). Above 70 = overbought (red).</p>

      <div style={{ background:V.bg2, borderLeft:`3px solid ${V.gold}`, borderRadius:'0 10px 10px 0', padding:'16px 20px', marginBottom:'24px' }}>
        <h4 style={{ ...ss, fontSize:'16px', fontWeight:400, color:V.goldL, margin:'0 0 8px' }}>RSI Behaves Differently in Uptrends</h4>
        <p style={{ ...bt, fontSize:'13px' }}>In a strong uptrend, RSI rarely drops below 30. The oversold zone shifts up. Watch for RSI to find support around 40–50 and bounce. That is your signal — not a reading below 30. Waiting for 30 in a strong uptrend means waiting too long.</p>
      </div>

      <h3 style={{ ...ss, fontSize:'20px', fontWeight:400, color:V.gold, margin:'0 0 10px' }}>The Combined Signal — MA + RSI Together</h3>
      <p style={{ ...bt, fontSize:'13px', marginBottom:'14px' }}>The highest-probability CSP entry happens when two conditions align: price touches or pierces the 20-day MA (from above), AND RSI is approaching or below 40 in an uptrend (or below 30 in a neutral market).</p>
      <canvas ref={comboRef} width={520} height={200} style={{ width:'100%', maxWidth:'520px', display:'block', borderRadius:'8px', border:'0.5px solid rgba(122,171,138,0.2)', marginBottom:'8px' }} />
      <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'11px', color:V.dim, marginBottom:'20px', textAlign:'center' }}>Price panel (top) + RSI panel (bottom). The highlighted zone shows both conditions aligned — the combined signal.</p>

      <div style={{ background:'rgba(122,171,138,0.06)', border:`0.5px solid rgba(122,171,138,0.25)`, borderRadius:'8px', padding:'16px 20px', marginBottom:'20px' }}>
        <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color:V.sage, marginBottom:'8px', letterSpacing:'1px' }}>THE TWO-CONDITION RULE</div>
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {['Price touches or pierces the 20-day MA from above (rubber band stretched DOWN)','RSI is at or below 40 in an uptrend, or below 30 in a neutral market'].map((c,i)=>(
            <div key={i} style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
              <span style={{ color:V.sage, flexShrink:0, fontFamily:"'DM Mono',monospace", fontSize:'12px' }}>{i+1}.</span>
              <span style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:'rgba(240,237,230,0.85)', lineHeight:1.6 }}>{c}</span>
            </div>
          ))}
        </div>
        <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, fontStyle:'italic', lineHeight:1.6, margin:'10px 0 0' }}>When both conditions are met, you have a rubber band stretched in your favor. This is when you sell a put.</p>
      </div>

      {onNavigate && (
        <button style={{ ...btnGold, padding:'10px 20px', fontSize:'13px' }} onClick={()=>onNavigate('practice_rsi')}>
          Practice RSI →
        </button>
      )}
    </div>
  );
}

function Ch05DeltaTabVisual() {
  const j = "'Jost',sans-serif";
  const bullet = (color, items) => items.map((t,i) => (
    <div key={i} style={{ display:'flex', gap:'8px', marginBottom:'6px' }}>
      <span style={{ color, flexShrink:0 }}>●</span>
      <span style={{ fontFamily:j, fontSize:'13px', color:V.muted, lineHeight:1.6 }}>{t}</span>
    </div>
  ));
  return (
    <div style={{ marginTop:'20px', display:'flex', flexDirection:'column', gap:'12px' }}>
      <div style={{ background:V.bg3, border:`0.5px solid rgba(201,168,76,0.2)`, borderRadius:'8px', padding:'14px 16px' }}>
        <div style={{ fontFamily:j, fontSize:'10px', color:V.gold, textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:'10px' }}>WHAT IT MEASURES</div>
        {bullet(V.gold, ["Measures how much an option's price changes for every $1 the stock moves","Delta of 0.50 = option moves $0.50 per $1 stock move","Delta of 0.15 = option moves $0.15 per $1 stock move"])}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
        <div style={{ background:V.bg3, border:`0.5px solid rgba(74,158,255,0.25)`, borderRadius:'8px', padding:'14px' }}>
          <div style={{ fontFamily:j, fontSize:'10px', color:'#4a9eff', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'8px' }}>CALLS</div>
          <div style={{ fontFamily:j, fontSize:'12px', color:V.muted, lineHeight:1.7 }}>Delta is POSITIVE (0 to 1.0)<br />ATM call: ~0.50<br />Deep ITM call: ~1.0<br />OTM call: closer to 0</div>
        </div>
        <div style={{ background:V.bg3, border:`0.5px solid rgba(196,122,122,0.25)`, borderRadius:'8px', padding:'14px' }}>
          <div style={{ fontFamily:j, fontSize:'10px', color:V.rose, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'8px' }}>PUTS</div>
          <div style={{ fontFamily:j, fontSize:'12px', color:V.muted, lineHeight:1.7 }}>Delta is NEGATIVE (0 to -1.0)<br />ATM put: ~-0.50<br />Deep ITM put: ~-1.0<br />OTM put: closer to 0</div>
        </div>
      </div>
      <div style={{ background:V.bg3, border:`1px solid rgba(201,168,76,0.4)`, borderRadius:'8px', padding:'16px' }}>
        <div style={{ fontFamily:j, fontSize:'13px', color:V.gold, fontWeight:700, marginBottom:'10px' }}>Delta = Probability</div>
        <div style={{ fontFamily:j, fontSize:'12px', color:V.muted, marginBottom:'8px' }}>A 0.15 delta put has approximately:</div>
        {bullet(V.gold, ["15% probability of expiring IN the money (assignment risk)","85% probability of expiring WORTHLESS (you keep the premium)"])}
        <p style={{ fontFamily:j, fontSize:'12px', color:V.muted, marginTop:'8px', lineHeight:1.7 }}>This is why this program targets 0.15 delta for CSP entries.</p>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px' }}>
        {[
          { label:'Conservative\n(-0.10)', color:V.sage, items:['Less premium','~90% probability','More cushion'], hl:false },
          { label:'Target ★\n(-0.15)', color:V.gold, items:['Balanced premium','~85% probability','Good cushion'], hl:true },
          { label:'Aggressive\n(-0.30)', color:V.rose, items:['More premium','~70% probability','Less cushion'], hl:false },
        ].map(({label,color,items,hl})=>(
          <div key={label} style={{ background:hl?'rgba(201,168,76,0.05)':V.bg3, border:`0.5px solid ${color}40`, borderRadius:'8px', padding:'12px' }}>
            <div style={{ fontFamily:j, fontSize:'10px', color, letterSpacing:'0.06em', marginBottom:'8px', whiteSpace:'pre-line' }}>{label}</div>
            {items.map((item,i)=><div key={i} style={{ fontFamily:j, fontSize:'11px', color:V.muted, lineHeight:1.6 }}>{item}</div>)}
          </div>
        ))}
      </div>
    </div>
  );
}

function Ch05ThetaTabVisual() {
  const j = "'Jost',sans-serif";
  const bullet = (color, items) => items.map((t,i) => (
    <div key={i} style={{ display:'flex', gap:'8px', marginBottom:'6px' }}>
      <span style={{ color, flexShrink:0 }}>●</span>
      <span style={{ fontFamily:j, fontSize:'13px', color:V.muted, lineHeight:1.6 }}>{t}</span>
    </div>
  ));
  return (
    <div style={{ marginTop:'20px', display:'flex', flexDirection:'column', gap:'12px' }}>
      <div style={{ background:V.bg3, border:`0.5px solid rgba(201,168,76,0.2)`, borderRadius:'8px', padding:'14px 16px' }}>
        <div style={{ fontFamily:j, fontSize:'10px', color:V.gold, textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:'10px' }}>WHAT IT MEASURES</div>
        {bullet(V.gold, ["Measures how much an option loses in value each day","If theta = -0.05: option loses $0.05 per share per day = $5 per contract per day"])}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
        <div style={{ background:V.bg3, borderLeft:`3px solid ${V.rose}`, borderRadius:'0 8px 8px 0', padding:'14px' }}>
          <div style={{ fontFamily:j, fontSize:'10px', color:V.rose, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'8px' }}>BUYERS</div>
          <div style={{ fontFamily:j, fontSize:'12px', color:V.muted, lineHeight:1.7 }}>Theta is the ENEMY<br />Every day costs them money<br />Time works against them</div>
        </div>
        <div style={{ background:'rgba(122,171,138,0.06)', border:`0.5px solid rgba(122,171,138,0.3)`, borderRadius:'8px', padding:'14px' }}>
          <div style={{ fontFamily:j, fontSize:'10px', color:V.sage, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'8px' }}>SELLERS ✓</div>
          <div style={{ fontFamily:j, fontSize:'12px', color:V.muted, lineHeight:1.7 }}>Theta is the INCOME ENGINE<br />Every day puts money in pocket<br />Time works FOR them</div>
        </div>
      </div>
      <div style={{ background:'rgba(122,171,138,0.06)', border:`0.5px solid rgba(122,171,138,0.25)`, borderRadius:'8px', padding:'16px' }}>
        <div style={{ fontFamily:j, fontSize:'10px', color:V.sage, textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:'10px' }}>THE 30-45 DAY SWEET SPOT</div>
        {bullet(V.sage, ["Options lose value FASTEST in the final 2 weeks","But risk also increases near expiration","30-45 DTE captures efficient theta decay","Close at 50% profit — you have collected the best portion of theta"])}
      </div>
    </div>
  );
}

function Ch05VegaTabVisual() {
  const j = "'Jost',sans-serif";
  const bt = { fontFamily:j, fontSize:'13px', color:'rgba(240,237,230,0.82)', lineHeight:1.85, margin:0 };
  const bullet = (color, items) => items.map((t,i) => (
    <div key={i} style={{ display:'flex', gap:'8px', marginBottom:'6px' }}>
      <span style={{ color, flexShrink:0 }}>●</span>
      <span style={{ fontFamily:j, fontSize:'13px', color:V.muted, lineHeight:1.6 }}>{t}</span>
    </div>
  ));
  return (
    <div style={{ marginTop:'20px', display:'flex', flexDirection:'column', gap:'12px' }}>
      <div style={{ background:V.bg3, border:`0.5px solid rgba(201,168,76,0.2)`, borderRadius:'8px', padding:'14px 16px' }}>
        <div style={{ fontFamily:j, fontSize:'10px', color:V.gold, textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:'10px' }}>WHAT IT MEASURES</div>
        {bullet(V.gold, ["Measures how much an option's price changes when IV changes 1%","High IV = expensive options = more premium to collect","Low IV = cheap options = less premium available"])}
      </div>
      <div style={{ background:V.bg3, border:`0.5px solid rgba(201,168,76,0.2)`, borderRadius:'8px', padding:'14px 16px' }}>
        <div style={{ fontFamily:j, fontSize:'10px', color:V.gold, textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:'10px' }}>SELLERS STRATEGY</div>
        {bullet(V.gold, ["Sell when IV is ELEVATED","Buy back when IV has DECLINED","Premium deflates — take profit","This is vega working FOR you"])}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px' }}>
        {[
          { vix:'VIX < 15', color:V.muted, note:'Low IV — options are cheap. Less premium. Consider waiting.' },
          { vix:'VIX 15–25', color:V.gold, note:'Normal IV — solid environment. Standard CSP sizing.' },
          { vix:'VIX > 25', color:V.rose, note:'Elevated IV — rich premium. More risk too. Size down.' },
        ].map(({vix,color,note})=>(
          <div key={vix} style={{ background:V.bg2, border:`0.5px solid ${color}30`, borderRadius:'8px', padding:'12px' }}>
            <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color, marginBottom:'4px' }}>{vix}</div>
            <p style={{ ...bt, fontSize:'12px', lineHeight:1.5 }}>{note}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Ch05GammaTabVisual() {
  const j = "'Jost',sans-serif";
  const bt = { fontFamily:j, fontSize:'13px', color:'rgba(240,237,230,0.82)', lineHeight:1.85, margin:0 };
  const bullet = (color, items) => items.map((t,i) => (
    <div key={i} style={{ display:'flex', gap:'8px', marginBottom:'6px' }}>
      <span style={{ color, flexShrink:0 }}>●</span>
      <span style={{ fontFamily:j, fontSize:'13px', color:V.muted, lineHeight:1.6 }}>{t}</span>
    </div>
  ));
  return (
    <div style={{ marginTop:'20px', display:'flex', flexDirection:'column', gap:'12px' }}>
      <div style={{ background:V.bg3, border:`0.5px solid rgba(201,168,76,0.2)`, borderRadius:'8px', padding:'14px 16px' }}>
        <div style={{ fontFamily:j, fontSize:'10px', color:V.gold, textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:'10px' }}>WHAT IT MEASURES</div>
        {bullet(V.gold, ["Measures how fast Delta is changing","High gamma = Delta changing rapidly","Low gamma = Delta changing slowly"])}
      </div>
      <div style={{ background:V.bg3, border:`0.5px solid rgba(196,122,122,0.25)`, borderRadius:'8px', padding:'14px 16px' }}>
        <div style={{ fontFamily:j, fontSize:'10px', color:V.rose, textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:'10px' }}>WHEN GAMMA IS HIGHEST</div>
        {bullet(V.rose, ["At-the-money options","Close to expiration (0-7 days)","During high volatility events"])}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
        <div style={{ background:'rgba(122,171,138,0.06)', border:`0.5px solid rgba(122,171,138,0.25)`, borderRadius:'8px', padding:'14px' }}>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color:V.sage, marginBottom:'6px' }}>30–45 DAYS (LOW GAMMA)</div>
          <p style={{ ...bt, fontSize:'12px', lineHeight:1.6 }}>Delta changes slowly. Position is stable. Manageable. This is where beginners operate.</p>
        </div>
        <div style={{ background:V.bg2, borderLeft:`3px solid ${V.rose}`, borderRadius:'0 8px 8px 0', padding:'14px' }}>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color:V.rose, marginBottom:'6px' }}>0–7 DAYS (HIGH GAMMA)</div>
          <p style={{ ...bt, fontSize:'12px', lineHeight:1.6 }}>Delta can change rapidly. Small moves = large value changes. Requires active management.</p>
        </div>
      </div>
      <div style={{ background:V.bg3, border:`0.5px solid rgba(201,168,76,0.2)`, borderRadius:'8px', padding:'14px 16px' }}>
        <div style={{ fontFamily:j, fontSize:'10px', color:V.gold, textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:'10px' }}>WHY THIS MATTERS FOR SELLERS</div>
        {bullet(V.gold, ["30-45 DTE = low gamma zone — manageable and predictable","Your position changes slowly — you have time to react","NEVER trade 0DTE in Tier 1 — gamma risk requires experience"])}
      </div>
    </div>
  );
}

function Ch05WhyGreeksVisual() {
  const GREEKS = [
    { sym: 'Δ', name: 'DELTA',  borderColor: V.gold,  textColor: V.gold,  body: 'How the option responds to stock price movement' },
    { sym: 'Θ', name: 'THETA',  borderColor: V.sage,  textColor: V.sage,  body: 'The time remaining until expiration' },
    { sym: 'V', name: 'VEGA',   borderColor: '#4a9eff', textColor: '#4a9eff', body: 'The volatility of the market' },
    { sym: 'Γ', name: 'GAMMA',  borderColor: V.rose,  textColor: V.rose,  body: "How fast Delta itself is changing" },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '20px' }}>
      {GREEKS.map(({ sym, name, borderColor, textColor, body }) => (
        <div key={name} style={{ background: V.bg3, border: `1px solid ${borderColor}40`, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '28px', color: textColor, fontWeight: 400, lineHeight: 1, marginBottom: '6px' }}>{sym}</div>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: textColor, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, marginBottom: '8px' }}>{name}</div>
          <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.65, margin: 0 }}>{body}</p>
        </div>
      ))}
    </div>
  );
}

function Ch05GreeksVisual({ onNavigate, onPractice }) {
  const thetaRef = useRef(null);
  const deltaRef = useRef(null);

  useEffect(() => {
    // Theta decay curve
    const drawTheta = () => {
      const cv = thetaRef.current; if (!cv) return;
      const ctx = cv.getContext('2d'), W = cv.width, H = cv.height;
      ctx.fillStyle = 'rgba(12,10,8,0.97)'; ctx.fillRect(0,0,W,H);
      const pad={l:44,r:24,t:20,b:36}, gW=W-pad.l-pad.r, gH=H-pad.t-pad.b;
      const N = 46; // 45 days to 0
      const px = i => pad.l + (i/(N-1))*gW;
      // Theta decay is slow early, accelerates near expiration
      const vals = Array.from({length:N},(_,i) => {
        const daysLeft = 45 - i;
        return Math.sqrt(daysLeft/45);
      });
      const py = v => pad.t + gH*(1-v);
      // Shaded "sweet spot" 30-45 DTE (first 15 points) in sage
      ctx.fillStyle = 'rgba(122,171,138,0.08)';
      ctx.fillRect(px(0), pad.t, px(14)-px(0), gH);
      ctx.strokeStyle = 'rgba(122,171,138,0.3)'; ctx.lineWidth=0.5; ctx.setLineDash([3,3]);
      ctx.beginPath();ctx.moveTo(px(15),pad.t);ctx.lineTo(px(15),pad.t+gH);ctx.stroke();
      ctx.setLineDash([]);
      // Accelerating decay zone (final 14 days) in rose
      ctx.fillStyle='rgba(196,122,122,0.06)';
      ctx.fillRect(px(31),pad.t,px(45)-px(31),gH);
      ctx.strokeStyle='rgba(196,122,122,0.3)';ctx.lineWidth=0.5;ctx.setLineDash([3,3]);
      ctx.beginPath();ctx.moveTo(px(31),pad.t);ctx.lineTo(px(31),pad.t+gH);ctx.stroke();
      ctx.setLineDash([]);
      // Grid
      ctx.strokeStyle='rgba(201,168,76,0.06)';ctx.lineWidth=0.5;
      for(let i=1;i<4;i++){ctx.beginPath();ctx.moveTo(pad.l,pad.t+gH*i/4);ctx.lineTo(pad.l+gW,pad.t+gH*i/4);ctx.stroke();}
      // Theta decay curve
      ctx.beginPath();ctx.strokeStyle='#c9a84c';ctx.lineWidth=2.5;
      vals.forEach((v,i)=>i===0?ctx.moveTo(px(i),py(v)):ctx.lineTo(px(i),py(v)));ctx.stroke();
      // X-axis labels
      ctx.font='8px "DM Mono",monospace';ctx.fillStyle='rgba(240,237,230,0.35)';ctx.textAlign='center';
      [[0,'45 DTE'],[15,'30 DTE'],[31,'14 DTE'],[45,'0']].forEach(([i,lbl])=>ctx.fillText(lbl,px(i),H-8));
      // Y-axis label
      ctx.save();ctx.translate(14,pad.t+gH/2);ctx.rotate(-Math.PI/2);
      ctx.textAlign='center';ctx.font='8px "DM Mono",monospace';ctx.fillStyle='rgba(240,237,230,0.25)';
      ctx.fillText('Option value remaining',0,0);ctx.restore();
      // Zone labels
      ctx.font='8px "Jost",sans-serif';ctx.fillStyle='rgba(122,171,138,0.7)';ctx.textAlign='center';
      ctx.fillText('Sweet spot',px(7),pad.t+12);
      ctx.fillStyle='rgba(196,122,122,0.7)';
      ctx.fillText('Rapid decay',px(38),pad.t+12);
    };
    // Delta probability bar
    const drawDelta = () => {
      const cv = deltaRef.current; if (!cv) return;
      const ctx = cv.getContext('2d'), W = cv.width, H = cv.height;
      ctx.fillStyle='rgba(12,10,8,0.97)';ctx.fillRect(0,0,W,H);
      const pad={l:32,r:32,t:28,b:36}, gW=W-pad.l-pad.r, gH=H-pad.t-pad.b;
      // Zones: OTM (0-0.30), ATM (0.30-0.70), ITM (0.70-1.0)
      const zones=[{from:0,to:0.30,color:'rgba(122,171,138,0.12)',label:'OTM'},{from:0.30,to:0.70,color:'rgba(201,168,76,0.1)',label:'ATM'},{from:0.70,to:1.0,color:'rgba(196,122,122,0.12)',label:'ITM'}];
      zones.forEach(z=>{
        const x1=pad.l+z.from*gW, x2=pad.l+z.to*gW;
        ctx.fillStyle=z.color;ctx.fillRect(x1,pad.t,x2-x1,gH);
        ctx.font='9px "Jost",sans-serif';ctx.textAlign='center';
        ctx.fillStyle=z.color.includes('171')?'rgba(122,171,138,0.7)':z.color.includes('168')?'rgba(201,168,76,0.6)':'rgba(196,122,122,0.7)';
        ctx.fillText(z.label,(x1+x2)/2,pad.t+14);
      });
      // Axis line
      ctx.strokeStyle='rgba(240,237,230,0.15)';ctx.lineWidth=0.5;
      ctx.beginPath();ctx.moveTo(pad.l,pad.t+gH);ctx.lineTo(pad.l+gW,pad.t+gH);ctx.stroke();
      // Tick marks and labels
      [0,0.15,0.30,0.50,0.70,1.0].forEach(v=>{
        const x=pad.l+v*gW;
        ctx.strokeStyle='rgba(240,237,230,0.2)';ctx.lineWidth=0.5;
        ctx.beginPath();ctx.moveTo(x,pad.t);ctx.lineTo(x,pad.t+gH);ctx.stroke();
        ctx.font='8px "DM Mono",monospace';ctx.fillStyle='rgba(240,237,230,0.4)';ctx.textAlign='center';
        ctx.fillText(v.toFixed(2),x,pad.t+gH+14);
      });
      // CSP target marker at 0.15
      const markerX = pad.l + 0.15*gW;
      ctx.fillStyle='rgba(122,171,138,0.2)';ctx.fillRect(markerX-1,pad.t,2,gH);
      ctx.strokeStyle='rgba(122,171,138,0.8)';ctx.lineWidth=2;
      ctx.beginPath();ctx.moveTo(markerX,pad.t);ctx.lineTo(markerX,pad.t+gH);ctx.stroke();
      ctx.font='bold 9px "Jost",sans-serif';ctx.fillStyle='rgba(122,171,138,0.9)';ctx.textAlign='center';
      ctx.fillText('▼ CSP target',markerX,pad.t-4);
      ctx.fillText('0.15 delta',markerX,pad.t+gH+26);
      ctx.fillText('~85% OTM',markerX,pad.t+gH+36);
      // Axis label
      ctx.font='8px "DM Mono",monospace';ctx.fillStyle='rgba(240,237,230,0.2)';ctx.textAlign='center';
      ctx.fillText('Delta (absolute value) →',pad.l+gW/2,H-4);
    };
    drawTheta(); drawDelta();
  }, []);

  const ss = { fontFamily:"'Cormorant Garamond',Georgia,serif" };
  const bt = { fontFamily:"'Jost',sans-serif", fontSize:'14px', color:'rgba(240,237,230,0.82)', lineHeight:1.85, margin:0 };
  const divider = { height:'0.5px', background:'rgba(201,168,76,0.12)', margin:'28px 0' };

  return (
    <div style={{ marginTop:'20px' }}>

      {/* Example position card */}
      <div style={{ background:V.bg3, border:`0.5px solid rgba(201,168,76,0.2)`, borderRadius:'10px', padding:'20px', marginBottom:'20px' }}>
        <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'10px', color:V.gold, textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:'12px' }}>EXAMPLE POSITION</div>
        {[['Stock','AAPL at $187'],['Position','Sold $165 Put'],['Expiration','30 days'],['Premium collected','$0.78 per share']].map(([label,value])=>(
          <div key={label} style={{ display:'flex', justifyContent:'space-between', borderBottom:'0.5px solid rgba(201,168,76,0.08)', paddingBottom:'6px', marginBottom:'6px' }}>
            <span style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.dim }}>{label}</span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'12px', color:V.text }}>{value}</span>
          </div>
        ))}
      </div>

      {/* 4 Greek reading cards */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'24px' }}>
        {[
          { sym:'Δ -0.15', name:'Delta', border:V.gold, color:V.gold, body:'15% probability of assignment\n85% probability expiring worthless\nStrike is well OTM — this is your cushion' },
          { sym:'Θ -0.03', name:'Theta', border:V.sage, color:V.sage, body:'Loses $0.03/share per day\n= $3 per contract per day\nworking in your favor' },
          { sym:'V 0.08', name:'Vega', border:'#4a9eff', color:'#4a9eff', body:'If IV drops 1%: option loses $0.08\nIf IV rises 1%: option gains $0.08\nMonitor IV when entering' },
          { sym:'Γ 0.02', name:'Gamma', border:V.rose, color:V.rose, body:'Low gamma at 30 days\nDelta is changing slowly\nPosition is stable and manageable' },
        ].map(({sym,name,border,color,body})=>(
          <div key={name} style={{ background:V.bg2, border:`1px solid ${border}25`, borderTop:`2px solid ${border}`, borderRadius:'8px', padding:'14px' }}>
            <div style={{ ...ss, fontSize:'22px', color, fontWeight:400, lineHeight:1, marginBottom:'4px' }}>{sym}</div>
            <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'10px', color:V.gold, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'8px' }}>{name}</div>
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, lineHeight:1.7, margin:0, whiteSpace:'pre-line' }}>{body}</p>
          </div>
        ))}
      </div>

      {/* 15-day table */}
      <div style={{ marginBottom:'28px' }}>
        <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'10px', color:V.gold, textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:'10px' }}>WHAT HAPPENS OVER 15 DAYS</div>
        <div style={{ background:V.bg2, border:`0.5px solid ${V.border}`, borderRadius:'8px', overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'70px 80px 110px 1fr', padding:'6px 14px', background:V.bg3, borderBottom:`0.5px solid ${V.border}` }}>
            {['DAY','PREMIUM','THETA COLL.','STATUS'].map(h=>(
              <span key={h} style={{ fontFamily:"'Jost',sans-serif", fontSize:'9px', color:V.dim, textTransform:'uppercase', letterSpacing:'0.08em' }}>{h}</span>
            ))}
          </div>
          {[{d:0,p:'$0.78',t:'—',s:'Entry',hl:false},{d:5,p:'$0.63',t:'$0.15',s:'Holding',hl:false},{d:10,p:'$0.48',t:'$0.30',s:'Holding',hl:false},{d:15,p:'$0.39',t:'$0.39',s:'⭐ Close',hl:true}].map(row=>(
            <div key={row.d} style={{ display:'grid', gridTemplateColumns:'70px 80px 110px 1fr', padding:'9px 14px', borderBottom:`0.5px solid ${V.bg3}`, background:row.hl?'rgba(122,171,138,0.08)':'transparent', borderLeft:`3px solid ${row.hl?V.sage:'transparent'}` }}>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'12px', color:row.hl?V.sage:V.muted }}>Day {row.d}</span>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'12px', color:V.gold }}>{row.p}</span>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'12px', color:V.sage }}>{row.t}</span>
              <span style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:row.hl?V.sage:V.dim }}>{row.s}</span>
            </div>
          ))}
        </div>
        <div style={{ background:V.bg2, borderLeft:`3px solid ${V.gold}`, borderRadius:'0 8px 8px 0', padding:'14px', marginTop:'12px' }}>
          <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:0 }}>
            Net profit: $0.78 − $0.39 = $0.39 per share ($39/contract)<br /><br />
            Do not wait for full expiration. The remaining premium represents disproportionate risk for diminishing reward. Take the win. Reset. Repeat.
          </p>
        </div>
      </div>

      {/* 4 Greek overview cards */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'24px' }}>
        {[
          { sym:'Δ', name:'DELTA', color:V.gold, tagline:'Your probability gauge', desc:'How much the option price moves for every $1 the stock moves' },
          { sym:'Θ', name:'THETA', color:V.sage, tagline:'Your income engine', desc:'How much the option loses in value every day that passes' },
          { sym:'V', name:'VEGA', color:V.gold, tagline:'Your volatility sensor', desc:'How much the option changes when volatility changes' },
          { sym:'Γ', name:'GAMMA', color:V.rose, tagline:'Your risk warning', desc:'How fast Delta is changing — highest near expiration' },
        ].map(({sym,name,color,tagline,desc})=>(
          <div key={name} style={{ background:V.bg2, border:`0.5px solid ${color}30`, borderRadius:'10px', padding:'16px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
              <span style={{ ...ss, fontSize:'24px', color, fontWeight:400 }}>{sym}</span>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color, letterSpacing:'1px' }}>{name}</span>
            </div>
            <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color, marginBottom:'4px', fontWeight:600 }}>{tagline}</div>
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, lineHeight:1.5, margin:0 }}>{desc}</p>
          </div>
        ))}
      </div>

      {/* Delta section */}
      <div style={divider} />
      <h3 style={{ ...ss, fontSize:'20px', fontWeight:400, color:V.gold, margin:'0 0 10px' }}>Delta — Δ — Probability Gauge</h3>
      <p style={{ ...bt, marginBottom:'14px', fontSize:'13px' }}>Delta approximates the probability that an option will expire in the money. A 0.15 delta put has approximately 15% probability of expiring in the money — and 85% probability of expiring worthless.</p>
      <canvas ref={deltaRef} width={520} height={90} style={{ width:'100%', maxWidth:'520px', display:'block', borderRadius:'8px', border:'0.5px solid rgba(120,110,90,0.2)', marginBottom:'8px' }} />
      <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'11px', color:V.dim, textAlign:'center', marginBottom:'16px' }}>The 0.15 delta target gives approximately 85% probability of keeping the full premium.</p>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
        <div style={{ background:V.bg2, borderLeft:`3px solid ${V.rose}`, borderRadius:'0 8px 8px 0', padding:'12px 14px' }}>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color:V.rose, marginBottom:'4px' }}>HIGHER DELTA (0.30)</div>
          <p style={{ ...bt, fontSize:'12px', lineHeight:1.6 }}>More premium ✓ · Higher assignment risk ✗ · Less cushion ✗</p>
        </div>
        <div style={{ background:'rgba(122,171,138,0.06)', border:`0.5px solid rgba(122,171,138,0.25)`, borderRadius:'8px', padding:'12px 14px' }}>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color:V.sage, marginBottom:'4px' }}>TARGET DELTA (0.15)</div>
          <p style={{ ...bt, fontSize:'12px', lineHeight:1.6 }}>Less premium ✗ · Lower assignment risk ✓ · More cushion ✓</p>
        </div>
      </div>

      {/* Theta section */}
      <div style={divider} />
      <h3 style={{ ...ss, fontSize:'20px', fontWeight:400, color:V.gold, margin:'0 0 10px' }}>Theta — Θ — Time Decay Engine</h3>
      <p style={{ ...bt, marginBottom:'14px', fontSize:'13px' }}>Theta is how much an option loses in value each day. For sellers, every day that passes puts money in your pocket. Time always passes. Theta never stops working.</p>
      <canvas ref={thetaRef} width={520} height={160} style={{ width:'100%', maxWidth:'520px', display:'block', borderRadius:'8px', border:'0.5px solid rgba(120,110,90,0.2)', marginBottom:'6px' }} />
      <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'11px', color:V.muted, fontStyle:'italic', textAlign:'center', padding:'0 16px', marginBottom:'14px' }}>Option value remaining over time. Decay accelerates in the final 2 weeks — exit at 50% profit to capture efficient theta.</p>
      <div style={{ background:'rgba(122,171,138,0.06)', border:`0.5px solid rgba(122,171,138,0.25)`, borderRadius:'8px', padding:'14px 18px' }}>
        <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color:V.sage, marginBottom:'6px' }}>THE 50% PROFIT RULE</div>
        <p style={{ ...bt, fontSize:'13px' }}>When your option reaches 50% of the original premium, close it. You have collected the efficient theta. The remaining premium represents disproportionate risk. Take the win. Reset. Repeat.</p>
      </div>

      {/* Vega section */}
      <div style={divider} />
      <h3 style={{ ...ss, fontSize:'20px', fontWeight:400, color:V.gold, margin:'0 0 10px' }}>Vega — V — Volatility Sensor</h3>
      <p style={{ ...bt, marginBottom:'14px', fontSize:'13px' }}>Vega measures how much an option's price changes when implied volatility changes by 1%. High IV = rich premium. Sell when IV is elevated. Buy back when IV has collapsed.</p>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px', marginBottom:'14px' }}>
        {[
          { vix:'VIX < 15', color:V.muted, note:'Low IV — options are cheap. Less premium. Consider waiting.' },
          { vix:'VIX 15–25', color:V.gold, note:'Normal IV — solid environment. Standard CSP sizing.' },
          { vix:'VIX > 25', color:V.rose, note:'Elevated IV — rich premium. More risk too. Size down.' },
        ].map(({vix,color,note})=>(
          <div key={vix} style={{ background:V.bg2, border:`0.5px solid ${color}30`, borderRadius:'8px', padding:'12px' }}>
            <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color, marginBottom:'4px' }}>{vix}</div>
            <p style={{ ...bt, fontSize:'12px', lineHeight:1.5 }}>{note}</p>
          </div>
        ))}
      </div>

      {/* Gamma section */}
      <div style={divider} />
      <h3 style={{ ...ss, fontSize:'20px', fontWeight:400, color:V.gold, margin:'0 0 10px' }}>Gamma — Γ — Risk Awareness</h3>
      <p style={{ ...bt, marginBottom:'14px', fontSize:'13px' }}>Gamma measures how fast Delta is changing. High gamma means your option's delta — and value — can change rapidly. It is highest for at-the-money options close to expiration.</p>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
        <div style={{ background:'rgba(122,171,138,0.06)', border:`0.5px solid rgba(122,171,138,0.25)`, borderRadius:'8px', padding:'14px' }}>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color:V.sage, marginBottom:'6px' }}>30–45 DAYS (LOW GAMMA)</div>
          <p style={{ ...bt, fontSize:'12px', lineHeight:1.6 }}>Delta changes slowly. Position is stable. Manageable. This is where beginners operate.</p>
        </div>
        <div style={{ background:V.bg2, borderLeft:`3px solid ${V.rose}`, borderRadius:'0 8px 8px 0', padding:'14px' }}>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'10px', color:V.rose, marginBottom:'6px' }}>0–7 DAYS (HIGH GAMMA)</div>
          <p style={{ ...bt, fontSize:'12px', lineHeight:1.6 }}>Delta can change rapidly. Small moves = large value changes. Requires active management.</p>
        </div>
      </div>

      {onNavigate && (
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginTop:'28px' }}>
          <button style={{ ...btnGold, padding:'10px 20px', fontSize:'13px' }} onClick={() => onNavigate('practice_greeks')}>
            Practice the Greeks →
          </button>
          <button style={{ ...btnGhost, padding:'10px 18px', fontSize:'13px' }} onClick={() => onNavigate('practice_greeks')}>
            Apply the Greeks →
          </button>
        </div>
      )}
    </div>
  );
}

function Ch04ConfluenceVisual({ onNavigate, onPractice }) {
  const ss = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
  const b = { fontSize: '14px', color: 'rgba(240,237,230,0.82)', fontFamily: "'Jost',sans-serif", lineHeight: 1.85, margin: 0 };
  const hr = { height: '0.5px', background: 'rgba(201,168,76,0.12)', margin: '28px 0' };
  return (
    <div style={{ position: 'relative', overflow: 'visible', height: 'auto', display: 'block' }}>
      {/* Eyebrow + Title + Subtitle */}
      <div style={{ fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: V.gold, fontFamily: "'Jost',sans-serif", marginBottom: '8px', marginTop: '4px' }}>PUTTING IT ALL TOGETHER</div>
      <h2 style={{ ...ss, fontSize: '32px', fontWeight: 400, color: V.gold, margin: '0 0 6px' }}>The Confluence</h2>
      <p style={{ ...ss, fontSize: '18px', fontStyle: 'italic', color: V.muted, marginBottom: '28px', lineHeight: 1.5 }}>When everything points to the same answer.</p>

      {/* Opening card */}
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 10px 10px 0', padding: '20px 24px', marginBottom: '28px' }}>
        <p style={{ ...b, marginBottom: '12px' }}>Every indicator in this program measures the rubber band from a different angle.</p>
        <p style={{ ...b, marginBottom: '12px' }}><strong style={{ color: V.text }}>Structure</strong> — support zones, resistance zones, supply and demand — shows you <em>where</em> the rubber band has anchored before and is likely to anchor again.</p>
        <p style={{ ...b, marginBottom: '12px' }}><strong style={{ color: V.text }}>Momentum</strong> — RSI, moving averages — shows you <em>how stretched</em> the rubber band is right now and whether it has conviction behind it.</p>
        <p style={{ ...b, marginBottom: '12px' }}>A setup with structure confirmation alone is interesting. A setup with momentum confirmation alone is interesting. A setup with <strong style={{ color: V.goldL }}>BOTH aligned at the same level</strong> — that is the confluence. That is when you act with conviction.</p>
        <p style={{ ...b }}>Professional traders wait for multiple independent signals pointing to the same conclusion before committing capital. This is not over-complicating trading. This is letting the market speak clearly before you respond.</p>
      </div>

      {/* Three Question Framework */}
      <div style={{ background: V.bg3, border: `1px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '20px', marginBottom: '28px' }}>
        <h3 style={{ ...ss, fontSize: '22px', fontWeight: 400, color: V.gold, margin: '0 0 6px' }}>The Three Question Framework</h3>
        <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginBottom: '18px', lineHeight: 1.6 }}>Ask these three questions before every trade. When all three point the same direction — that is your confluence.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[
            { n: '1', title: 'WHERE IS THE STRUCTURE?', body: 'Is there a support or demand zone near my target strike? Has price respected this level before? Is there significant OI at this level?' },
            { n: '2', title: 'WHAT IS MOMENTUM SAYING?', body: 'Is RSI showing the rubber band is stretched in my favor? Are the moving averages confirming the trend direction? Is volume confirming the move?' },
            { n: '3', title: 'ARE THEY ALIGNED?', body: 'If structure and momentum point to the same conclusion — that is your confluence. That is when you act. If they conflict — wait.' },
          ].map(({ n, title, body }) => (
            <div key={n} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 8px 8px 0', padding: '14px 16px' }}>
              <div style={{ ...ss, fontSize: '22px', color: V.gold, fontWeight: 400, lineHeight: 1, flexShrink: 0 }}>{n}</div>
              <div>
                <div style={{ fontSize: '11px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.08em', marginBottom: '5px' }}>{title}</div>
                <p style={{ ...b, fontSize: '13px', lineHeight: 1.7 }}>{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={hr} />

      {/* High Confluence Example */}
      <div style={{ background: V.bg2, border: `1px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '20px', marginBottom: '14px' }}>
        <div style={{ display: 'inline-block', background: 'rgba(122,171,138,0.12)', border: `0.5px solid rgba(122,171,138,0.4)`, borderRadius: '4px', padding: '2px 8px', fontSize: '10px', color: V.sage, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>HIGH CONFLUENCE ✓</div>
        <div style={{ fontSize: '13px', color: V.goldL, fontFamily: "'Jost',sans-serif", fontWeight: 600, marginBottom: '12px' }}>Example — Four Signals Aligned</div>
        <div style={{ ...b, fontSize: '13px', lineHeight: 1.9 }}>
          <div style={{ marginBottom: '4px' }}>Stock is at $165.</div>
          <div style={{ color: V.sage, marginBottom: '3px' }}>✓ Support zone confirmed at $162–165 (price bounced here twice before)</div>
          <div style={{ color: V.sage, marginBottom: '3px' }}>✓ RSI is at 33 and turning up (oversold — momentum shifting)</div>
          <div style={{ color: V.sage, marginBottom: '3px' }}>✓ 20 day MA at $163 pointing up (trend intact)</div>
          <div style={{ color: V.sage, marginBottom: '12px' }}>✓ Put OI at $160 strike: 1,850 (institutional positioning below)</div>
          <div style={{ color: V.muted, marginBottom: '4px' }}>Four independent signals. All pointing to the same conclusion: buyers are likely to defend this level.</div>
          <div style={{ color: V.goldL, fontWeight: 500 }}>CSP at $160 — below the support zone. This is the confluence.</div>
        </div>
      </div>

      {/* Low Confluence Example */}
      <div style={{ background: V.bg2, border: `1px solid rgba(196,122,122,0.25)`, borderRadius: '10px', padding: '20px', marginBottom: '28px' }}>
        <div style={{ display: 'inline-block', background: 'rgba(196,122,122,0.1)', border: `0.5px solid rgba(196,122,122,0.4)`, borderRadius: '4px', padding: '2px 8px', fontSize: '10px', color: V.rose, fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>LOW CONFLUENCE ✗</div>
        <div style={{ fontSize: '13px', color: V.goldL, fontFamily: "'Jost',sans-serif", fontWeight: 600, marginBottom: '12px' }}>Example — Only One Signal</div>
        <div style={{ ...b, fontSize: '13px', lineHeight: 1.9 }}>
          <div style={{ marginBottom: '4px' }}>Stock approaching resistance at $180.</div>
          <div style={{ color: V.sage, marginBottom: '3px' }}>✓ Resistance visible at $180</div>
          <div style={{ color: V.rose, marginBottom: '3px' }}>✗ RSI at 52 — neutral, no exhaustion</div>
          <div style={{ color: V.rose, marginBottom: '3px' }}>✗ 20 day MA pointing up strongly</div>
          <div style={{ color: V.rose, marginBottom: '12px' }}>✗ Call OI at $180: only 45</div>
          <div style={{ color: V.muted, marginBottom: '4px' }}>Only ONE signal suggests resistance. Three signals suggest the opposite.</div>
          <div style={{ color: V.rose, fontWeight: 500 }}>This is NOT the confluence. Wait for better alignment.</div>
        </div>
      </div>

      {/* Practice button */}
      {onNavigate && (
        <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 8px 8px 0', padding: '16px 20px', marginBottom: '4px' }}>
          <div style={{ ...ss, fontSize: '18px', color: V.gold, marginBottom: '6px' }}>Ready to practice?</div>
          <p style={{ ...b, fontSize: '13px', marginBottom: '14px' }}>Seven scenarios. Multiple signals. One question: where is the confluence?</p>
          <button style={{ ...btnGold, width: '100%', height: '48px', fontSize: '14px' }} onClick={() => onNavigate('confluence_practice')}>
            Practice The Confluence →
          </button>
        </div>
      )}
    </div>
  );
}

function DeltaRangeCard({ title }) {
  const rows = [
    { delta: '0.15 delta', prob: '~85% probability of expiring worthless', note: title.includes('COVERED CALL') ? 'Shares stay. Premium kept.' : 'Maximum cushion. Lower assignment risk.', badge: 'RECOMMENDED FOR BEGINNERS', color: V.sage, badgeBg: 'rgba(122,171,138,0.15)', badgeBorder: 'rgba(122,171,138,0.4)' },
    { delta: '0.20–0.25 delta', prob: '75-80% probability', note: 'More premium. Moderate assignment risk.', badge: 'INTERMEDIATE', color: V.gold, badgeBg: 'rgba(201,168,76,0.12)', badgeBorder: 'rgba(201,168,76,0.4)' },
    { delta: '0.30 delta', prob: '~70% probability', note: title.includes('COVERED CALL') ? 'Higher premium. Real assignment risk. Be willing to sell at this price.' : 'Higher premium. Real assignment risk. Be willing to own at this price.', badge: 'EXPERIENCED', color: V.rose, badgeBg: 'rgba(196,122,122,0.12)', badgeBorder: 'rgba(196,122,122,0.4)' },
  ];
  return (
    <div style={{ background: V.bg3, border: `1px solid ${V.gold}`, borderRadius: '8px', padding: '16px', margin: '20px 0' }}>
      <div style={{ ...eyebrow, color: V.gold, marginBottom: '14px', fontSize: '10px' }}>{title}</div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 0', borderTop: i > 0 ? `0.5px solid ${V.border}` : 'none' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '14px', fontWeight: 600, color: r.color, marginBottom: '2px' }}>{r.delta}</div>
            <div style={{ fontSize: '12px', color: V.muted, lineHeight: 1.6 }}>{r.prob}<br />{r.note}</div>
          </div>
          <div style={{ fontSize: '9px', fontFamily: "'Jost',sans-serif", fontWeight: 700, letterSpacing: '0.07em', padding: '3px 8px', borderRadius: '4px', background: r.badgeBg, border: `0.5px solid ${r.badgeBorder}`, color: r.color, whiteSpace: 'nowrap' }}>{r.badge}</div>
        </div>
      ))}
    </div>
  );
}

function Ch05DeltaRangeCard({ onNavigate, onPractice, chapterId, section }) {
  const checks = [
    { n: '①', q: 'Is the strike AT or BELOW a support level?', tag: 'Structure', col: V.sage },
    { n: '②', q: 'Is RSI neutral to oversold at entry?', tag: 'Momentum', col: '#60a5fa' },
    { n: '③', q: 'Is the stock above its 200-day moving average?', tag: 'Trend — non-negotiable', col: V.gold },
    { n: '④', q: 'Is IV Rank above 30?', tag: 'Premium', col: '#a78bfa' },
    { n: '⑤', q: 'Would I be comfortable owning 100 shares at this price?', tag: 'Ownership — non-negotiable', col: V.rose },
  ];
  return (
    <>
      <DeltaRangeCard title="BEGINNER CSP DELTA TARGET" />
      <div style={{ background: V.bg2, borderRadius: '12px', padding: '20px', marginTop: '20px', marginBottom: '4px' }}>
        <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: V.muted, marginBottom: '16px' }}>
          Strike Selection Checklist
        </div>
        {checks.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '12px 0', borderBottom: i < 4 ? '0.5px solid rgba(255,255,255,0.06)' : 'none' }}>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '22px', color: item.col, lineHeight: 1, flexShrink: 0, minWidth: '28px' }}>{item.n}</div>
            <div>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.text, marginBottom: '4px', lineHeight: 1.4 }}>{item.q}</div>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', color: item.col }}>{item.tag}</div>
            </div>
          </div>
        ))}
        <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, fontStyle: 'italic', marginTop: '14px', paddingTop: '10px', borderTop: '0.5px solid rgba(201,168,76,0.2)' }}>
          All five green — proceed. The more boxes checked the higher-probability your entry.
        </div>
      </div>
      {onNavigate && (
        <div style={{ marginTop: '16px' }}>
          <button
            style={{ ...btnGold, padding: '10px 20px', fontSize: '13px' }}
            onClick={() => onNavigate('full_trade_simulator')}
          >
            Practice in The Full Trade Simulator →
          </button>
        </div>
      )}
    </>
  );
}

function Ch06DeltaRangeCard() {
  return <DeltaRangeCard title="BEGINNER COVERED CALL DELTA TARGET" />;
}

function WheelDiagram() {
  const W = 560
  const H = 500
  const cx = 280
  const cy = 250
  const r = 160

  const points = [
    { angle: -90,  label: 'SELL A CSP',         sub: 'Collect premium',              color: '#c9a84c', icon: '↓',  labelX: 280, labelY: 60,  textAnchor: 'middle' },
    { angle: -18,  label: 'EXPIRES WORTHLESS',   sub: 'Keep the premium',             color: '#7aab8a', icon: '✓',  labelX: 490, labelY: 160, textAnchor: 'end' },
    { angle: 54,   label: 'GET ASSIGNED',         sub: 'Own shares at\nlower cost basis', color: '#c9a84c', icon: '⬇', labelX: 490, labelY: 380, textAnchor: 'end' },
    { angle: 126,  label: 'SELL COVERED CALL',   sub: 'Collect more premium',         color: '#c9a84c', icon: '↑',  labelX: 70,  labelY: 380, textAnchor: 'start' },
    { angle: 198,  label: 'CALLED AWAY',          sub: 'Keep premium + gain',          color: '#7aab8a', icon: '✓',  labelX: 70,  labelY: 160, textAnchor: 'start' },
  ]

  const toXY = (angle, radius) => {
    const rad = (angle * Math.PI) / 180
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) }
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px', marginTop: '8px' }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '480px', display: 'block' }}>
        <rect width={W} height={H} fill="#0d1117" rx="12" />

        <circle cx={cx} cy={cy} r={55} fill="rgba(201,168,76,0.08)" stroke="#c9a84c" strokeWidth="1.5" strokeDasharray="4,3" />
        <text x={cx} y={cy - 8}  textAnchor="middle" fill="#c9a84c" fontSize="13" fontWeight="700" fontFamily="'Cormorant Garamond'">THE</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fill="#c9a84c" fontSize="13" fontWeight="700" fontFamily="'Cormorant Garamond'">WHEEL</text>
        <text x={cx} y={cy + 26} textAnchor="middle" fill="rgba(201,168,76,0.5)" fontSize="9" fontFamily="monospace">REPEATING CYCLE</text>

        {points.map((p, i) => {
          const start = toXY(p.angle, r)
          const next = points[(i + 1) % points.length]
          const end = toXY(next.angle, r)
          const midAngle = (p.angle + next.angle) / 2
          const arrowPos = toXY(midAngle, r)
          return (
            <g key={i}>
              <path d={`M ${start.x} ${start.y} A ${r} ${r} 0 0 1 ${end.x} ${end.y}`} fill="none" stroke={p.color} strokeWidth="2" opacity="0.4" />
              <circle cx={arrowPos.x} cy={arrowPos.y} r="6" fill={p.color} opacity="0.3" />
            </g>
          )
        })}

        {points.map((p, i) => {
          const pos = toXY(p.angle, r)
          return (
            <g key={i}>
              <circle cx={pos.x} cy={pos.y} r="22" fill="#111827" stroke={p.color} strokeWidth="2" />
              <text x={pos.x} y={pos.y + 5} textAnchor="middle" fill={p.color} fontSize="14" fontWeight="700">{p.icon}</text>
              <text x={p.labelX} y={p.labelY} textAnchor={p.textAnchor} fill="rgba(240,237,230,0.9)" fontSize="10" fontWeight="700" fontFamily="'Jost',sans-serif">{p.label}</text>
              {p.sub.split('\n').map((line, li) => (
                <text key={li} x={p.labelX} y={p.labelY + 14 + li * 13} textAnchor={p.textAnchor} fill="rgba(240,237,230,0.45)" fontSize="9" fontFamily="'Jost',sans-serif">{line}</text>
              ))}
            </g>
          )
        })}

        <text x={cx + r + 20} y={cy + 20} textAnchor="start" fill="rgba(240,237,230,0.3)" fontSize="8" fontFamily="monospace">TWO EXITS</text>
        <text x={cx + r + 20} y={cy + 32} textAnchor="start" fill="rgba(240,237,230,0.3)" fontSize="8" fontFamily="monospace">BOTH PROFITABLE</text>
      </svg>
    </div>
  )
}

function Ch07WheelDeltaCard() {
  return (
    <div>
      <WheelDiagram />
      <div style={{ borderLeft: `3px solid ${V.gold}`, background: V.bg2, borderRadius: '0 8px 8px 0', padding: '16px 20px', margin: '20px 0' }}>
        <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', color: V.muted, lineHeight: 1.75, margin: 0 }}>
          The Wheel at 0.15 delta is designed for maximum consistency — high win rate, steady premium income, lower assignment risk on both legs. This is the foundation of the strategy. Master it here before adjusting delta. There is no rush. Consistent small wins compound into significant wealth.
        </p>
      </div>
    </div>
  );
}

function Ch05BullPutSpreadVisuals({ onNavigate, onUpdate, onPractice, chapterId, section }) {
  const payoffRef = useRef(null);

  useEffect(() => {
    const canvas = payoffRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = 580, H = 260;
    ctx.clearRect(0, 0, W, H); ctx.fillStyle = '#0a0a0c'; ctx.fillRect(0, 0, W, H);
    const L=65, R=20, T=24, B=44, pW=W-L-R, pH=H-T-B;
    const prMin=125, prMax=185, vMin=-460, vMax=115;
    const px = p => L + (p-prMin)/(prMax-prMin)*pW;
    const py = v => T + (vMax-v)/(vMax-vMin)*pH;
    ctx.fillStyle='rgba(122,171,138,0.1)'; ctx.fillRect(px(164.25),T, W-R-px(164.25), py(0)-T);
    ctx.fillStyle='rgba(196,122,122,0.1)'; ctx.fillRect(L, py(0), pW, H-B-py(0));
    ctx.strokeStyle='rgba(240,237,230,0.22)'; ctx.lineWidth=1; ctx.setLineDash([6,4]);
    ctx.beginPath(); ctx.moveTo(L, py(0)); ctx.lineTo(W-R, py(0)); ctx.stroke(); ctx.setLineDash([]);
    ctx.strokeStyle='rgba(240,237,230,0.05)'; ctx.lineWidth=0.5;
    [140,150,160,165,170].forEach(p=>{ctx.beginPath();ctx.moveTo(px(p),T);ctx.lineTo(px(p),H-B);ctx.stroke();});
    ctx.lineWidth=3; ctx.lineJoin='round';
    ctx.strokeStyle='#c47a7a'; ctx.beginPath(); ctx.moveTo(px(125),py(-425)); ctx.lineTo(px(140),py(-425)); ctx.stroke();
    const g=ctx.createLinearGradient(px(140),0,px(165),0);
    g.addColorStop(0,'#c47a7a'); g.addColorStop(1,'#7aab8a');
    ctx.strokeStyle=g; ctx.beginPath(); ctx.moveTo(px(140),py(-425)); ctx.lineTo(px(164.25),py(0)); ctx.lineTo(px(165),py(75)); ctx.stroke();
    ctx.strokeStyle='#7aab8a'; ctx.beginPath(); ctx.moveTo(px(165),py(75)); ctx.lineTo(px(185),py(75)); ctx.stroke();
    ctx.strokeStyle='rgba(240,237,230,0.12)'; ctx.lineWidth=1;
    ctx.beginPath(); ctx.moveTo(L,T); ctx.lineTo(L,H-B); ctx.lineTo(W-R,H-B); ctx.stroke();
    ctx.fillStyle='rgba(240,237,230,0.35)'; ctx.font="9px 'Jost',sans-serif"; ctx.textAlign='center';
    [130,140,150,160,165,170,178].forEach(p=>{ ctx.fillText('$'+p, px(p), H-B+14); });
    ctx.textAlign='right';
    [{v:75,c:'rgba(122,171,138,0.7)'},{v:0,c:'rgba(240,237,230,0.35)'},{v:-100,c:'rgba(196,122,122,0.6)'},{v:-200,c:'rgba(196,122,122,0.6)'},{v:-300,c:'rgba(196,122,122,0.6)'},{v:-425,c:'rgba(196,122,122,0.8)'}].forEach(({v,c})=>{
      ctx.fillStyle=c; ctx.fillText((v>=0?'+$':'-$')+Math.abs(v), L-5, py(v)+3);
    });
    [{p:140,v:-425,c:'#c47a7a',lbl:'Max Loss −$425',side:-1},{p:164.25,v:0,c:'rgba(240,237,230,0.9)',lbl:'Breakeven',side:-1},{p:165,v:75,c:'#7aab8a',lbl:'Max Profit +$75',side:1},{p:178,v:75,c:'#c9a84c',lbl:'Current $178',side:1}].forEach(d=>{
      ctx.fillStyle=d.c; ctx.beginPath(); ctx.arc(px(d.p),py(d.v),5,0,Math.PI*2); ctx.fill();
      ctx.font="10px 'Jost',sans-serif"; ctx.textAlign=d.side<0?'right':'left';
      const lx=d.side<0?px(d.p)-10:px(d.p)+10;
      const ly=d.lbl==='Current $178'?py(d.v)+16:(d.v<0?py(d.v)+14:py(d.v)-9);
      ctx.fillStyle=d.c; ctx.fillText(d.lbl, lx, ly);
    });
  }, []);

  return (
    <div style={{ margin:'20px 0' }}>

      {/* Lynn's Note */}
      <div style={{ background:V.bg2, border:'1px solid rgba(201,168,76,0.3)', borderRadius:'12px', padding:'24px', marginBottom:'28px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px' }}>
          <span style={{ fontSize:'16px', color:V.gold }}>♀</span>
          <span style={{ fontFamily:"'Jost',sans-serif", fontSize:'10px', color:V.muted, textTransform:'uppercase', letterSpacing:'2px' }}>A Note from Lynn</span>
        </div>
        <p style={{ ...serif, fontSize:'20px', color:'#f0ede6', fontStyle:'italic', lineHeight:1.8, margin:'0 0 16px' }}>
          "This is the strategy I use every single day. This is what generates consistent income for me regardless of what the market is doing.
          <br /><br />
          When you hear me talk about my trading day — this is usually what I am doing. Not because it is the only strategy. Because it works. Consistently. Quietly. Over and over again.
          <br /><br />
          Learn this one well before anything else. Everything else builds on it."
        </p>
        <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.gold }}>— Lynn</div>
      </div>

      {/* Three concept cards */}
      {[
        { border:V.gold, bg:'rgba(201,168,76,0.06)', title:'Why use a spread instead of a naked put?', body:'When the stock has elevated IV but you want to cap your downside. When you have a smaller account. When the rubber band has stretched far and you want defined risk on the recovery trade.' },
        { border:V.rose, bg:'rgba(196,122,122,0.06)', title:'The trade-off', body:'You collect less premium than a naked put — but your maximum loss is capped. You can never lose more than the spread width minus the net credit received. No surprise losses.' },
        { border:V.sage, bg:'rgba(122,171,138,0.06)', title:'Setting it up', body:'Sell the put at 0.15 delta. Buy a put $25 below for protection. The difference between what you collect and what you pay is your net credit.' },
      ].map((c,i) => (
        <div key={i} style={{ borderLeft:`3px solid ${c.border}`, background:c.bg, borderRadius:'0 8px 8px 0', padding:'16px 18px', marginBottom:'12px' }}>
          <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.gold, fontWeight:700, marginBottom:'6px' }}>{c.title}</div>
          <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.75 }}>{c.body}</div>
        </div>
      ))}
      <div style={{ borderLeft:'2px solid rgba(201,168,76,0.3)', background:'rgba(201,168,76,0.04)', borderRadius:'0 8px 8px 0', padding:'12px 16px', marginTop:'8px', marginBottom:'4px' }}>
        <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.75, margin:0 }}>
          The spread is not a different strategy. It is a Cash Secured Put with a built-in safety net. Learn the CSP first. Then add the spread when you want defined risk.
        </p>
      </div>

      {/* Simple Math */}
      <div style={{ background:V.bg3, border:`1px solid rgba(201,168,76,0.25)`, borderRadius:'10px', padding:'20px', marginTop:'16px' }}>
        <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'11px', color:V.gold, textTransform:'uppercase', letterSpacing:'2px', marginBottom:'14px' }}>The Bull Put Spread — Simple Math</div>
        <div style={{ borderLeft:`3px solid ${V.rose}`, background:'rgba(196,122,122,0.06)', borderRadius:'0 6px 6px 0', padding:'10px 14px', marginBottom:'8px' }}>
          <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'11px', color:V.rose, fontWeight:600, letterSpacing:'0.05em', marginBottom:'4px' }}>SHORT PUT — SOLD</div>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'13px', color:V.text }}>$165 strike</div>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'13px', color:V.goldL }}>$0.78 per share premium</div>
        </div>
        <div style={{ borderLeft:`3px solid ${V.sage}`, background:'rgba(122,171,138,0.06)', borderRadius:'0 6px 6px 0', padding:'10px 14px', marginBottom:'16px' }}>
          <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'11px', color:V.sage, fontWeight:600, letterSpacing:'0.05em', marginBottom:'4px' }}>LONG PUT — BOUGHT</div>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'13px', color:V.text }}>$140 strike</div>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'13px', color:V.muted }}>$0.18 per share premium</div>
        </div>
        {[
          { label:'Spread Width',  calc:'$165 − $140',         result:'$25' },
          { label:'Net Credit',    calc:'$0.78 − $0.18',       result:'$0.60 / share' },
          { label:'Per Contract',  calc:'$0.60 × 100',         result:'$60' },
          { label:'Max Loss',      calc:'($25 − $0.60) × 100', result:'$2,440' },
          { label:'Breakeven',     calc:'$165 − $0.60',        result:'$164.40' },
          { label:'Probability',   calc:'~85%',                result:'(0.15 delta)' },
        ].map((r,i) => (
          <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'7px 0', borderBottom:i<5?'0.5px solid rgba(240,237,230,0.06)':'none' }}>
            <span style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, minWidth:'110px' }}>{r.label}</span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'12px', color:V.muted }}>{r.calc}</span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'13px', color:V.gold, fontWeight:600 }}>{r.result}</span>
          </div>
        ))}
      </div>

      {/* Three Non-Negotiable Rules */}
      <div style={{ background:V.bg3, border:`1px solid ${V.gold}`, borderRadius:'10px', padding:'24px', marginTop:'24px' }}>
        <div style={{ ...serif, fontSize:'22px', color:V.gold, marginBottom:'6px' }}>Three Rules. Every Time.</div>
        <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.6, marginBottom:'20px' }}>
          Before you place any Bull Put Spread these three rules are non-negotiable.
        </p>
        {[
          { n:'1', title:'Sell at 0.15 delta', body:'Your short put should have a delta of approximately −0.15. This gives you an 85% probability of the spread expiring worthless. More cushion. Higher win rate. Start here.' },
          { n:'2', title:'Buy protection $25 below', body:'Always buy the long put $25 below your short put. This caps your maximum loss and makes the trade defined risk. You always know your worst case before you enter.' },
          { n:'3', title:'Never risk more than 1–2% per spread', body:'Calculate your maximum loss before entering. Make sure it is 1–2% of your account or less. No spread is worth more than that. Position sizing protects everything.' },
        ].map(r => (
          <div key={r.n} style={{ borderLeft:'3px solid #7aab8a', background:'rgba(122,171,138,0.08)', borderRadius:'8px', padding:'16px', marginBottom:'12px' }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:'12px' }}>
              <div style={{ ...serif, fontSize:'28px', color:'#7aab8a', lineHeight:1, flexShrink:0, marginTop:'2px' }}>{r.n}</div>
              <div>
                <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'14px', color:V.gold, fontWeight:700, marginBottom:'6px' }}>{r.title}</div>
                <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.7 }}>{r.body}</div>
              </div>
            </div>
          </div>
        ))}
        <div style={{ borderLeft:`3px solid ${V.gold}`, background:'rgba(201,168,76,0.05)', borderRadius:'0 6px 6px 0', padding:'12px 16px', marginTop:'16px' }}>
          <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, fontStyle:'italic', lineHeight:1.75, margin:'0 0 8px' }}>
            You will learn more rules as you gain experience — when to close early, how to manage a losing spread, how VIX affects your premium. Those come with time and practice. Master these three first.
          </p>
          {onNavigate && (
            <button onClick={() => onNavigate('tradingrules')} style={{ background:'transparent', border:'none', padding:0, fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.gold, cursor:'pointer', textDecoration:'underline', textDecorationColor:'rgba(201,168,76,0.4)' }}>
              See all trading rules →
            </button>
          )}
        </div>
      </div>

      {/* Paper Trade Prompt */}
      <div style={{ background:V.bg2, border:'1px solid rgba(201,168,76,0.2)', borderRadius:'10px', padding:'20px', marginTop:'20px' }}>
        <div style={{ ...serif, fontSize:'20px', color:V.gold, marginBottom:'12px' }}>Your First Paper Trade</div>
        <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, marginBottom:'16px' }}>
          Before you move to Chapter 7 — open ThinkorSwim and paper trade one Bull Put Spread.
          <br /><br />
          Use what you just learned: Find a stock in an uptrend above its 20, 50, and 200 day MAs. Sell the put at 0.15 delta. Buy the put $25 below. Calculate your net credit, max loss, and breakeven before submitting.
          <br /><br />
          Log it in your Trading Journal. Note how it feels to place the trade. That emotional data matters as much as the mechanics.
        </p>
      </div>

      {onNavigate && (
        <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginTop:'20px' }}>
          <button style={{ ...btnGold, padding:'11px 20px', fontSize:'13px', width:'100%' }} onClick={() => onNavigate('full_trade_simulator')}>
            Practice in The Full Trade Simulator →
          </button>
          <button style={{ ...btnGhost, padding:'10px 18px', fontSize:'13px' }} onClick={() => onNavigate('chapter_ch07')}>
            Continue to Chapter 7 →
          </button>
        </div>
      )}

    </div>
  );
}

function Ch07RollVisual({ onNavigate }) {
  const choices = [
    {
      label: 'Roll Down & Out',
      color: V.gold,
      icon: '⟳',
      when: 'Stock dropped but thesis is intact',
      how: 'Close current put → sell new put at lower strike, further expiration. Collect net credit.',
      why: 'Buys time, reduces cost basis, keeps you in the trade.'
    },
    {
      label: 'Close the Position',
      color: V.rose,
      icon: '✕',
      when: 'Fundamental breakdown — bad earnings, sector collapse, broken thesis',
      how: 'Buy back the short put and take the defined loss.',
      why: 'A controlled loss today beats rolling a doomed trade into a larger one.'
    },
    {
      label: 'Take Assignment',
      color: V.sage,
      icon: '◎',
      when: 'You still want to own the stock at this price',
      how: 'Let shares be put to you. Begin selling covered calls immediately.',
      why: 'Assignment is not failure — it\'s the Wheel doing exactly what it was designed to do.'
    },
  ];
  return (
    <div style={{ margin: '20px 0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        {choices.map(c => (
          <div key={c.label} style={{ background: V.bg2, border: `0.5px solid ${c.color}33`, borderTop: `3px solid ${c.color}`, borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span style={{ fontSize: '18px', color: c.color }}>{c.icon}</span>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 600, color: c.color }}>{c.label}</span>
            </div>
            <div style={{ marginBottom: '6px' }}>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: V.dim }}>When</span>
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, margin: '2px 0 8px', lineHeight: 1.6 }}>{c.when}</p>
            </div>
            <div style={{ marginBottom: '6px' }}>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: V.dim }}>How</span>
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.text, margin: '2px 0 8px', lineHeight: 1.6 }}>{c.how}</p>
            </div>
            <div>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: V.dim }}>Why it works</span>
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, margin: '2px 0 0', lineHeight: 1.6, fontStyle: 'italic' }}>{c.why}</p>
            </div>
          </div>
        ))}
      </div>
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '8px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, fontStyle: 'italic', margin: 0, lineHeight: 1.65 }}>
          Run the numbers on all three scenarios before you decide.
        </p>
        <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', whiteSpace: 'nowrap' }}
          onClick={() => onNavigate && onNavigate('roll_calculator')}>
          Open Roll Calculator →
        </button>
      </div>
    </div>
  );
}

function Ch06PracticeBtn({ onNavigate }) {
  return (
    <div style={{ marginTop:'16px' }}>
      <button
        style={{ ...btnGold, padding:'10px 20px', fontSize:'13px' }}
        onClick={() => onNavigate && onNavigate('full_trade_simulator')}
      >
        Practice in The Full Trade Simulator →
      </button>
    </div>
  );
}

function Ch03PuttingItTogetherVisual({ onNavigate }) {
  const ss = { fontFamily:"'Cormorant Garamond',Georgia,serif" };
  const jost = { fontFamily:"'Jost',sans-serif" };
  const sections = [
    {
      label: 'CANDLESTICKS',
      body: `NFLX was in a bull trend — higher highs and higher lows on the daily chart.\n\nThe candlesticks showed a quiet orderly pullback. No large red candles. No heavy selling volume. Buyers were still in control.`,
    },
    {
      label: 'MOVING AVERAGES',
      body: `The MA lines were ordered correctly. Price was above the key MAs.\n\nThe pullback was bringing price back toward the MAs — not breaking through them.\n\nThis is a healthy pullback in an uptrend — not a reversal.`,
    },
    {
      label: 'RSI',
      body: `RSI was approaching 30 — the oversold zone. The rubber band was stretched near its limit to the downside.\n\nNot broken. Not in freefall. Stretched — and approaching the point where it historically snaps back.`,
    },
    {
      label: 'VOLUME',
      body: `Volume was confirming the move. No panic selling. Just a normal pullback with decreasing volume — healthy.`,
    },
    {
      label: 'THE PRICE',
      body: `$81.42 — near the 52-week low. A significant support level watched by institutional traders.`,
    },
  ];

  return (
    <div style={{ marginTop: '20px' }}>
      <p style={{ ...jost, fontSize: '14px', color: 'rgba(240,237,230,0.85)', lineHeight: 1.85, margin: '0 0 8px' }}>
        You have now learned three ways to read the market:
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', margin: '0 0 20px' }}>
        {[
          { label: 'Candlesticks', note: 'what price is doing' },
          { label: 'Moving Averages', note: 'where the trend is' },
          { label: 'RSI', note: 'how stretched the rubber band is' },
        ].map(({ label, note }) => (
          <div key={label} style={{ display: 'flex', gap: '10px', alignItems: 'baseline' }}>
            <span style={{ ...jost, fontSize: '13px', color: V.gold, fontWeight: 600, minWidth: '130px' }}>{label}</span>
            <span style={{ ...jost, fontSize: '13px', color: V.muted }}>— {note}</span>
          </div>
        ))}
      </div>
      <p style={{ ...jost, fontSize: '14px', color: 'rgba(240,237,230,0.85)', lineHeight: 1.85, margin: '0 0 24px' }}>
        Here is how they work together on a real trade.
      </p>

      <div style={{ ...jost, fontSize: '10px', color: V.muted, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px' }}>REAL EXAMPLE — JUNE 15, 2026</div>
      <h3 style={{ ...ss, fontSize: '22px', color: V.gold, fontWeight: 400, margin: '0 0 16px' }}>NFLX at $81.42</h3>

      <div style={{ background: V.bg2, border: `1px solid ${V.gold}40`, borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
        <div style={{ ...jost, fontSize: '10px', color: V.muted, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '16px' }}>WHAT THE CHART WAS SHOWING</div>
        {sections.map(({ label, body }) => (
          <div key={label} style={{ marginBottom: '16px' }}>
            <div style={{ ...jost, fontSize: '11px', color: V.gold, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px' }}>{label}</div>
            <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.85, margin: 0, whiteSpace: 'pre-line' }}>{body}</p>
          </div>
        ))}
        <div style={{ height: '0.5px', background: 'rgba(201,168,76,0.15)', margin: '20px 0' }} />
        <div style={{ ...jost, fontSize: '11px', color: V.gold, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px' }}>WHAT THIS TOLD ME</div>
        <p style={{ ...jost, fontSize: '13px', color: 'rgba(240,237,230,0.85)', lineHeight: 1.85, margin: '0 0 12px' }}>
          All three indicators were saying the same thing:
        </p>
        <div style={{ background: 'rgba(201,168,76,0.06)', border: '0.5px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '14px 16px', marginBottom: '16px' }}>
          <p style={{ ...jost, fontSize: '13px', color: V.goldL, lineHeight: 1.8, margin: 0, fontStyle: 'italic' }}>
            "The rubber band is stretched.<br />
            The trend is intact.<br />
            Buyers have shown up here before.<br />
            The conditions favor a bounce."
          </p>
        </div>
        <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.85, margin: '0 0 12px' }}>
          This is confluence. Not one signal. Not two. All of them pointing in the same direction.
        </p>
        <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.85, margin: '0 0 12px' }}>
          When I opened the options chain:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
          {[
            'IV showed expected move of only +/- $2.52 (market not pricing in a large move)',
            'Open Interest above 500 at every strike (plenty of liquidity)',
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <span style={{ color: V.sage, flexShrink: 0 }}>—</span>
              <span style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.7 }}>{item}</span>
            </div>
          ))}
        </div>
        <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.85, margin: '0 0 16px' }}>
          Everything confirmed the setup.
        </p>
        <div style={{ height: '0.5px', background: 'rgba(201,168,76,0.15)', margin: '0 0 16px' }} />
        <p style={{ ...jost, fontSize: '13px', color: 'rgba(240,237,230,0.85)', lineHeight: 1.85, margin: '0 0 8px', fontWeight: 600 }}>
          THIS is what you are learning in these first chapters.
        </p>
        <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.85, margin: 0 }}>
          Not one indicator in isolation. All of them together — telling the same story.
        </p>
        <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.85, margin: '8px 0 0' }}>
          Chapter 4 teaches you exactly how to find this confluence for yourself.
        </p>
      </div>

      <div style={{ background: 'rgba(201,168,76,0.07)', border: `1px solid ${V.gold}50`, borderRadius: '8px', marginTop: '16px', padding: '14px 18px', marginBottom: '24px' }}>
        <p style={{ ...jost, fontSize: '13px', color: V.goldL, lineHeight: 1.85, margin: '0 0 12px', fontStyle: 'italic' }}>
          "You are not lost. You are learning the language.
        </p>
        <p style={{ ...jost, fontSize: '13px', color: V.goldL, lineHeight: 1.85, margin: '0 0 12px', fontStyle: 'italic' }}>
          By Chapter 4 you will look at a chart like this NFLX setup and immediately recognize what it is saying.
        </p>
        <p style={{ ...jost, fontSize: '13px', color: V.goldL, lineHeight: 1.85, margin: 0, fontStyle: 'italic' }}>
          Keep going."
        </p>
      </div>

      <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px' }}
        onClick={() => onNavigate && onNavigate('chapter_ch04')}>
        Continue to Chapter 4 →
      </button>
    </div>
  );
}

function Ch08WheelInActionVisual() {
  const jost = { fontFamily: "'Jost',sans-serif" };
  const eyebrowStyle = (color) => ({ ...jost, fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color, marginBottom: '12px' });
  const cardStyle = (borderColor) => ({ background: V.bg2, border: `0.5px solid ${borderColor}33`, borderLeft: `3px solid ${borderColor}`, borderRadius: '10px', padding: '20px', marginBottom: '16px' });
  const bodyText = { ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.9, margin: 0 };

  const tradeRows = [
    { label: 'Stock', value: 'BE (Bloom Energy)' },
    { label: 'Strategy', value: 'Cash Secured Put' },
    { label: 'Strike', value: '$275' },
    { label: 'Premium Collected', value: '$5.00/share = $500 per contract' },
    { label: 'Stock Price at Entry', value: 'Above $300' },
    { label: 'Thesis', value: 'BE had just entered a partnership with Oracle. Strong AI energy demand. Convinced of long term growth.' },
  ];

  const costRows = [
    { label: 'Purchase price', value: '$275/share' },
    { label: 'Minus CSP premium', value: '−$5.00' },
    { label: 'Minus covered call premium', value: '−$7.20' },
    { label: 'New cost basis', value: '$262.80/share', highlight: true },
  ];

  return (
    <div style={{ marginTop: '4px' }}>

      {/* Intro card */}
      <div style={{ padding: '16px', background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '8px', marginBottom: '20px' }}>
        <div style={{ ...jost, fontSize: '9px', color: V.muted, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px' }}>Real Trade — June 2026</div>
        <div style={{ ...jost, fontSize: '13px', color: V.muted, fontStyle: 'italic', lineHeight: 1.8 }}>
          This is what The Wheel looks like when the market moves against you.{'\n\n'}
          Not a hypothetical.{'\n'}Not a textbook example.{'\n'}A real trade — with real money — and real decisions made in real time.{'\n\n'}
          Originally shared in the{'\n'}Trade Like a Woman community{'\n'}group as it was happening —{'\n'}June 2026.
        </div>
      </div>

      {/* Section 1 — The Entry */}
      <div style={eyebrowStyle(V.gold)}>The Entry</div>
      <div style={cardStyle(V.gold)}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {tradeRows.map(r => (
              <tr key={r.label} style={{ borderBottom: `0.5px solid ${V.dim}` }}>
                <td style={{ ...jost, fontSize: '12px', color: V.muted, padding: '8px 12px 8px 0', width: '40%', verticalAlign: 'top' }}>{r.label}</td>
                <td style={{ ...jost, fontSize: '13px', color: V.text, padding: '8px 0', lineHeight: 1.6 }}>{r.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Section 2 — When It Went Wrong */}
      <div style={eyebrowStyle(V.rose)}>When It Went Wrong</div>
      <div style={cardStyle(V.rose)}>
        <p style={bodyText}>
          In the last hour of trading{'\n'}BE dropped 19% in a single day —{'\n'}well below my $275 strike.
        </p>
        <p style={{ ...bodyText, marginTop: '12px' }}>
          A competitor announcement and{'\n'}sector-wide tech selloff hit{'\n'}at the same time.
        </p>
        <p style={{ ...bodyText, marginTop: '12px' }}>
          I had less than 2 hours{'\n'}to make a decision.
        </p>
      </div>

      {/* Section 3 — The Decision Process */}
      <div style={eyebrowStyle(V.gold)}>The Decision Process</div>
      <div style={cardStyle(V.gold)}>
        <p style={{ ...bodyText, marginBottom: '14px' }}>Here is what I asked myself:</p>
        {[
          { num: '①', q: 'Has my conviction on the stock changed?', a: 'BE still has the Oracle partnership. The energy crisis for AI is real. One competitor announcement does not change their existing contracts. This looked like an overreaction.' },
          { num: '②', q: 'Do I want to own these shares?', a: 'At $275 — with a $262 cost basis after the premium I collected — yes. I believe BE will recover.' },
          { num: '③', q: 'What does The Wheel tell me to do?', a: 'Get assigned. Keep the premium. Start selling covered calls. Let the wheel spin.' },
        ].map(item => (
          <div key={item.num} style={{ marginBottom: '14px', paddingLeft: '8px', borderLeft: `2px solid ${V.gold}44` }}>
            <div style={{ ...jost, fontSize: '13px', color: V.goldL, fontWeight: 600, marginBottom: '4px' }}>{item.num} {item.q}</div>
            <div style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.8 }}>{item.a}</div>
          </div>
        ))}
      </div>

      {/* Section 4 — The Wheel Spinning */}
      <div style={eyebrowStyle(V.sage)}>The Wheel Spinning</div>
      <div style={cardStyle(V.sage)}>
        <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '14px' }}>
          <div style={{ ...jost, fontSize: '11px', color: V.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>Cost Basis Calculation</div>
          {costRows.map(r => (
            <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `0.5px solid ${V.dim}` }}>
              <span style={{ ...jost, fontSize: '13px', color: V.muted }}>{r.label}</span>
              <span style={{ ...jost, fontSize: '13px', color: r.highlight ? V.sage : V.text, fontWeight: r.highlight ? 700 : 400 }}>{r.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Section 5 — Beginner vs Experienced */}
      <div style={eyebrowStyle(V.gold)}>What a Beginner Sees vs What an Experienced Trader Sees</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
        <div style={{ background: V.bg2, border: `0.5px solid ${V.rose}55`, borderTop: `3px solid ${V.rose}`, borderRadius: '10px', padding: '18px' }}>
          <div style={{ ...jost, fontSize: '11px', fontWeight: 700, color: V.rose, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>Beginner Sees</div>
          {['Stock dropped 19%.', 'Position is a loss.', 'This is a disaster.', 'I should have sold earlier.', 'I am bad at this.'].map(line => (
            <div key={line} style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.9 }}>{line}</div>
          ))}
        </div>
        <div style={{ background: V.bg2, border: `0.5px solid ${V.sage}55`, borderTop: `3px solid ${V.sage}`, borderRadius: '10px', padding: '18px' }}>
          <div style={{ ...jost, fontSize: '11px', fontWeight: 700, color: V.sage, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>Experienced Trader Sees</div>
          {['Stock dropped on news.', 'Is my thesis still intact?', 'Yes — assignment at $275 is acceptable at this price.', 'The wheel continues.', 'Cost basis is now $262.', 'Next covered call incoming.'].map(line => (
            <div key={line} style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.9 }}>{line}</div>
          ))}
        </div>
      </div>
      <p style={{ ...jost, fontSize: '13px', color: V.muted, fontStyle: 'italic', lineHeight: 1.8, marginBottom: '20px' }}>
        That shift in perspective —{'\n'}from panic to process —{'\n'}is what this program is{'\n'}designed to give you.
      </p>

      {/* Section 6 — Note from Lynn */}
      <div style={{ background: V.bg2, border: `0.5px solid ${V.gold}55`, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px' }}>
        <div style={{ ...jost, fontSize: '13px', fontWeight: 700, color: V.goldL, marginBottom: '14px' }}>♀ A Note from Lynn</div>
        <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.9, margin: 0 }}>
          I know this post is further ahead than most of you are right now.{'\n\n'}
          That is intentional.{'\n\n'}
          I want you to see where this program takes you — not just what it teaches you today.{'\n\n'}
          The student who completes this program will look at a trade like BE and think:{'\n\n'}
          <span style={{ color: V.text, fontStyle: 'italic' }}>"I know exactly what she did. I know exactly why. And I know I could make that same decision."</span>{'\n\n'}
          That is the goal.{'\n'}Not perfection.{'\n'}Process.{'\n\n'}
          Keep going.
        </p>
      </div>

    </div>
  );
}

function CostBasisTable() {
  const rows = [
    { event: 'Assigned 100 shares', premium: '—',      costBasis: '$62.00', note: 'Starting cost basis' },
    { event: 'Covered Call #1',     premium: '−$0.75', costBasis: '$61.25', note: '30 days later' },
    { event: 'Covered Call #2',     premium: '−$0.70', costBasis: '$60.55', note: '30 days later' },
    { event: 'Covered Call #3',     premium: '−$0.65', costBasis: '$59.90', note: '30 days later' },
    { event: 'Covered Call #4',     premium: '−$0.70', costBasis: '$59.20', note: '30 days later' },
    { event: 'Covered Call #5',     premium: '−$0.65', costBasis: '$58.55', note: '30 days later' },
  ]

  return (
    <div style={{ marginTop: '20px', marginBottom: '20px' }}>
      <div style={{ fontSize: '10px', color: '#c9a84c', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px', fontFamily: "'Jost',sans-serif" }}>
        KO — Cost Basis Reduction Over 5 Covered Call Cycles
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr', gap: '8px', padding: '8px 12px', background: 'rgba(201,168,76,0.1)', borderRadius: '6px 6px 0 0', borderBottom: '1px solid rgba(201,168,76,0.3)' }}>
        {['Event', 'Premium', 'Cost Basis', 'Note'].map(h => (
          <div key={h} style={{ fontSize: '9px', color: '#c9a84c', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: "'Jost',sans-serif", fontWeight: '700' }}>{h}</div>
        ))}
      </div>

      {rows.map((row, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr', gap: '8px', padding: '10px 12px', background: i === 0 ? 'rgba(196,122,122,0.06)' : i % 2 === 0 ? 'rgba(240,237,230,0.02)' : 'transparent', borderBottom: '1px solid #1e2535' }}>
          <div style={{ fontSize: '12px', color: i === 0 ? 'rgba(240,237,230,0.7)' : 'rgba(240,237,230,0.85)', fontFamily: "'Jost',sans-serif", fontWeight: i === 0 ? '400' : '500' }}>{row.event}</div>
          <div style={{ fontSize: '12px', color: i === 0 ? 'rgba(240,237,230,0.4)' : '#7aab8a', fontFamily: 'monospace', fontWeight: '600' }}>{row.premium}</div>
          <div style={{ fontSize: '13px', color: i === 0 ? '#c47a7a' : i === rows.length - 1 ? '#7aab8a' : 'rgba(240,237,230,0.85)', fontFamily: 'monospace', fontWeight: '700' }}>{row.costBasis}</div>
          <div style={{ fontSize: '10px', color: 'rgba(240,237,230,0.35)', fontFamily: "'Jost',sans-serif", fontStyle: 'italic' }}>{row.note}</div>
        </div>
      ))}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr', gap: '8px', padding: '12px', background: 'rgba(122,171,138,0.08)', borderRadius: '0 0 6px 6px', border: '1px solid rgba(122,171,138,0.2)', borderTop: 'none' }}>
        <div style={{ fontSize: '11px', color: '#7aab8a', fontFamily: "'Jost',sans-serif", fontWeight: '700' }}>Total Reduction</div>
        <div style={{ fontSize: '12px', color: '#7aab8a', fontFamily: 'monospace', fontWeight: '700' }}>−$3.45</div>
        <div style={{ fontSize: '13px', color: '#7aab8a', fontFamily: 'monospace', fontWeight: '700' }}>$58.55</div>
        <div style={{ fontSize: '10px', color: 'rgba(122,171,138,0.6)', fontFamily: "'Jost',sans-serif", fontStyle: 'italic' }}>from $62.00 assigned</div>
      </div>

      <div style={{ fontSize: '11px', color: 'rgba(240,237,230,0.4)', fontStyle: 'italic', marginTop: '10px', lineHeight: '1.7', fontFamily: "'Jost',sans-serif" }}>
        Each covered call reduces your cost basis by the premium collected. After 5 cycles you own KO at $58.55 — a stock you were assigned at $62.00. The wheel paid you $3.45/share just for holding it.
      </div>
    </div>
  )
}

const CHAPTER_VISUALS = {
  'ch02:0': RubberBandChartVisual,
  'ch02:1': Ch02CandlestickTabVisual,
  'ch02:2': Ch02TrendVisual,
  'ch02:3': Ch02VolumeVisual,
  'ch02:4': Ch02ChartPatternsVisual,
  'ch02:5': Ch02DrawingTOSVisual,
  'ch03:1': Ch03MATabVisual,
  'ch03:2': Ch03RSITabVisual,
  'ch03:3': Ch03PuttingItTogetherVisual,
  'ch04:0': Ch04SupportVisual,
  'ch04:1': Ch04ResistanceVisual,
  'ch04:2': Ch04OIVisual,
  'ch04:3': Ch04ConfluenceVisual,
  'ch05:0': Ch05WhyGreeksVisual,
  'ch05:1': Ch05DeltaTabVisual,
  'ch05:2': Ch05ThetaTabVisual,
  'ch05:3': Ch05VegaTabVisual,
  'ch05:4': Ch05GammaTabVisual,
  'ch05:5': Ch05GreeksVisual,
  'ch06:2': Ch05DeltaRangeCard,
  'ch06:4': Ch05BullPutSpreadVisuals,
  'ch07:3': Ch06DeltaRangeCard,
  'ch08:1': Ch07WheelDeltaCard,
  'ch08:2': CostBasisTable,
  'ch08:4': Ch07RollVisual,
  'ch08:5': Ch08WheelInActionVisual,
  'ch09:1': Ch08PositionSizingVisual,
};

export { CHAPTER_VISUALS }
